import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AnalyzingIndicator from './AnalyzingIndicator';

// status/statusColor are only for flagging something that ISN'T fully live
// yet (e.g. a toggle that's currently off) - a card with nothing to flag
// just omits them, rather than every card carrying a pointless "Built" badge.
// onNavigate (Status tab only) makes the whole header a shortcut to the
// tab where that section actually lives.
function SectionCard({ title, status, statusColor, children, onNavigate, navigateLabel }) {
    return (
        <div className="social-admin-card" style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: 'white' }}>
            <div
                onClick={onNavigate}
                role={onNavigate ? 'button' : undefined}
                tabIndex={onNavigate ? 0 : undefined}
                onKeyDown={onNavigate ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(); } } : undefined}
                style={{
                    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap',
                    cursor: onNavigate ? 'pointer' : 'default',
                }}
            >
                <h4 style={{ margin: 0, color: onNavigate ? '#6f42c1' : 'inherit' }}>{title}</h4>
                {status && (
                    <span style={{
                        padding: '2px 10px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        backgroundColor: statusColor?.bg || '#e2e3e5',
                        color: statusColor?.text || '#41464b'
                    }}>
                        {status}
                    </span>
                )}
                {onNavigate && (
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6f42c1', fontWeight: 'bold' }}>
                        {navigateLabel || 'View'} &rarr;
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}

// One "is this set up" row for the Status tab - ok=true/pending=true/
// neither map to green/yellow/red, matching this file's existing status-
// badge color convention (see STATUS_COLORS below).
function StatusRow({ label, ok, pending, okText, notOkText, neutral }) {
    const bg = pending ? '#fff3cd' : ok ? '#d4edda' : (neutral ? '#e2e3e5' : '#f8d7da');
    const color = pending ? '#856404' : ok ? '#155724' : (neutral ? '#41464b' : '#721c24');
    const text = pending ? 'Checking...' : ok ? okText : notOkText;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderTop: '1px solid #f2f2f2', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 'bold', flex: '1 1 160px' }}>{label}</span>
            <span style={{
                padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold',
                backgroundColor: bg, color: color,
            }}>
                {text}
            </span>
        </div>
    );
}

// Status tab only - tells the admin exactly what to do next to get a
// section live, only rendered when that section actually needs action.
// Optional `action` ({label, onClick}) turns it from a description into a
// one-click jump to the tab that next step actually lives on, so reading
// this doesn't mean manually going and finding that tab yourself.
function NextStepHint({ children, action }) {
    return (
        <p style={{
            fontSize: '12px', color: '#856404', backgroundColor: '#fff3cd',
            border: '1px solid #ffe69c', borderRadius: '4px', padding: '6px 10px',
            margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        }}>
            <span><strong>Next:</strong> {children}</span>
            {action && (
                <button
                    type="button"
                    onClick={action.onClick}
                    style={{
                        padding: '3px 10px', fontSize: '11px', fontWeight: 'bold', border: '1px solid #856404',
                        borderRadius: '4px', backgroundColor: 'white', color: '#856404', cursor: 'pointer', flexShrink: 0,
                    }}
                >
                    {action.label} &rarr;
                </button>
            )}
        </p>
    );
}

// One step in the "how a city goes live" pipeline explainer. Numbered steps
// are things ONLY the admin can do - what already happens on its own as a
// result is folded into that same step as a small "triggers" note instead
// of getting its own numbered row, so the numbering reads as a literal
// to-do list rather than mixing in things there's nothing to do for.
// `done`/`action` (both optional) let a step double as a live per-city
// checklist item, not just static copy: `done` swaps the Manual/Optional
// pill for a green "Done" one, and `action` renders a jump button - either
// "go do this step now" (not done yet) or "on to the next step" (this step
// is done, the next one isn't), computed by the caller from real city data.
function PipelineStep({ n, kind, children, triggers, done, action }) {
    const kindStyle = {
        Manual: { bg: '#e2e3e5', text: '#41464b' },
        Optional: { bg: '#fff3cd', text: '#856404' },
    }[kind];
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0',
            borderTop: n === 1 ? 'none' : '1px solid #f2f2f2', flexWrap: 'wrap',
        }}>
            <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#999', minWidth: '16px' }}>{n}.</span>
            <span style={{
                padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
                backgroundColor: done ? '#d4edda' : kindStyle.bg, color: done ? '#155724' : kindStyle.text, flexShrink: 0,
            }}>
                {done ? '✓ Done' : kind}
            </span>
            <div style={{ flex: '1 1 200px' }}>
                <span style={{ fontSize: '13px', color: '#333' }}>{children}</span>
                {triggers && (
                    <div style={{
                        fontSize: '11px', color: '#155724', backgroundColor: '#d4edda', borderRadius: '4px',
                        padding: '3px 8px', marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                    }}>
                        ⚡ Then automatically: {triggers}
                    </div>
                )}
            </div>
            {action && (
                <button
                    type="button"
                    onClick={action.onClick}
                    style={{
                        padding: '4px 10px', fontSize: '11px', fontWeight: 'bold', border: '1px solid #6f42c1',
                        borderRadius: '4px', backgroundColor: 'white', color: '#6f42c1', cursor: 'pointer', flexShrink: 0,
                    }}
                >
                    {action.label} &rarr;
                </button>
            )}
        </div>
    );
}

// One ✓/○ pill in a per-city checklist row (CityChecklistRow below).
function MiniCheck({ ok, label }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px',
            padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold', whiteSpace: 'nowrap',
            backgroundColor: ok ? '#d4edda' : '#f8f9fa', color: ok ? '#155724' : '#999',
            border: ok ? 'none' : '1px solid #ddd',
        }}>
            {ok ? '✓' : '○'} {label}
        </span>
    );
}

// One city's row in the Status tab's per-city checklist - shows exactly
// which of the (in-order) stages a city has reached, and what to do next
// for whichever stage is first incomplete. Pure summary of state already
// loaded elsewhere in AdminSocials (cities/pages/livePagesByCity/drafts) -
// no extra fetches, so it can never drift from what the other tabs show.
function CityChecklistRow({ city, enabled, hasLivePage, cityPages, draftsCount, postedCount, onGoToCities, onGoToPages, onGoToReview, pendingDiscoveryJob }) {
    const activePages = cityPages.filter((p) => p.is_active);
    const hasPage = activePages.length > 0;
    const hasCadence = activePages.some((p) => p.has_cadence);
    const pendingEvents = pendingDiscoveryJob?.status === 'done' ? (pendingDiscoveryJob.events || []) : [];

    // Paired with an action button below - same case, so the text and the
    // jump-to-tab button can never point at different steps.
    let nextStep = null;
    let action = null;
    if (!enabled) {
        nextStep = 'Enable this city in the Cities tab.';
        action = { label: 'Go to Cities', onClick: onGoToCities };
    } else if (!hasLivePage && pendingEvents.length > 0) {
        nextStep = `${pendingEvents.length} event${pendingEvents.length === 1 ? '' : 's'} were found automatically - review and publish them (nothing publishes unreviewed).`;
        action = { label: 'Go to Review & Post', onClick: () => onGoToReview(city, { expandManualTools: true, pendingEvents }) };
    } else if (!hasLivePage && pendingDiscoveryJob?.status === 'failed') {
        nextStep = 'Automatic event discovery failed - run Find Events in Review & Post\'s manual tools.';
        action = { label: 'Go to Review & Post', onClick: () => onGoToReview(city, { expandManualTools: true }) };
    } else if (!hasLivePage) {
        nextStep = "Events are found automatically within a minute or two of enabling, then wait for review before publishing - if it's been longer, run Find Events in Review & Post's manual tools.";
        action = { label: 'Go to Review & Post', onClick: () => onGoToReview(city, { expandManualTools: true }) };
    } else if (!hasPage) {
        nextStep = 'Add a Facebook or LinkedIn posting page for this city in Pages & Cadence.';
        action = { label: 'Go to Pages & Cadence', onClick: () => onGoToPages(city) };
    } else if (!hasCadence) {
        nextStep = "Set a posting cadence for this city's page in Pages & Cadence.";
        action = { label: 'Go to Pages & Cadence', onClick: () => onGoToPages(city) };
    } else if (draftsCount === 0) {
        nextStep = 'Drafts generate automatically on that cadence, or generate now in Review & Post.';
        action = { label: 'Go to Review & Post', onClick: () => onGoToReview(city, { expandManualTools: true }) };
    }

    return (
        <div style={{ borderTop: '1px solid #f2f2f2', padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <strong style={{ fontSize: '13px' }}>{city}</strong>
                <MiniCheck ok={enabled} label="Enabled" />
                <MiniCheck ok={hasLivePage} label="Events page" />
                <MiniCheck ok={hasPage} label="Posting page" />
                <MiniCheck ok={hasCadence} label="Cadence" />
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {draftsCount} draft{draftsCount === 1 ? '' : 's'}, {postedCount} posted
                </span>
            </div>
            {nextStep && <NextStepHint action={action}>{nextStep}</NextStepHint>}
        </div>
    );
}

const inputStyle = {
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px'
};

// Big, clickable "i" badge that toggles a text block open/closed - lets a
// section header stay a compact one-liner instead of always showing a full
// explanatory paragraph underneath it. Click-to-toggle (not hover) so it
// works the same on touch devices, with a click-outside listener to close
// it since it isn't a native <details> element.
function InfoTooltip({ text }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <span ref={wrapperRef} style={{ position: 'relative', display: 'inline-block', marginLeft: '8px' }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="More info"
                aria-expanded={open}
                style={{
                    width: '22px', height: '22px', borderRadius: '50%', border: '1px solid #6f42c1', backgroundColor: open ? '#6f42c1' : '#f8f9fa',
                    color: open ? 'white' : '#6f42c1', fontSize: '14px', lineHeight: '20px', padding: 0, cursor: 'pointer', fontStyle: 'italic',
                    fontFamily: 'Georgia, serif', fontWeight: 'bold', verticalAlign: 'middle'
                }}
            >
                i
            </button>
            {open && (
                <div style={{
                    position: 'absolute', zIndex: 10, top: '28px', left: 0, width: 'min(300px, 85vw)',
                    backgroundColor: '#333', color: 'white', fontSize: '12px', fontWeight: 'normal',
                    lineHeight: '1.5', padding: '10px 12px', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                }}>
                    {text}
                </div>
            )}
        </span>
    );
}

// Stats tab only - fetches one Buffer-posted post's experimental metrics
// on click rather than eagerly for every row, since it's a real API call
// per post and Buffer itself warns this data can be unreliable/missing.
function PostMetricsButton({ postId, adminToken }) {
    const [state, setState] = useState('idle'); // idle | loading | done | error
    const [metrics, setMetrics] = useState([]);
    const [error, setError] = useState('');

    const fetchMetrics = async () => {
        setState('loading');
        try {
            const response = await fetch(`/api/admin/socials/post-metrics?post_id=${encodeURIComponent(postId)}`, {
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to fetch metrics');
            setMetrics(data.metrics || []);
            setState('done');
        } catch (err) {
            setError(err.message);
            setState('error');
        }
    };

    if (state === 'idle') {
        return (
            <button
                type="button"
                onClick={fetchMetrics}
                style={{ fontSize: '11px', border: 'none', background: 'none', color: '#6f42c1', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
                Check metrics (experimental)
            </button>
        );
    }
    if (state === 'loading') return <span style={{ fontSize: '11px', color: '#999' }}>Checking...</span>;
    if (state === 'error') return <span style={{ fontSize: '11px', color: '#999' }}>Metrics unavailable ({error})</span>;
    if (metrics.length === 0) return <span style={{ fontSize: '11px', color: '#999' }}>Buffer hasn't reported any metrics for this post yet</span>;
    return (
        <span style={{ fontSize: '11px', color: '#666' }}>
            {metrics.map((m) => `${m.type}: ${m.value}`).join(' · ')}
        </span>
    );
}

const DEFAULT_ANGLES = [
    '10 things to do in {city} this weekend',
    '5 events for meeting new people in {city}',
    'Free events in {city} this week',
];

const STATUS_COLORS = {
    draft: { bg: '#e2e3e5', text: '#41464b' },
    approved: { bg: '#d4edda', text: '#155724' },
    posted: { bg: '#cce5ff', text: '#004085' },
    archived: { bg: '#f8d7da', text: '#721c24' },
};

// Drives the status filter button row in the "Review & post drafts" card -
// 'approved' is labeled "Scheduled" there since, now that approving
// auto-schedules/auto-posts, that's the more accurate name for what this
// status now means to an admin flipping between views.
const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Drafts' },
    { value: 'approved', label: 'Scheduled' },
    { value: 'posted', label: 'Posted' },
    { value: 'archived', label: 'Archived' },
];

const PLATFORM_LABELS = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn' };
const PROVIDER_LABELS = { buffer: 'Buffer', meta_direct: 'direct Meta' };

// Which provider actually sends a given draft, per platform leg - mirrors
// app.py's _queue_post_dispatched routing exactly (linkedin always Buffer,
// instagram always direct Meta, facebook follows that page's own
// SocialPage.provider) so the admin UI never has to guess. `page` is the
// draft's resolved SocialPage (via page_id) - undefined/null falls back to
// 'meta_direct', the same default _queue_post_dispatched itself uses when
// no page is found.
// Whether a draft's target_date/target_time (both wall-clock Europe/London
// - see SocialPostDraft.target_time's docstring) is still in the future,
// compared purely as "YYYY-MM-DDTHH:MM" strings in the UK's own current
// wall-clock time (via Intl, DST-aware) rather than building a JS Date and
// doing real timezone math - simpler, and correct for this string
// comparison since both sides are zero-padded ISO-shaped. This is only a
// display hint for the admin; app.py's own _scheduled_instant_utc is the
// actual enforcement point.
function isFutureUkInstant(dateStr, timeStr) {
    if (!dateStr || !timeStr) return false;
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const nowUk = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    const targetUk = `${dateStr}T${timeStr.slice(0, 5)}`;
    return targetUk > nowUk;
}

function postingLegsFor(draft, page) {
    const facebookProvider = page?.provider === 'buffer' ? 'buffer' : 'meta_direct';
    if (draft.platform === 'both') {
        return [
            { platform: 'facebook', provider: facebookProvider },
            { platform: 'instagram', provider: 'meta_direct' },
        ];
    }
    if (draft.platform === 'linkedin') return [{ platform: 'linkedin', provider: 'buffer' }];
    if (draft.platform === 'instagram') return [{ platform: 'instagram', provider: 'meta_direct' }];
    return [{ platform: 'facebook', provider: facebookProvider }];
}

// Fixed total posts/week across a page's active objectives (see
// CadenceRatioEditor and app.py's upsert_social_page_cadence, which
// enforces this same number server-side). Not admin-adjustable - the admin
// only sets each objective's relative share of it, deliberately a fixed
// platform-wide policy rather than a per-page setting, so it can't be
// nudged up "just this once" and drift into spam-flag territory.
const MAX_POSTS_PER_WEEK_PER_PAGE = 14; // 2/day
const CADENCE_CAP_HELP_TEXT = (
    'Fixed at 2 posts per day (14 per week) total across all objectives for this page, not admin-adjustable. '
    + 'Posting less than once a day is too infrequent to build an audience, but posting more than twice a day '
    + "risks the page being flagged as spam by Facebook/Instagram/LinkedIn's own systems - 2/day is the sweet spot."
);

// Seed weights for CadenceRatioEditor when a page has no cadence set yet -
// matches the "use Conversion sparingly, roughly 1 in every 5-7 posts"
// guidance already given in that objective's own strategy_instructions.
// Matched by label since objectives are admin-editable/addable rather than
// a fixed enum - an unrecognized label (a custom objective, or one of these
// renamed) falls back to CADENCE_DEFAULT_WEIGHT_FALLBACK, a neutral middle
// weight that neither dominates nor gets crowded out by the two defaults
// above.
const CADENCE_DEFAULT_WEIGHTS = {
    'Engagement': 45,
    'Follower Growth': 45,
    'Conversion (Subscribe Clicks)': 10,
};
const CADENCE_DEFAULT_WEIGHT_FALLBACK = 30;

// Largest-remainder apportionment: splits `total` (a whole number of posts/
// week) across objectives in proportion to their relative weight, always
// summing to exactly `total` (never over, unlike naive independent
// rounding, which can push several objectives' rounded-up shares past the
// page-wide cap - see app.py's upsert_social_page_cadence, which would
// reject that). An objective with weight 0 always gets 0. An objective
// whose exact share rounds below 1 can still end up at 0 (no forced
// minimum) - correctly reflects that the requested total is too small to
// give every active objective its own weekly post; CadenceRatioEditor
// surfaces that in the UI rather than silently forcing a 1.
function apportionByWeight(total, weightsById) {
    const ids = Object.keys(weightsById).filter((id) => weightsById[id] > 0);
    const sumWeights = ids.reduce((s, id) => s + weightsById[id], 0);
    const result = {};
    Object.keys(weightsById).forEach((id) => { result[id] = 0; });
    if (total <= 0 || sumWeights <= 0) return result;

    const exact = {};
    let allocated = 0;
    ids.forEach((id) => {
        const share = (total * weightsById[id]) / sumWeights;
        const base = Math.floor(share);
        result[id] = base;
        exact[id] = share - base; // fractional remainder
        allocated += base;
    });
    let remaining = total - allocated;
    // Largest remainder first - ties broken by id order, which is stable
    // and good enough (no meaningful reason to prefer one objective over
    // another on an exact tie).
    const byRemainder = ids.slice().sort((a, b) => exact[b] - exact[a]);
    for (let i = 0; i < byRemainder.length && remaining > 0; i++) {
        result[byRemainder[i]] += 1;
        remaining -= 1;
    }
    return result;
}

// OkGather's own Meta Business Manager - safe to hardcode, this admin panel
// only ever manages this one business. META_SYSTEM_USER_URL goes straight
// to the "DirectPosting" System User (confirmed working URL, points at the
// exact page where a new Page/Instagram account gets assigned as an
// asset); META_BUSINESS_SETTINGS_URL is the generic Business Settings
// root (Meta's stable top-level settings URL) for the "find the ID" step,
// since the exact Accounts > Pages / Accounts > Instagram accounts sub-page
// URL hasn't been confirmed and a wrong deep link would be worse than a
// generic one.
const META_SYSTEM_USER_URL = 'https://business.facebook.com/latest/settings/system_users?business_id=969002232028794&selected_user_id=61594398807328';
const META_BUSINESS_SETTINGS_URL = 'https://business.facebook.com/settings?business_id=969002232028794';

function ExternalLink({ href, children }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1', fontWeight: 'bold' }}>
            {children} ↗
        </a>
    );
}

// Connecting a new page for direct Meta posting is two steps, both quick
// once done the first time (the App/System User setup itself is one-time,
// never repeated): 1) grant our System User access to the page, 2) find
// the ID and paste it here. Shown as a tooltip right on the field since
// this is easy to forget between uses. JSX (not a plain string) so the
// links inside are actually clickable - InfoTooltip renders `text` as a
// plain React child, so JSX works here same as a string would.
const META_PAGE_ID_HELP_TEXT = (
    <>
        To connect a new page: (0) If this Page was created while just logged into a personal Facebook account
        (not through the Business Portfolio), it won't show up in step 1 yet - first, in{' '}
        <ExternalLink href={META_BUSINESS_SETTINGS_URL}>Business Settings</ExternalLink>, go to Accounts &gt; Pages
        &gt; Add &gt; Add a Page, and select it there. This just links the existing page in (you keep your personal
        admin access, nothing is recreated or moved) - skip this step if the page already shows up in step 1. (1) In{' '}
        <ExternalLink href={META_SYSTEM_USER_URL}>Meta System Users settings</ExternalLink>,
        assign this Page (or Instagram account) to "DirectPosting" as an asset with the "Content" permission - takes
        ~30 seconds since the App and System User already exist and work. (2) Find the ID: on the Page itself
        (facebook.com), go to About &gt; Page transparency (or Settings &gt; Page transparency), which shows
        "Page ID: ..." - this is the one to use, not the number in a facebook.com/profile.php?id= URL, which can be
        a different id. Alternatively, if you're viewing the page inside Business Manager (having clicked it from
        the business's asset list), the asset_id in that page's own URL is also the Page ID, e.g.
        business.facebook.com/.../?...&amp;asset_id=1234567890 - the digits after asset_id=. (For an Instagram
        account there's no separate "transparency" page - use the Business Manager asset_id method instead.) No
        Heroku config changes needed for a new page - the access token is shared across every page.
    </>
);

// Rough, industry-typical ranges for a brand-new small/local-interest page
// posting consistently at the frequencies suggested above - NOT a
// guarantee, and a hyper-local city page starts from a smaller addressable
// audience than the national-brand benchmarks these are drawn from. Meant
// as a sanity-check reference for the Timeline tab ("are we in a plausible
// ballpark"), not a target to chase.
const GROWTH_BENCHMARKS = {
    facebook: [
        { milestone: 'Week 1', followers: '10-50', engagement: '1-3%', clicks: 'a handful' },
        { milestone: 'Month 1', followers: '50-300', engagement: '1-3%', clicks: '10-40/week' },
        { milestone: 'Month 3', followers: '200-1,000', engagement: '2-4%', clicks: '30-120/week' },
        { milestone: 'Month 6', followers: '500-3,000', engagement: '2-4%', clicks: '50-250/week' },
    ],
    instagram: [
        { milestone: 'Week 1', followers: '20-80', engagement: '2-4%', clicks: 'a handful' },
        { milestone: 'Month 1', followers: '80-400', engagement: '2-5%', clicks: '15-50/week' },
        { milestone: 'Month 3', followers: '300-1,500', engagement: '3-6%', clicks: '40-150/week' },
        { milestone: 'Month 6', followers: '800-4,000', engagement: '3-6%', clicks: '60-300/week' },
        // Reels-heavy accounts routinely beat the top of these ranges - see
        // the "Reels vs. static images" best-practice note, this pipeline
        // only produces a static image per post.
    ],
    linkedin: [
        { milestone: 'Week 1', followers: '10-30', engagement: '2-5%', clicks: 'a handful' },
        { milestone: 'Month 1', followers: '30-150', engagement: '2-6%', clicks: '5-20/week' },
        { milestone: 'Month 3', followers: '100-500', engagement: '2-6%', clicks: '10-40/week' },
        { milestone: 'Month 6', followers: '250-1,200', engagement: '2-6%', clicks: '20-70/week' },
    ],
};

// generate-post-drafts prefixes a draft's angle with "[Objective Label] "
// when one was picked (see app.py) - no schema change needed on
// SocialPostDraft to track it. Split back out for display/filtering.
function parseAngle(angle) {
    const m = /^\[([^\]]+)\]\s*(.*)$/.exec(angle || '');
    return m ? { objective: m[1], text: m[2] } : { objective: null, text: angle || '' };
}

// Both templates are functions of the live city value (re-rendered on every
// keystroke), not a static string with a bracket placeholder to edit by
// hand - the admin doesn't have to remember to swap anything before
// copying. Both explicitly ask for the structured event data (with real
// links) in a fixed "Name | Date | Description | URL" format, matching
// what parseManualEvents below expects - that's the piece a pure "write me
// a caption" prompt would miss: without it, there's no way to get event
// data with real links into a page on OkGather from what the LLM gives
// back, only a caption.
function buildRoundupTemplate(city) {
    const c = city.trim() || '[CITY]';
    return `I'm creating a social media post and an events listing for OkGather (a social events platform) covering ${c}.

Find 5-10 real, currently-happening events in ${c} in the next 7 days that would appeal to people in their 20s and 30s who want to meet new people - markets, festivals, live music, food/drink, open-to-everyone meetups. Favor broadly-appealing events over ones organized around a specific identity/demographic group unless that's genuinely the best fit for this list. Only include events you can find a real source/link for - never invent one.

For each event give me: name, date/time, a short 1-2 sentence description, and the real event URL (Eventbrite/organiser site/etc).

Then give me two things:

1. One Facebook/Instagram caption rounding up these events for ${c} - punchy, warm, no em dashes, ending with a line inviting people to tag who they'd bring. Don't put any links in the caption itself, I'll add those separately.

2. The same events in exactly this format, one per line, so I can paste them straight into our system:
Name | Date | Description | URL`;
}

function buildSpotlightTemplate(city) {
    const c = city.trim() || '[CITY]';
    return `I'm creating a daily social media post and an events listing for OkGather (a social events platform) covering ${c}.

Find ONE standout real event happening in ${c} today or in the next few days that would appeal to people in their 20s and 30s who want to meet new people. Only use it if you can find a real source/link for it - never invent one.

Give me: name, date/time, a 2-3 sentence description (what it is, why it's good for meeting people), and the real event URL.

Then give me two things:

1. One Instagram/Facebook caption spotlighting just this event - punchy, warm, no em dashes, ending with a line inviting people to tag who they'd bring, and a line pointing people to see the rest of what's on in ${c} on OkGather (not a hard sales pitch). Don't put any links in the caption itself, I'll add those separately.

2. The event in exactly this format so I can paste it straight into our system:
Name | Date | Description | URL`;
}

// One event per line: "Name | Date | Description | URL" - Date/Description/URL
// are all optional (a bare name-only line still counts), matching what the
// template prompts above ask an external AI chat tool to hand back.
function parseManualEvents(text) {
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const parts = line.split('|').map((p) => p.trim());
            return {
                name: parts[0] || '',
                date: parts[1] || '',
                description: parts[2] || '',
                source_url: parts[3] || '',
            };
        })
        .filter((e) => e.name);
}

// Matches jobs/generate_scheduled_social_posts.py's BATCH_SIZE/
// BATCH_INTERVAL_DAYS - a page's total is always the fixed 14/week (2/day),
// so a batch of 4 distinct posts is due every 2 days, regardless of how
// that 14/week is split across objectives.
const CADENCE_BATCH_SIZE = 4;
const CADENCE_BATCH_INTERVAL_DAYS = 2;

// Every timestamp (ms) a page's next BATCH_SIZE-post batch is expected to
// generate within the next `days`, mirroring jobs/generate_scheduled_social_posts.py's
// _is_page_batch_due (a fixed interval, not per-objective posts_per_week -
// due-ness is tracked per PAGE now, not per (page, objective) cadence).
// cadencesForPage's cadences all share the same last_generated_at (the job
// advances them together - see SocialPageObjectiveCadence's docstring), so
// any one of them reflects the whole page. A page overdue or never yet
// generated for (last_generated_at null) counts its first occurrence as
// "now" - the job would pick it up on its very next run. Purely a display
// estimate: the job still has to actually run (on whatever schedule Heroku
// Scheduler uses) and event discovery still has to succeed, so this is
// "roughly when", never a guarantee.
function pageBatchOccurrencesWithin(cadencesForPage, days) {
    const intervalMs = CADENCE_BATCH_INTERVAL_DAYS * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const lastGeneratedAt = cadencesForPage[0]?.last_generated_at;
    let next = lastGeneratedAt ? new Date(lastGeneratedAt).getTime() + intervalMs : now;
    if (next < now) next = now;
    const cutoff = now + days * 24 * 60 * 60 * 1000;
    const occurrences = [];
    while (next <= cutoff) {
        occurrences.push(next);
        next += intervalMs;
    }
    return occurrences;
}

// "due any time now" / "in ~4h" / "in ~3 days" - matches this file's
// existing habit of hedging automatic-timing copy ("usually within a
// minute or two") rather than promising an exact time.
function formatRelativeEta(timestampMs) {
    const diffMs = timestampMs - Date.now();
    if (diffMs <= 15 * 60 * 1000) return 'due any time now';
    const hours = diffMs / (60 * 60 * 1000);
    if (hours < 36) return `in ~${Math.round(hours)}h`;
    return `in ~${Math.round(hours / 24)} days`;
}

function TemplateBlock({ title, description, template }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(template);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', margin: '0 0 3px 0' }}>{title}</p>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0' }}>{description}</p>
            <pre style={{
                whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa', border: '1px solid #eee', borderRadius: '4px',
                padding: '14px', fontSize: '13px', fontFamily: 'Menlo, Consolas, monospace', lineHeight: '1.6', margin: '0 0 8px 0'
            }}>
                {template}
            </pre>
            <button
                type="button"
                onClick={handleCopy}
                style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
            >
                {copied ? 'Copied!' : 'Copy Template'}
            </button>
        </div>
    );
}

function ObjectiveCard({ objective, adminToken, onChange, onDelete }) {
    const [label, setLabel] = useState(objective.label);
    const [description, setDescription] = useState(objective.description || '');
    const [strategy, setStrategy] = useState(objective.strategy_instructions);
    const [saving, setSaving] = useState(false);
    const dirty = label !== objective.label
        || description !== (objective.description || '')
        || strategy !== objective.strategy_instructions;

    const patch = async (fields) => {
        setSaving(true);
        try {
            const response = await fetch(`/api/admin/socials/objectives/${objective.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify(fields)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update objective');
            }
            onChange(await response.json());
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ border: '1px solid #eee', borderRadius: '6px', padding: '15px', marginBottom: '12px', opacity: objective.is_active ? 1 : 0.55 }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    style={{ ...inputStyle, fontWeight: 'bold', flex: '1 1 200px' }}
                />
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={objective.is_active} onChange={(e) => patch({ is_active: e.target.checked })} disabled={saving} />
                    Active
                </label>
                <button type="button" onClick={() => onDelete(objective.id)} disabled={saving}
                    style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #dc3545', borderRadius: '4px', backgroundColor: 'white', color: '#dc3545', cursor: 'pointer' }}>
                    Delete
                </button>
            </div>
            <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short description (shown in the objective picker)"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px', fontSize: '13px' }}
            />
            <textarea
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
                rows={4}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px', fontFamily: 'inherit', fontSize: '13px' }}
            />
            <button
                type="button"
                onClick={() => patch({ label, description, strategy_instructions: strategy })}
                disabled={saving || !dirty}
                style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: dirty ? '#6f42c1' : '#eee', color: dirty ? 'white' : '#999', cursor: dirty ? 'pointer' : 'default' }}
            >
                Save
            </button>
        </div>
    );
}

function DraftRow({ draft, adminToken, onChange, onDelete, automationStatus, onGoToManualTools, batchSize, batchChosen, pages }) {
    const [caption, setCaption] = useState(draft.caption);
    const [hashtags, setHashtags] = useState(draft.hashtags || '');
    // Tracks WHICH action is in flight, not just whether one is - so each
    // button below can show its own "Saving..."/"Posting..." label instead
    // of every button in the row looking identical while only one of them
    // is actually doing something (the bug: clicking Approve gave no
    // visible feedback until the request finished, since disabled-but-same-
    // label doesn't read as "in progress" at a glance). `saving` stays a
    // plain boolean for every existing disabled={saving} check below, which
    // still correctly blocks a second click while ANY action is running.
    const [pendingAction, setPendingAction] = useState(null);
    const saving = pendingAction !== null;
    const [copied, setCopied] = useState(false);
    const [commentCopied, setCommentCopied] = useState(false);
    // Which platform's failure message was just copied (not a plain boolean -
    // a draft can have more than one failed platform at once) - lets an
    // admin grab the exact error text (e.g. to paste into a support request
    // or hand to whoever's debugging it) without reading it off a hover
    // tooltip or re-triggering the same failure just to see it again.
    const [copiedErrorPlatform, setCopiedErrorPlatform] = useState(null);
    const dirty = caption !== draft.caption || hashtags !== (draft.hashtags || '');

    // Shows a large banner right after a successful approve (see the patch()
    // success branch below) instead of relying on the admin to notice the
    // button label change to "Approved (check)" or spot the smaller queue
    // badges further down - self-clears after a few seconds so it doesn't
    // linger once the admin has moved on.
    const [justApprovedMessage, setJustApprovedMessage] = useState(null);
    const justApprovedTimeoutRef = useRef(null);
    useEffect(() => () => clearTimeout(justApprovedTimeoutRef.current), []);

    const [linkPlatform, setLinkPlatform] = useState('facebook');
    const [linkUrl, setLinkUrl] = useState('');
    const [addingLink, setAddingLink] = useState(false);
    const [linkError, setLinkError] = useState('');

    const handleAddPublication = async () => {
        if (!linkUrl.trim()) {
            setLinkError('Paste the live post URL first');
            return;
        }
        setAddingLink(true);
        setLinkError('');
        try {
            const response = await fetch(`/api/admin/socials/post-drafts/${draft.id}/publications`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({ platform: linkPlatform, post_url: linkUrl.trim() })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add link');
            }
            const publication = await response.json();
            onChange({ ...draft, publications: [...(draft.publications || []), publication] });
            setLinkUrl('');
        } catch (err) {
            setLinkError(err.message);
        } finally {
            setAddingLink(false);
        }
    };

    const patch = async (fields, actionName = 'save') => {
        setPendingAction(actionName);
        try {
            const response = await fetch(`/api/admin/socials/post-drafts/${draft.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify(fields)
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update draft');
            }
            const updated = await response.json();
            // The PATCH response's scheduler_queue (if present) is only the
            // entry from *this* approval, not the draft's full history -
            // merge rather than replace so an earlier attempt's entry (e.g.
            // from re-approving after moving back to draft) isn't lost.
            onChange({
                ...updated,
                scheduler_queue: updated.scheduler_queue
                    ? [...(draft.scheduler_queue || []), ...updated.scheduler_queue]
                    : draft.scheduler_queue,
            });
            if (updated.page_update_error) alert(updated.page_update_error);
            // A bigger, harder-to-miss confirmation than the button label
            // alone flipping to "Approved (check)" - built from the SAME
            // postingLegs/willHoldForSchedule this row already worked out
            // for the "Posts immediately"/"Holds for scheduled time" pill
            // above, since approving doesn't change platform/date/time.
            if (actionName === 'approve' && updated.status === 'approved') {
                const automationConfigured = automationStatus?.buffer_configured || automationStatus?.meta_direct_configured;
                const message = !automationConfigured
                    ? 'Approved. No posting automation is connected yet, so post it manually using the copy buttons above, then click "Mark posted" once it is live.'
                    : `Approved - ${postingLegs.map((leg) => {
                        const label = PLATFORM_LABELS[leg.platform] || leg.platform;
                        if (leg.provider === 'meta_direct') {
                            return willHoldForSchedule
                                ? `${label} is scheduled and will post automatically via direct Meta at the time set above`
                                : `${label} just went live via direct Meta`;
                        }
                        return `${label} has been added to Buffer's own queue`;
                    }).join('; ')}.`;
                clearTimeout(justApprovedTimeoutRef.current);
                setJustApprovedMessage(message);
                justApprovedTimeoutRef.current = setTimeout(() => setJustApprovedMessage(null), 10000);
            }
        } catch (err) {
            alert(err.message);
        } finally {
            setPendingAction(null);
        }
    };

    // "Post now" (Approved/posted/archived table, expanded row) - forces
    // immediate dispatch of whichever platform(s) are still sitting in
    // 'scheduled' (see SocialSchedulerQueueEntry's docstring), for when an
    // admin doesn't want to wait for jobs/dispatch_scheduled_meta_posts.py's
    // next hourly run. Separate from patch() since it's not a field edit -
    // the backend resolves exactly which pending leg(s) to fire itself
    // (never re-touches an already-queued/failed leg, so a platform='both'
    // draft where only Instagram is still pending can't accidentally
    // double-post Facebook).
    const handleDispatchNow = async () => {
        setPendingAction('dispatch-now');
        try {
            const response = await fetch(`/api/admin/socials/post-drafts/${draft.id}/dispatch-now`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to post now');
            onChange({
                ...draft,
                // status/posted_at are only present when this dispatch just
                // completed the draft's last outstanding direct-Meta leg -
                // see app.py's _maybe_mark_posted_from_direct_dispatch.
                ...(data.status ? { status: data.status, posted_at: data.posted_at } : {}),
                scheduler_queue: [...(draft.scheduler_queue || []), ...(data.scheduler_queue || [])],
            });
        } catch (err) {
            alert(err.message);
        } finally {
            setPendingAction(null);
        }
    };

    const handleCopy = () => {
        const text = hashtags ? `${caption}\n\n${hashtags}` : caption;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopyComment = () => {
        navigator.clipboard.writeText(draft.suggested_first_comment || '');
        setCommentCopied(true);
        setTimeout(() => setCommentCopied(false), 2000);
    };

    // The only way to unlock a batch's other variations once one is
    // approved (see SocialPostDraftRepository.update) - moves this one back
    // to 'draft' so it stops matching the ("approved", "posted") check that
    // blocks its siblings from being approved. Plain confirm text for a
    // standalone draft; batch-specific wording only when it's actually
    // relevant, so this reads the same as before for anyone not using
    // batches.
    const handleRevoke = () => {
        const question = draft.status === 'posted'
            ? "Revoke this? It's marked posted, so if it already went out, this only resets tracking here - it won't unpost anything."
            + (batchSize ? ' The other variations in this batch become approvable again.' : '')
            : 'Revoke approval and move this back to draft?' + (batchSize ? ' The other variations in this batch become approvable again.' : '');
        if (!window.confirm(question)) return;
        patch({ status: 'draft' }, 'revoke');
    };

    const { objective: draftObjective, text: angleText } = parseAngle(draft.angle);

    // scheduler_queue accumulates every attempt ever made (see the merge
    // comment on `patch` above), so the LATEST entry per platform is what
    // actually reflects "did this end up queued". A Buffer+Instagram entry
    // is always a permanent "can't do this" failure (Buffer never supported
    // posting to Instagram at all - see social_scheduler.py), never
    // something retrying helps with, so that specific combination is
    // excluded here - direct Meta's own Instagram support has no such
    // limitation, so an Instagram failure via meta_direct is just as
    // retryable as any other platform (a transient error, or a since-fixed
    // one, e.g. the token/permission issues this was originally built to
    // recover from).
    const latestQueueByPlatform = {};
    (draft.scheduler_queue || []).forEach((entry) => { latestQueueByPlatform[entry.platform] = entry; });
    const retryableFailures = Object.values(latestQueueByPlatform)
        .filter((entry) => entry.status === 'failed' && !(entry.provider === 'buffer' && entry.platform === 'instagram'));
    const retryableQueueFailure = retryableFailures.length > 0;
    const retryableProviderLabel = Array.from(new Set(retryableFailures.map((e) => PROVIDER_LABELS[e.provider] || e.provider))).join('/');
    // Whether at least one platform is still held back for its scheduled
    // time (see SocialSchedulerQueueEntry's docstring) - drives the "Post
    // now" button below, which forces jobs/dispatch_scheduled_meta_posts.py's
    // work to happen immediately instead of waiting for its next run.
    const hasPendingScheduled = Object.values(latestQueueByPlatform).some((entry) => entry.status === 'scheduled');

    // Which provider(s) approving this draft will actually dispatch to -
    // see postingLegsFor's docstring. Only meaningful pre-approval (once
    // approved/posted, latestQueueByPlatform above shows what actually
    // happened instead).
    const resolvedPage = pages?.find((p) => p.id === draft.page_id);
    const postingLegs = postingLegsFor(draft, resolvedPage);
    const goesToMeta = postingLegs.some((leg) => leg.provider === 'meta_direct');
    // A meta_direct leg only actually WAITS for the scheduled time if one is
    // set and still in the future - otherwise approving posts it right away
    // (see app.py's _queue_post_dispatched). Buffer legs are unaffected
    // either way, Buffer always manages its own queue/schedule.
    const willHoldForSchedule = goesToMeta && isFutureUkInstant(draft.target_date, draft.target_time);

    return (
        <div className="draft-row-card" style={{
            border: '1px solid #e2e2e2', borderRadius: '8px', padding: '14px',
            marginBottom: '14px', backgroundColor: '#fff', boxSizing: 'border-box',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                {draftObjective && (
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#e2d9f3', color: '#4b2e83' }}>
                        {draftObjective}
                    </span>
                )}
                <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
                    backgroundColor: STATUS_COLORS[draft.status]?.bg, color: STATUS_COLORS[draft.status]?.text
                }}>
                    {draft.status}
                </span>
                {batchSize && (
                    <span
                        title="Generated together as alternate takes on the same events - approving one locks the rest until that approval is revoked, only one can go out at a time."
                        style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#e2d9f3', color: '#4b2e83' }}
                    >
                        One of {batchSize} variations - pick one
                    </span>
                )}
            </div>
            <p style={{ fontWeight: 'bold', fontSize: '17px', margin: '0 0 10px 0', lineHeight: '1.3' }}>{angleText}</p>

            <div className="draft-row-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
                <label className="draft-row-field">
                    <span className="draft-row-field-label">Platform</span>
                    <select
                        value={draft.platform}
                        onChange={(e) => patch({ platform: e.target.value })}
                        disabled={saving}
                        style={{ ...inputStyle, padding: '5px 6px', fontSize: '12px' }}
                    >
                        <option value="both">Instagram + Facebook</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="linkedin">LinkedIn</option>
                    </select>
                </label>
                <label className="draft-row-field">
                    <span className="draft-row-field-label">Date</span>
                    <input
                        type="date"
                        value={draft.target_date || ''}
                        onChange={(e) => patch({ target_date: e.target.value || null })}
                        disabled={saving}
                        style={{ ...inputStyle, padding: '5px 6px', fontSize: '12px' }}
                    />
                </label>
                {goesToMeta && (
                    <>
                        <label className="draft-row-field">
                            <span className="draft-row-field-label">Time (UK)</span>
                            <input
                                type="time"
                                value={draft.target_time ? draft.target_time.slice(0, 5) : ''}
                                onChange={(e) => patch({ target_time: e.target.value || null })}
                                disabled={saving}
                                style={{ ...inputStyle, padding: '5px 6px', fontSize: '12px' }}
                            />
                        </label>
                        <div className="draft-row-field">
                            <span className="draft-row-field-label">Quick set</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                {[['11:30', '11:30am'], ['18:30', '6:30pm']].map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => patch({ target_time: value })}
                                        disabled={saving}
                                        style={{
                                            flex: '1 1 auto', padding: '5px 6px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '4px',
                                            backgroundColor: draft.target_time?.slice(0, 5) === value ? '#e2d9f3' : 'white',
                                            color: draft.target_time?.slice(0, 5) === value ? '#4b2e83' : '#666', cursor: 'pointer',
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {draft.status === 'draft' && (
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                    {postingLegs.map((leg) => (
                        <span key={leg.platform} style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
                            backgroundColor: '#f1f3f5', color: '#495057',
                        }}>
                            {PLATFORM_LABELS[leg.platform] || leg.platform} via {PROVIDER_LABELS[leg.provider] || leg.provider}
                        </span>
                    ))}
                    {goesToMeta && (
                        <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
                            backgroundColor: willHoldForSchedule ? '#d0e6ff' : '#fff3cd',
                            color: willHoldForSchedule ? '#004085' : '#856404',
                        }}>
                            {willHoldForSchedule ? 'Holds for scheduled time' : 'Posts immediately on approval'}
                        </span>
                    )}
                    <InfoTooltip text={
                        goesToMeta
                            ? "Direct Meta legs only wait for a scheduled time if you set a future time above - otherwise approving posts them right away. Set a time (or use the 11:30am/6:30pm quick-picks, the two strongest UK posting windows for this kind of content) to hold it instead; a background job checks every so often and fires it once that time arrives, even if you're not online. Buffer legs (if any) always get queued into Buffer's own schedule regardless."
                            : "Buffer queues this for its own posting schedule once approved, regardless of the date above - that's just this admin panel's planning label."
                    } />
                </div>
            )}

            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0' }}>
                {draft.city}
                {draft.city_page ? (
                    <>
                        {' - events page: '}
                        <a href={draft.city_page.url} target="_blank" rel="noopener noreferrer">{draft.city_page.url}</a>
                    </>
                ) : (
                    <>
                        {' - no events page published for this city yet'}
                        {onGoToManualTools && (
                            <button
                                type="button"
                                onClick={() => onGoToManualTools(draft.city)}
                                style={{
                                    marginLeft: '8px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold',
                                    border: '1px solid #6f42c1', borderRadius: '4px', backgroundColor: 'white',
                                    color: '#6f42c1', cursor: 'pointer',
                                }}
                            >
                                Find Events &rarr;
                            </button>
                        )}
                    </>
                )}
            </p>

            <div style={{ marginBottom: '10px' }}>
                <img
                    src={`/api/social/post-image/${draft.id}.png`}
                    alt="Branded card attached to this post"
                    style={{ width: '100%', maxWidth: '360px', display: 'block', borderRadius: '8px', border: '1px solid #eee' }}
                />
                <p style={{ fontSize: '11px', color: '#999', margin: '4px 0 0 0' }}>
                    Auto-generated from the angle above and attached automatically for Facebook/LinkedIn - not sent for Instagram, which Buffer can't post to here at all yet.
                </p>
            </div>

            <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={9}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px', fontFamily: 'inherit', lineHeight: '1.5' }}
            />
            <input
                type="text"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="Hashtags"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '10px' }}
            />

            {draft.suggested_first_comment && (
                <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', margin: '0 0 4px 0' }}>
                        {automationStatus?.buffer_configured
                            ? 'Suggested first comment (attached automatically on approval for Facebook/LinkedIn - copy here if posting Instagram or by hand)'
                            : 'Suggested first comment (paste this as a comment right after posting, not in the caption)'}
                    </p>
                    <pre style={{
                        whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa', border: '1px solid #eee', borderRadius: '4px',
                        padding: '10px', fontSize: '12px', fontFamily: 'inherit', lineHeight: '1.5', margin: '0 0 6px 0'
                    }}>
                        {draft.suggested_first_comment}
                    </pre>
                    <button
                        type="button"
                        onClick={handleCopyComment}
                        style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                    >
                        {commentCopied ? 'Copied!' : 'Copy first comment'}
                    </button>
                </div>
            )}

            {justApprovedMessage && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px',
                    margin: '0 0 10px 0', fontSize: '13px', fontWeight: 'bold', backgroundColor: '#d4edda', color: '#155724',
                }}>
                    <span style={{ fontSize: '16px' }}>✅</span>
                    <span>{justApprovedMessage}</span>
                </div>
            )}

            <div className="draft-row-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onClick={() => patch({ caption, hashtags }, 'save')}
                    disabled={saving || !dirty}
                    style={{
                        padding: '7px 14px', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '4px',
                        backgroundColor: dirty ? '#6f42c1' : '#eee', color: dirty ? 'white' : '#999', cursor: dirty ? 'pointer' : 'default',
                        boxShadow: dirty ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', opacity: pendingAction === 'save' ? 0.7 : 1,
                    }}
                >
                    {pendingAction === 'save' ? 'Saving...' : 'Save edits'}
                </button>
                <button
                    type="button"
                    onClick={handleCopy}
                    style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                >
                    {copied ? 'Copied!' : 'Copy caption + hashtags'}
                </button>
                {draft.status !== 'approved' && batchChosen && (
                    <span style={{ fontSize: '12px', color: '#999', alignSelf: 'center' }} title="Revoke that approval first if you want to switch">
                        Locked - "{parseAngle(batchChosen.angle).text}" is approved instead
                    </span>
                )}
                {!(draft.status !== 'approved' && batchChosen) && (() => {
                    // Once approved, this button never disappears - it either
                    // greys out ("done", nothing left to do) or, if the last
                    // attempt for a real platform failed, stays live as an
                    // explicit retry so a transient/now-fixed failure (e.g.
                    // an expired token or a permissions issue since fixed)
                    // isn't a dead end that requires un-approving and
                    // re-approving to try again. Never names a single
                    // provider ("Buffer") here - which provider actually
                    // handles this draft depends on its platform(s) and
                    // each page's own provider setting (see SocialPage's
                    // docstring), so it could be Buffer, direct Meta, or
                    // both at once for a platform='both' draft.
                    const automationConfigured = automationStatus?.buffer_configured || automationStatus?.meta_direct_configured;
                    const isApproved = draft.status === 'approved';
                    const isDone = isApproved && !retryableQueueFailure;
                    const isPending = pendingAction === 'approve';
                    const label = isPending
                        ? 'Saving...'
                        : !isApproved
                        ? `Mark approved${automationConfigured ? ' (auto-schedules)' : ''}`
                        : retryableQueueFailure ? `Retry sending (${retryableProviderLabel})` : 'Approved ✓';
                    const title = !isApproved
                        ? ([
                            batchSize ? 'Archives the other variations in this batch' : null,
                            automationConfigured ? "Will also update the city's events page and queue this for posting" : null,
                        ].filter(Boolean).join(' - ') || undefined)
                        : retryableQueueFailure ? `${retryableProviderLabel} rejected this last time - click to try sending it again` : 'Already approved';
                    return (
                        <button
                            type="button"
                            onClick={() => patch({ status: 'approved' }, 'approve')}
                            disabled={saving || isDone}
                            title={title}
                            style={{
                                padding: '7px 14px', fontSize: '12px', borderRadius: '4px', fontWeight: 'bold',
                                cursor: isDone ? 'default' : 'pointer', border: 'none',
                                backgroundColor: isDone ? '#f5f5f5' : (retryableQueueFailure ? '#dc3545' : '#6f42c1'),
                                color: isDone ? '#aaa' : 'white',
                                boxShadow: isDone ? 'none' : '0 1px 3px rgba(0,0,0,0.15)',
                                opacity: isPending ? 0.7 : 1,
                            }}
                        >
                            {label}
                        </button>
                    );
                })()}
                {hasPendingScheduled && (
                    <button
                        type="button"
                        onClick={handleDispatchNow}
                        disabled={saving}
                        title="Fires the still-scheduled platform(s) right now instead of waiting for the next automatic check (runs hourly)"
                        style={{
                            padding: '7px 14px', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '4px',
                            backgroundColor: '#0d6efd', color: 'white', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                            opacity: pendingAction === 'dispatch-now' ? 0.7 : 1,
                        }}
                    >
                        {pendingAction === 'dispatch-now' ? 'Posting...' : 'Post now'}
                    </button>
                )}
                {(draft.status === 'approved' || draft.status === 'posted') && (
                    <button
                        type="button"
                        onClick={handleRevoke}
                        disabled={saving}
                        title={batchSize ? 'Unlocks the other variations in this batch so one of them can be approved instead' : 'Move back to draft'}
                        style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '4px', backgroundColor: '#ffc107', color: '#664d03', cursor: 'pointer', opacity: pendingAction === 'revoke' ? 0.7 : 1 }}
                    >
                        {pendingAction === 'revoke' ? 'Saving...' : 'Revoke approval'}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => patch({ status: 'posted' }, 'posted')}
                    disabled={saving || draft.status === 'posted'}
                    style={{
                        padding: '7px 14px', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '4px',
                        cursor: draft.status === 'posted' ? 'default' : 'pointer',
                        backgroundColor: draft.status === 'posted' ? '#f5f5f5' : '#28a745',
                        color: draft.status === 'posted' ? '#aaa' : 'white',
                        boxShadow: draft.status === 'posted' ? 'none' : '0 1px 3px rgba(0,0,0,0.15)',
                        opacity: pendingAction === 'posted' ? 0.7 : 1,
                    }}
                >
                    {pendingAction === 'posted' ? 'Saving...' : draft.status === 'posted' ? 'Posted ✓' : 'Mark posted'}
                </button>
                <button
                    type="button"
                    onClick={() => patch({ status: 'archived' }, 'archived')}
                    disabled={saving || draft.status === 'archived'}
                    style={{
                        padding: '7px 14px', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '4px',
                        cursor: draft.status === 'archived' ? 'default' : 'pointer',
                        backgroundColor: draft.status === 'archived' ? '#f5f5f5' : '#6c757d',
                        color: draft.status === 'archived' ? '#aaa' : 'white',
                        opacity: pendingAction === 'archived' ? 0.7 : 1,
                    }}
                >
                    {pendingAction === 'archived' ? 'Saving...' : draft.status === 'archived' ? 'Archived ✓' : 'Archive'}
                </button>
                <button type="button" onClick={() => onDelete(draft.id)} disabled={saving}
                    style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 'bold', border: 'none', borderRadius: '4px', backgroundColor: '#dc3545', color: 'white', cursor: 'pointer', marginLeft: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>
                    Delete
                </button>
            </div>

            {(draft.publications || []).length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '12px' }}>
                    {draft.publications.map((pub) => (
                        <a key={pub.id} href={pub.post_url} target="_blank" rel="noopener noreferrer" style={{
                            display: 'inline-block', marginRight: '8px', marginBottom: '4px', padding: '3px 8px',
                            borderRadius: '10px', backgroundColor: '#cce5ff', color: '#004085', textDecoration: 'none', fontWeight: 'bold'
                        }}>
                            View on {PLATFORM_LABELS[pub.platform] || pub.platform} ↗
                        </a>
                    ))}
                </div>
            )}

            {Object.keys(latestQueueByPlatform).length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '12px' }}>
                    {/* Only the latest attempt per platform - a retry (see the
                        approve/retry button above) means an earlier "failed"
                        badge for the same platform shouldn't keep sitting here
                        forever next to the newer, actually-current result. */}
                    {Object.values(latestQueueByPlatform).map((entry) => {
                        // A meta_direct 'queued' result IS live - the Graph API
                        // call already created a real post, unlike Buffer's
                        // 'queued', which only means "added to Buffer's own
                        // future-posting queue", not actually live yet (see
                        // app.py's _maybe_mark_posted_from_direct_dispatch).
                        const isLive = entry.status === 'queued' && entry.provider === 'meta_direct';
                        const badgeColors = isLive ? { bg: '#d4edda', text: '#155724' }
                            : entry.status === 'queued' ? { bg: '#e2d9f3', text: '#4b2e83' }
                            : entry.status === 'scheduled' ? { bg: '#d0e6ff', text: '#004085' }
                            : { bg: '#f8d7da', text: '#721c24' };
                        const label = isLive ? 'Live now via ' + (PROVIDER_LABELS[entry.provider] || entry.provider)
                            : entry.status === 'queued' ? `Queued to ${entry.provider}`
                            : entry.status === 'scheduled' ? 'Scheduled - will post automatically, or use Post now below'
                            : `${entry.provider} queue failed: ${entry.error_message}`;
                        return (
                            <span key={entry.platform} title={entry.error_message || ''} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px', marginRight: '8px', marginBottom: '4px',
                                padding: '3px 8px', borderRadius: '10px', fontWeight: 'bold',
                                backgroundColor: badgeColors.bg, color: badgeColors.text,
                            }}>
                                {label}
                                {entry.status === 'failed' && entry.error_message && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(entry.error_message);
                                            setCopiedErrorPlatform(entry.platform);
                                            setTimeout(() => setCopiedErrorPlatform(null), 2000);
                                        }}
                                        title="Copy the full error message"
                                        style={{
                                            border: 'none', background: 'rgba(0,0,0,0.08)', color: 'inherit', cursor: 'pointer',
                                            borderRadius: '8px', padding: '1px 6px', fontSize: '10px', fontWeight: 'bold',
                                        }}
                                    >
                                        {copiedErrorPlatform === entry.platform ? 'Copied!' : 'Copy'}
                                    </button>
                                )}
                            </span>
                        );
                    })}
                </div>
            )}

            {draft.status === 'posted' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
                    <select
                        value={linkPlatform}
                        onChange={(e) => setLinkPlatform(e.target.value)}
                        style={{ ...inputStyle, padding: '4px 6px', fontSize: '12px' }}
                    >
                        <option value="facebook">Facebook</option>
                        <option value="instagram">Instagram</option>
                        <option value="linkedin">LinkedIn</option>
                    </select>
                    <input
                        type="text"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Paste the live post's URL once you've posted it"
                        style={{ ...inputStyle, flex: '1 1 250px', padding: '4px 6px', fontSize: '12px' }}
                    />
                    <button
                        type="button"
                        onClick={handleAddPublication}
                        disabled={addingLink}
                        style={{ padding: '5px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: addingLink ? 'not-allowed' : 'pointer' }}
                    >
                        {addingLink ? 'Adding...' : 'Add link to live post'}
                    </button>
                    {linkError && <span style={{ color: 'red', fontSize: '12px' }}>{linkError}</span>}
                </div>
            )}
        </div>
    );
}

// Anything no longer 'draft' (approved/posted/archived) is done - it doesn't
// need active review, so it collapses into one compact table row instead of
// a full DraftRow card, keeping the page from growing without bound as
// drafts pile up over time. Each row expands individually (click it, or the
// chevron), and "Expand/Collapse all" flips every row at once. An expanded
// row renders the exact same DraftRow used for open drafts, so every action
// (edit, revoke, mark posted, add a live link, etc.) still works from here.
function ResolvedDraftsTable({ drafts, adminToken, onChange, onDelete, automationStatus, onGoToManualTools, allDraftsForBatches, pages }) {
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const allExpanded = drafts.length > 0 && drafts.every((d) => expandedIds.has(d.id));

    const toggleAll = () => {
        setExpandedIds(allExpanded ? new Set() : new Set(drafts.map((d) => d.id)));
    };
    const toggleOne = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Deliberately not an HTML <table> - a table's columns can't reflow on a
    // narrow screen without either a fixed min-width (forcing horizontal
    // scroll to even see the Status column) or hiding whole columns
    // outright. A flex row wraps its own pieces naturally instead, so
    // nothing needs to be hidden to stay usable on mobile.
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0', flexWrap: 'wrap', gap: '8px' }}>
                <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', margin: 0 }}>
                    Approved / posted / archived ({drafts.length})
                </p>
                <button
                    type="button"
                    onClick={toggleAll}
                    title="Expand or collapse every row below at once"
                    style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                >
                    {allExpanded ? 'Collapse all ▲' : 'Expand all ▼'}
                </button>
            </div>
            <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                {drafts.map((draft, i) => {
                    const isExpanded = expandedIds.has(draft.id);
                    const { objective: draftObjective, text: angleText } = parseAngle(draft.angle);
                    const batchSiblings = draft.batch_id ? allDraftsForBatches.filter((d) => d.batch_id === draft.batch_id) : [];
                    const batchChosen = batchSiblings.find((d) => d.id !== draft.id && (d.status === 'approved' || d.status === 'posted'));
                    return (
                        <div key={draft.id}>
                            <div
                                onClick={() => toggleOne(draft.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                                    padding: '10px 12px', cursor: 'pointer', fontSize: '12px',
                                    borderTop: i > 0 ? '1px solid #eee' : 'none',
                                    backgroundColor: isExpanded ? '#faf8ff' : 'white',
                                }}
                            >
                                <span style={{ color: '#999', flexShrink: 0 }}>{isExpanded ? '▾' : '▸'}</span>
                                <span style={{ fontWeight: 'bold', flexShrink: 0 }}>{draft.city}</span>
                                <span style={{
                                    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', flexShrink: 0,
                                    backgroundColor: STATUS_COLORS[draft.status]?.bg, color: STATUS_COLORS[draft.status]?.text,
                                }}>
                                    {draft.status}
                                </span>
                                <span style={{ color: '#666', flexShrink: 0 }}>{PLATFORM_LABELS[draft.platform] || draft.platform}</span>
                                <span style={{
                                    flex: '1 1 140px', minWidth: 0, color: '#888',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {draftObjective ? `${draftObjective} - ` : ''}{angleText}
                                </span>
                            </div>
                            {isExpanded && (
                                <div style={{ padding: '10px 12px', backgroundColor: '#faf8ff', borderTop: '1px solid #eee' }}>
                                    <DraftRow
                                        draft={draft}
                                        adminToken={adminToken}
                                        onChange={onChange}
                                        onDelete={onDelete}
                                        automationStatus={automationStatus}
                                        onGoToManualTools={onGoToManualTools}
                                        batchSize={batchSiblings.length > 1 ? batchSiblings.length : undefined}
                                        batchChosen={batchChosen || null}
                                        pages={pages}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Replaces the old one-input-per-objective absolute posts_per_week editor:
// the total is always the fixed MAX_POSTS_PER_WEEK_PER_PAGE cap (not
// admin-adjustable, same "fixed platform policy" reasoning as the cap
// itself) - the admin only sets a relative weight per objective (doesn't
// need to sum to anything in particular, e.g. 45/45/10 or 3/3/1 are
// equivalent). apportionByWeight turns that into the actual whole-number
// posts_per_week per objective, shown live as each input changes, always
// summing to exactly MAX_POSTS_PER_WEEK_PER_PAGE as long as at least one
// objective has a positive weight. Saving is one coordinated action across
// every active objective rather than per-row, since the values are now
// interdependent (changing one weight can change every other objective's
// computed count too).
function CadenceRatioEditor({ page, activeObjectives, cadences, adminToken, onCadenceChange, onCadenceRemoved }) {
    const [weights, setWeights] = useState(() => {
        const w = {};
        activeObjectives.forEach((o) => {
            const existing = cadences[o.id]?.posts_per_week;
            w[o.id] = String(existing != null ? existing : (CADENCE_DEFAULT_WEIGHTS[o.label] ?? CADENCE_DEFAULT_WEIGHT_FALLBACK));
        });
        return w;
    });
    const [saving, setSaving] = useState(false);

    const weightNums = {};
    activeObjectives.forEach((o) => { weightNums[o.id] = Math.max(0, parseFloat(weights[o.id]) || 0); });
    const computed = apportionByWeight(MAX_POSTS_PER_WEEK_PER_PAGE, weightNums);

    const dirty = activeObjectives.some((o) => {
        const existing = cadences[o.id]?.posts_per_week;
        const savedWeight = String(existing != null ? existing : (CADENCE_DEFAULT_WEIGHTS[o.label] ?? CADENCE_DEFAULT_WEIGHT_FALLBACK));
        return weights[o.id] !== savedWeight;
    });

    const save = async () => {
        setSaving(true);
        try {
            // Deletes and decreases before increases, so an in-progress save
            // never asks the server to accept a value that would - even
            // briefly, mid-save - push this page's stored total over the cap
            // (app.py's upsert_social_page_cadence checks against what's
            // currently stored for OTHER objectives, not the new incoming
            // configuration as a whole).
            const actions = activeObjectives.map((o) => ({
                objective: o,
                existing: cadences[o.id]?.posts_per_week || 0,
                next: computed[o.id] || 0,
            })).filter((a) => a.next !== a.existing);
            const removals = actions.filter((a) => a.next === 0);
            const decreases = actions.filter((a) => a.next > 0 && a.next < a.existing);
            const increases = actions.filter((a) => a.next > 0 && a.next >= a.existing);

            for (const a of [...removals, ...decreases, ...increases]) {
                if (a.next === 0) {
                    const response = await fetch(`/api/admin/socials/pages/${page.id}/cadences/${a.objective.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${adminToken}` },
                    });
                    if (!response.ok) throw new Error(`Failed to clear cadence for ${a.objective.label}`);
                    onCadenceRemoved(a.objective.id);
                } else {
                    const response = await fetch(`/api/admin/socials/pages/${page.id}/cadences/${a.objective.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                        body: JSON.stringify({ posts_per_week: a.next }),
                    });
                    if (!response.ok) throw new Error((await response.json()).error || `Failed to save cadence for ${a.objective.label}`);
                    onCadenceChange(await response.json());
                }
            }
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0' }}>
                Total is fixed at {MAX_POSTS_PER_WEEK_PER_PAGE}/week (2/day, see the "i" above) - set each
                objective's relative share below and the equivalent weekly count is computed automatically.
            </p>
            {activeObjectives.map((o) => {
                const count = computed[o.id] || 0;
                return (
                    <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: '1px solid #f2f2f2' }}>
                        <span style={{ flex: '1 1 160px', fontSize: '13px' }}>{o.label}</span>
                        <span style={{ fontSize: '11px', color: '#888' }}>share</span>
                        <input
                            type="number"
                            min="0"
                            value={weights[o.id]}
                            onChange={(e) => setWeights((prev) => ({ ...prev, [o.id]: e.target.value }))}
                            style={{ ...inputStyle, width: '60px', padding: '6px' }}
                        />
                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: count > 0 ? '#333' : '#dc3545', flex: '1 1 170px' }}>
                            ≈ {count}/week{count === 0 && weightNums[o.id] > 0 ? ' (too small a share - off)' : ''}
                        </span>
                        <span style={{ fontSize: '11px', color: '#888' }}>
                            suggested: {o.suggested_posts_per_week}/week
                        </span>
                    </div>
                );
            })}
            <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                style={{ marginTop: '8px', padding: '5px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: dirty ? '#6f42c1' : '#eee', color: dirty ? 'white' : '#999', cursor: dirty ? 'pointer' : 'default' }}
            >
                {saving ? 'Saving...' : 'Save'}
            </button>
        </div>
    );
}

// Best-effort emoji per Buffer `service` string, for the channel picker
// below - an unrecognized service just falls back to a generic link icon
// rather than being hidden, since Buffer supports more channel types than
// this project posts to.
const SERVICE_ICONS = {
    facebook: '📘', linkedin: '💼', instagram: '📷', twitter: '🐦', x: '🐦',
    mastodon: '🐘', tiktok: '🎵', pinterest: '📌', youtube: '▶️', threads: '🧵',
};

// Buffer channel ID entry: a manual text field (always available, with
// instructions for finding the ID by hand) plus an optional live picker
// fetched from Buffer's API - see social_scheduler.list_channels's
// docstring for why manual entry never goes away (the fetch can fail, or a
// channel might not show up here for some reason, and the admin still
// needs a way in either way).
function BufferChannelIdField({ value, onChange, adminToken, platformHint, onSelectChannel, disabled }) {
    const [expanded, setExpanded] = useState(false);
    const [channels, setChannels] = useState(null); // null = not fetched yet this session
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchChannels = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('/api/admin/socials/buffer-channels', {
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to fetch channels');
            setChannels(data.channels || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = () => {
        const next = !expanded;
        setExpanded(next);
        if (next && channels === null && !loading) fetchChannels();
    };

    const handlePick = (channel) => {
        onChange(channel.id);
        if (onSelectChannel) onSelectChannel(channel);
        setExpanded(false);
    };

    // Sorted, not filtered, so a channel doesn't just disappear because its
    // `service` string doesn't match what we expected for this platform.
    const sorted = (channels || []).slice().sort((a, b) => {
        const rank = (c) => (platformHint && (c.service || '').toLowerCase().includes(platformHint) ? 0 : 1);
        return rank(a) - rank(b);
    });

    return (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Buffer channel ID"
                    disabled={disabled}
                    style={{ ...inputStyle, flex: '1 1 220px' }}
                />
                <button
                    type="button"
                    onClick={handleToggle}
                    disabled={disabled}
                    style={{
                        padding: '6px 12px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap',
                        border: '1px solid #6f42c1', borderRadius: '4px', cursor: 'pointer',
                        backgroundColor: expanded ? '#6f42c1' : 'white', color: expanded ? 'white' : '#6f42c1',
                    }}
                >
                    {expanded ? 'Hide channels' : 'Choose from Buffer'}
                </button>
            </div>
            <p style={{ fontSize: '11px', color: '#888', margin: '4px 0 0 0', lineHeight: 1.5 }}>
                Manual: open{' '}
                <a href="https://publish.buffer.com/" target="_blank" rel="noopener noreferrer">publish.buffer.com</a>
                , click the channel, then copy the ID from its URL - e.g. for{' '}
                <code style={{ fontSize: '11px' }}>.../channels/<strong>6aa0531ccd8b9c702c2f010b</strong>/settings</code>,
                the channel ID is <strong>6aa0531ccd8b9c702c2f010b</strong>.
            </p>

            {expanded && (
                <div style={{ marginTop: '8px', border: '1px solid #eee', borderRadius: '6px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                    {loading ? (
                        <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Loading channels from Buffer...</p>
                    ) : error ? (
                        <p style={{ fontSize: '12px', color: '#856404', margin: 0 }}>
                            Couldn't fetch channels: {error}. Use the manual field above instead.
                        </p>
                    ) : channels && channels.length === 0 ? (
                        <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                            No channels found on this Buffer account - use the manual field above instead.
                        </p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontSize: '11px', color: '#888' }}>
                                    {platformHint ? `${PLATFORM_LABELS[platformHint] || platformHint} channels listed first` : 'Connected channels'}
                                </span>
                                <button
                                    type="button"
                                    onClick={fetchChannels}
                                    style={{ fontSize: '11px', border: 'none', background: 'none', color: '#6f42c1', cursor: 'pointer', padding: 0 }}
                                >
                                    &#8635; Refresh
                                </button>
                            </div>
                            {sorted.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => handlePick(c)}
                                    disabled={c.is_disconnected}
                                    title={c.is_disconnected ? 'Disconnected in Buffer - reconnect it there first' : undefined}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left',
                                        padding: '6px 8px', marginBottom: '4px', border: '1px solid #eee', borderRadius: '4px',
                                        backgroundColor: value === c.id ? '#ede7f6' : 'white',
                                        cursor: c.is_disconnected ? 'not-allowed' : 'pointer',
                                        opacity: c.is_disconnected ? 0.5 : 1, fontSize: '13px',
                                    }}
                                >
                                    <span>{SERVICE_ICONS[(c.service || '').toLowerCase()] || '🔗'}</span>
                                    <span style={{ flex: 1 }}>{c.name || '(unnamed channel)'}</span>
                                    <span style={{ fontSize: '11px', color: '#999' }}>
                                        {c.service}{c.is_disconnected ? ' - disconnected' : ''}
                                    </span>
                                </button>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// Two-way "Posting via" chip toggle - used instead of a plain <select> for
// Facebook pages (the only platform with a real provider choice) since a
// side-by-side pair of buttons reads faster than a dropdown for a binary
// decision an admin needs to notice, not just technically be able to find.
function ProviderToggle({ value, onChange, disabled }) {
    const chipStyle = (active, color) => ({
        padding: '4px 10px', fontSize: '11px', fontWeight: 'bold', borderRadius: '10px', cursor: disabled ? 'default' : 'pointer',
        border: active ? `1px solid ${color}` : '1px solid #ddd',
        backgroundColor: active ? color : 'white',
        color: active ? 'white' : '#999',
    });
    return (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666' }}>Posting via</span>
            <button type="button" disabled={disabled} onClick={() => onChange('meta_direct')} style={chipStyle(value === 'meta_direct', '#1e7e34')}>
                Direct Meta
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange('buffer')} style={chipStyle(value === 'buffer', '#6f42c1')}>
                Buffer
            </button>
        </div>
    );
}

// Who a page is for (see SocialPage's page_type/age_group docstring) - a
// city can have more than one page on the same platform now, e.g. a
// general Social page and a separate Tech page, each getting different AI
// content (jobs/generate_scheduled_social_posts.py). Same chip pattern as
// ProviderToggle above.
function PageTypeToggle({ value, onChange, disabled }) {
    const chipStyle = (active, color) => ({
        padding: '4px 10px', fontSize: '11px', fontWeight: 'bold', borderRadius: '10px', cursor: disabled ? 'default' : 'pointer',
        border: active ? `1px solid ${color}` : '1px solid #ddd',
        backgroundColor: active ? color : 'white',
        color: active ? 'white' : '#999',
    });
    return (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666' }}>Page type</span>
            <button type="button" disabled={disabled} onClick={() => onChange('social')} style={chipStyle(value === 'social', '#6f42c1')}>
                Social
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange('tech')} style={chipStyle(value === 'tech', '#0d6efd')}>
                Tech
            </button>
        </div>
    );
}

function AgeGroupToggle({ value, onChange, disabled }) {
    const chipStyle = (active) => ({
        padding: '4px 10px', fontSize: '11px', fontWeight: 'bold', borderRadius: '10px', cursor: disabled ? 'default' : 'pointer',
        border: active ? '1px solid #6f42c1' : '1px solid #ddd',
        backgroundColor: active ? '#6f42c1' : 'white',
        color: active ? 'white' : '#999',
    });
    return (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666' }}>Age group</span>
            <button type="button" disabled={disabled} onClick={() => onChange('20s_30s')} style={chipStyle(value === '20s_30s')}>
                20s &amp; 30s
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange('30s_40s')} style={chipStyle(value === '30s_40s')}>
                30s &amp; 40s
            </button>
        </div>
    );
}

const PAGE_TYPE_HELP_TEXT = (
    "Tech pages still mostly surface local events, just framed for tech professionals - but some posts will be " +
    "general tech-industry observations for this city instead of an events roundup, since that page's audience " +
    "is defined by industry, not age. Social pages are the general audience, split by age group so a 20s&30s " +
    "page and a 30s&40s page for the same city can have a slightly different tone."
);

// The Buffer-channel-ID and Meta-page-ID inputs for one page, shared by
// PageCard (editing) and the Add a page form (creating) - the point is to
// make it obvious which ID actually matters right now instead of showing
// two same-weight fields side by side. Whichever provider is active gets a
// prominent, highlighted field; the other platform's ID (only relevant if
// this page ever switches provider) starts collapsed behind a "+ also
// save..." link unless it already has a value, so existing data is never
// hidden. LinkedIn/Instagram have no real choice of provider (see
// SocialPage's docstring), so for those only the one relevant field shows
// at all - there's nothing to fall back to.
function ProviderIdFields({ platform, provider, bufferValue, onBufferChange, metaValue, onMetaChange, adminToken, disabled, onSelectChannel }) {
    const locked = platform === 'linkedin' ? 'buffer' : platform === 'instagram' ? 'meta_direct' : null;
    const active = locked || provider;
    const metaLabel = platform === 'instagram' ? 'Instagram Business Account ID' : 'Meta Page ID';
    const activeLabel = active === 'buffer' ? 'Buffer Channel ID' : metaLabel;
    const fallbackLabel = active === 'buffer' ? metaLabel : 'Buffer Channel ID';
    const fallbackValue = active === 'buffer' ? metaValue : bufferValue;
    const [showFallback, setShowFallback] = useState(!locked && !!fallbackValue);

    const metaField = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
                type="text"
                value={metaValue}
                onChange={(e) => onMetaChange(e.target.value)}
                placeholder={metaLabel}
                style={{ ...inputStyle, flex: '1 1 auto' }}
                disabled={disabled}
            />
            <InfoTooltip text={META_PAGE_ID_HELP_TEXT} />
        </div>
    );
    const bufferField = (
        <BufferChannelIdField
            value={bufferValue}
            onChange={onBufferChange}
            adminToken={adminToken}
            platformHint={platform}
            disabled={disabled}
            onSelectChannel={onSelectChannel}
        />
    );

    return (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ border: '2px solid #28a745', borderRadius: '6px', padding: '10px', backgroundColor: '#f7fdf9', marginBottom: '6px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: 'bold', color: '#1e7e34' }}>
                    ✓ {activeLabel} - used for posting
                </p>
                {active === 'buffer' ? bufferField : metaField}
            </div>
            {!locked && (
                showFallback ? (
                    <div style={{ border: '1px solid #eee', borderRadius: '6px', padding: '10px', backgroundColor: '#fafafa' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                                {fallbackLabel} <span style={{ fontStyle: 'italic' }}>(fallback, not currently used)</span>
                            </p>
                            <button
                                type="button"
                                onClick={() => { setShowFallback(false); (active === 'buffer' ? onMetaChange : onBufferChange)(''); }}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', color: '#999' }}
                            >
                                Remove
                            </button>
                        </div>
                        {active === 'buffer' ? metaField : bufferField}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowFallback(true)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', color: '#6f42c1', padding: 0 }}
                    >
                        + Also save a {fallbackLabel}, so you can switch to {active === 'buffer' ? 'direct Meta' : 'Buffer'} instantly later
                    </button>
                )
            )}
        </div>
    );
}

// One Facebook/LinkedIn page - its Buffer channel ID plus its per-objective
// posting cadence, which jobs/generate_scheduled_social_posts.py reads to
// decide what's due for automatic draft generation. See SocialPage's
// docstring.
function PageCard({ page, objectives, adminToken, onChange, onDelete, hasDraftsForCity, onGoToReview, onHasCadenceChange, onCadenceChange, hasLivePage }) {
    const [displayName, setDisplayName] = useState(page.display_name);
    const [bufferProfileId, setBufferProfileId] = useState(page.buffer_profile_id || '');
    const [metaPageId, setMetaPageId] = useState(page.meta_page_id || '');
    const [saving, setSaving] = useState(false);
    const [cadences, setCadences] = useState({});
    const [cadencesLoading, setCadencesLoading] = useState(true);
    const [bioUrlCopied, setBioUrlCopied] = useState(false);
    // Shown right after switching an existing page TO direct Meta posting -
    // same "not just the ID" gap as a newly-added page, see the Add a page
    // form's justAddedPage banner.
    const [showMetaSetupHint, setShowMetaSetupHint] = useState(false);
    // LinkedIn only ever posts via Buffer, Instagram only ever via direct
    // Meta (see SocialPage's docstring) - only Facebook pages have a real
    // choice of provider.
    const providerLocked = page.platform === 'linkedin' ? 'buffer' : page.platform === 'instagram' ? 'meta_direct' : null;
    const dirty = displayName !== page.display_name || bufferProfileId !== (page.buffer_profile_id || '')
        || metaPageId !== (page.meta_page_id || '');
    const activeObjectives = objectives.filter((o) => o.is_active);
    const totalPostsPerWeek = activeObjectives.reduce((sum, o) => sum + (cadences[o.id]?.posts_per_week || 0), 0);

    const handleCopyBioUrl = () => {
        navigator.clipboard.writeText(page.bio_url || '');
        setBioUrlCopied(true);
        setTimeout(() => setBioUrlCopied(false), 2000);
    };

    useEffect(() => {
        fetch(`/api/admin/socials/pages/${page.id}/cadences`, { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { cadences: [] }))
            .then((data) => {
                const map = {};
                (data.cadences || []).forEach((c) => { map[c.objective_id] = c; });
                setCadences(map);
            })
            .catch(() => {})
            .finally(() => setCadencesLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page.id]);

    const patch = async (fields) => {
        setSaving(true);
        try {
            const response = await fetch(`/api/admin/socials/pages/${page.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify(fields)
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to update page');
            onChange(await response.json());
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCadenceChange = (updated) => {
        setCadences((prev) => ({ ...prev, [updated.objective_id]: updated }));
        onHasCadenceChange(page.id);
        if (onCadenceChange) onCadenceChange();
    };

    // A weight rounding to 0 in CadenceRatioEditor means that objective's
    // cadence gets deleted server-side (posts_per_week can't be 0 - see
    // upsert_social_page_cadence) - drop it from local state to match.
    const handleCadenceRemoved = (objectiveId) => {
        setCadences((prev) => {
            const next = { ...prev };
            delete next[objectiveId];
            return next;
        });
        if (onCadenceChange) onCadenceChange();
    };

    return (
        <div style={{ border: '1px solid #eee', borderRadius: '6px', padding: '15px', marginBottom: '12px', opacity: page.is_active ? 1 : 0.55 }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                <strong>{page.city}</strong>
                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#d0e6ff', color: '#004085' }}>
                    {PLATFORM_LABELS[page.platform]}
                </span>
                {providerLocked ? (
                    <span
                        title={providerLocked === 'buffer' ? "LinkedIn only ever posts via Buffer" : "Instagram only ever posts via direct Meta"}
                        style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
                            backgroundColor: providerLocked === 'buffer' ? '#e2d9f3' : '#d4edda',
                            color: providerLocked === 'buffer' ? '#3d2a6b' : '#155724',
                        }}
                    >
                        Posting via {providerLocked === 'buffer' ? 'Buffer' : 'Direct Meta'}
                    </span>
                ) : (
                    <ProviderToggle
                        value={page.provider}
                        disabled={saving}
                        onChange={(newProvider) => {
                            patch({ provider: newProvider });
                            setShowMetaSetupHint(newProvider === 'meta_direct' && page.provider !== 'meta_direct');
                        }}
                    />
                )}
                <PageTypeToggle
                    value={page.page_type}
                    disabled={saving}
                    onChange={(newType) => {
                        // A social page needs an age_group, a tech page must
                        // not have one - see _validate_page_type_age_group.
                        patch({ page_type: newType, age_group: newType === 'social' ? '20s_30s' : null });
                    }}
                />
                {page.page_type === 'social' && (
                    <AgeGroupToggle
                        value={page.age_group}
                        disabled={saving}
                        onChange={(newAgeGroup) => patch({ age_group: newAgeGroup })}
                    />
                )}
                <InfoTooltip text={PAGE_TYPE_HELP_TEXT} />
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={page.is_active} onChange={(e) => patch({ is_active: e.target.checked })} disabled={saving} />
                    Active
                </label>
                <button
                    type="button"
                    onClick={() => onDelete(page.id)}
                    disabled={saving}
                    style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #dc3545', borderRadius: '4px', backgroundColor: 'white', color: '#dc3545', cursor: 'pointer', marginLeft: 'auto' }}
                >
                    Delete
                </button>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Display name, e.g. 'OkGather London'"
                    style={{ ...inputStyle, flex: '1 1 220px' }}
                />
                <button
                    type="button"
                    onClick={() => patch({ display_name: displayName, buffer_profile_id: bufferProfileId, meta_page_id: metaPageId })}
                    disabled={saving || !dirty}
                    style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: dirty ? '#6f42c1' : '#eee', color: dirty ? 'white' : '#999', cursor: dirty ? 'pointer' : 'default' }}
                >
                    Save
                </button>
            </div>
            <ProviderIdFields
                platform={page.platform}
                provider={page.provider}
                bufferValue={bufferProfileId}
                onBufferChange={setBufferProfileId}
                metaValue={metaPageId}
                onMetaChange={setMetaPageId}
                adminToken={adminToken}
                disabled={saving}
            />
            {showMetaSetupHint && (
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px',
                    marginBottom: '10px', padding: '10px 12px', borderRadius: '6px',
                    border: '2px solid #ffc107', backgroundColor: '#fff8e1', fontSize: '13px', color: '#333',
                }}>
                    <span>
                        Switched to direct Meta - this Page still needs assigning to "DirectPosting" as an asset in{' '}
                        <ExternalLink href={META_SYSTEM_USER_URL}>Meta System Users settings</ExternalLink> (with the "Content"
                        permission) before it can actually post, the Meta Page ID alone isn't enough. If the Page doesn't show up
                        there, it was likely created outside the Business Portfolio - add it first via{' '}
                        <ExternalLink href={META_BUSINESS_SETTINGS_URL}>Business Settings</ExternalLink> &gt; Accounts &gt; Pages
                        &gt; Add &gt; Add a Page. No Heroku config changes needed - the existing System User token already works
                        for any Page assigned to it, current or future.
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowMetaSetupHint(false)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#856404', flexShrink: 0 }}
                    >
                        Dismiss ✕
                    </button>
                </div>
            )}
            {page.bio_url && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                        Point this page's bio/website link at:
                    </span>
                    <code style={{ fontSize: '12px', backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '4px' }}>{page.bio_url}</code>
                    <button
                        type="button"
                        onClick={handleCopyBioUrl}
                        style={{ padding: '3px 8px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                    >
                        {bioUrlCopied ? 'Copied!' : 'Copy'}
                    </button>
                </div>
            )}
            {page.bio_url && (
                hasLivePage ? (
                    <p style={{ fontSize: '11px', color: '#155724', margin: '0 0 10px 0' }}>
                        ✓ Live - {page.city} already has a published events page at this link.
                    </p>
                ) : (
                    <p style={{ fontSize: '11px', color: '#856404', margin: '0 0 10px 0' }}>
                        Not live yet - this link 404s until {page.city} has a published events page (Cities tab, or
                        approving a draft for this city both publish/update one automatically).
                    </p>
                )
            )}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', margin: 0 }}>
                    Posting cadence (automatic draft generation) - {totalPostsPerWeek > 0
                        ? `${MAX_POSTS_PER_WEEK_PER_PAGE}/week (2/day) split by ratio below`
                        : `not set up yet - will be ${MAX_POSTS_PER_WEEK_PER_PAGE}/week (2/day) once saved below`}
                </p>
                <InfoTooltip text={CADENCE_CAP_HELP_TEXT} />
            </div>
            {cadencesLoading ? (
                <p style={{ fontSize: '13px', color: '#666' }}>Loading cadence...</p>
            ) : activeObjectives.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#666' }}>No active objectives to schedule yet - add one above.</p>
            ) : (
                <CadenceRatioEditor
                    page={page}
                    activeObjectives={activeObjectives}
                    cadences={cadences}
                    adminToken={adminToken}
                    onCadenceChange={handleCadenceChange}
                    onCadenceRemoved={handleCadenceRemoved}
                />
            )}
            {!cadencesLoading && activeObjectives.some((o) => cadences[o.id]?.posts_per_week) && !hasDraftsForCity && (
                <NextStepHint action={{ label: 'Go to Review & Post', onClick: () => onGoToReview(page.city, { expandManualTools: true }) }}>
                    drafts generate automatically on that cadence (usually within a day), or generate one now instead of waiting.
                </NextStepHint>
            )}
        </div>
    );
}

// One of OkGather's active operating cities, with its optional audience/
// theme note - see SocialCityProfile's docstring. Layered into both event
// discovery and caption drafting for that city.
function CityProfileCard({ profile, pages, adminToken, onChange, onGoToPages, onGoToReview, livePage, pendingDiscoveryJob, onDiscoveryJobStarted }) {
    const [description, setDescription] = useState(profile.audience_description || '');
    const [saving, setSaving] = useState(false);
    const [togglingGeneration, setTogglingGeneration] = useState(false);
    const [justEnabled, setJustEnabled] = useState(false);
    const dirty = description !== (profile.audience_description || '');
    const cityPages = pages.filter((p) => p.city === profile.city);
    const pendingEvents = pendingDiscoveryJob?.status === 'done' ? (pendingDiscoveryJob.events || []) : [];

    const save = async () => {
        setSaving(true);
        try {
            const response = await fetch('/api/admin/socials/cities', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify({ city: profile.city, audience_description: description })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to save city');
            onChange(await response.json());
        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Toggling fires its own PUT immediately (same "Active" checkbox pattern
    // as ObjectiveCard/PageCard) - deliberately sends the last-SAVED
    // audience_description (profile.audience_description), not whatever's
    // currently typed in the textarea above, so flipping this switch can
    // never accidentally persist a half-written note.
    const handleToggleGeneration = async (checked) => {
        setTogglingGeneration(true);
        try {
            const response = await fetch('/api/admin/socials/cities', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify({
                    city: profile.city,
                    audience_description: profile.audience_description || '',
                    generation_enabled: checked,
                })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to update city');
            const updated = await response.json();
            if (updated.auto_discovery_job_id) {
                setJustEnabled(true);
                onDiscoveryJobStarted();
            }
            onChange(updated);
        } catch (err) {
            alert(err.message);
        } finally {
            setTogglingGeneration(false);
        }
    };

    return (
        <div style={{ borderTop: '1px solid #eee', padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '13px' }}>{profile.city}</p>
                <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
                    backgroundColor: profile.generation_enabled ? '#d4edda' : '#e2e3e5',
                    color: profile.generation_enabled ? '#155724' : '#41464b'
                }}>
                    Auto-generation {profile.generation_enabled ? 'ON' : 'OFF'}
                </span>
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginLeft: 'auto' }}>
                    <input
                        type="checkbox"
                        checked={!!profile.generation_enabled}
                        onChange={(e) => handleToggleGeneration(e.target.checked)}
                        disabled={togglingGeneration}
                    />
                    Turn on auto-generation for this city
                </label>
            </div>
            {profile.generation_enabled && (
                <div style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#155724', backgroundColor: '#d4edda', borderRadius: '4px', padding: '8px 10px' }}>
                    {justEnabled && (
                        <p style={{ margin: '0 0 6px 0' }}>
                            Finding events for this city now - check back in a minute or two (refresh this page) to review and publish them.
                        </p>
                    )}
                    <p style={{ margin: (cityPages.length === 0 || (!livePage && pendingEvents.length > 0)) ? '0 0 6px 0' : 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {livePage ? (
                            <a
                                href={`${window.location.origin}/local-events/${livePage.city_slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontWeight: 'bold' }}
                            >
                                Open events page ↗
                            </a>
                        ) : pendingDiscoveryJob?.status === 'failed' ? (
                            <span>Automatic event discovery failed - use "Find Events" in Review &amp; Post's manual tools.</span>
                        ) : (
                            <span>Events page not live yet - events are found automatically within a minute or two of enabling, then wait for your review before publishing (nothing publishes unreviewed).</span>
                        )}
                    </p>
                    {!livePage && pendingEvents.length > 0 && (
                        <>
                            <p style={{ margin: '0 0 6px 0' }}>
                                <strong>{pendingEvents.length} event{pendingEvents.length === 1 ? '' : 's'} found</strong> and waiting for your review before this city's events page can go live.
                            </p>
                            <button
                                type="button"
                                onClick={() => onGoToReview(profile.city, { expandManualTools: true, pendingEvents })}
                                style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid #155724', borderRadius: '4px', backgroundColor: 'white', color: '#155724', cursor: 'pointer', fontWeight: 'bold', marginBottom: '6px' }}
                            >
                                Review &amp; Publish &rarr;
                            </button>
                        </>
                    )}
                    {cityPages.length === 0 && (
                        <>
                            <p style={{ margin: '0 0 6px 0' }}>
                                <strong>Next:</strong> add a Facebook or LinkedIn posting page for this city so drafts have somewhere to post to.
                            </p>
                            <button
                                type="button"
                                onClick={() => onGoToPages(profile.city)}
                                style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid #155724', borderRadius: '4px', backgroundColor: 'white', color: '#155724', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                Go to Pages &amp; Cadence &rarr;
                            </button>
                        </>
                    )}
                </div>
            )}
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                Pages: {cityPages.length === 0 ? (
                    "none configured yet - you'll pick Social (with an age group) or Tech per page there"
                ) : (
                    cityPages.map((p) => (
                        `${PLATFORM_LABELS[p.platform] || p.platform} (${p.page_type === 'tech' ? 'Tech' : `Social, ${p.age_group === '30s_40s' ? '30s&40s' : '20s&30s'}`})`
                    )).join(', ')
                )}
                {' '}(manage in the "Pages & Cadence" tab)
            </p>
            <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="City context note (optional), e.g. 'Large student population and an active outdoor/hiking scene' - not for age targeting, that's set per page below"
                rows={2}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px', fontFamily: 'inherit', fontSize: '13px' }}
            />
            <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                style={{ padding: '5px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: dirty ? '#6f42c1' : '#eee', color: dirty ? 'white' : '#999', cursor: dirty ? 'pointer' : 'default' }}
            >
                Save
            </button>
        </div>
    );
}

// Add-one-data-point form for the Timeline tab's manual follower/engagement
// tracking (see SocialGrowthSnapshot's docstring for why this has to be
// manual - neither Buffer's API nor this app has an automatic source for
// either metric). Deliberately minimal - city/platform/date plus whichever
// of followers/engagement the admin actually has to hand this time.
function GrowthSnapshotForm({ cities, citiesLoading, onSubmit }) {
    const [snapCity, setSnapCity] = useState('');
    const [snapPlatform, setSnapPlatform] = useState('facebook');
    const [snapDate, setSnapDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [snapFollowers, setSnapFollowers] = useState('');
    const [snapEngagement, setSnapEngagement] = useState('');
    const [snapNotes, setSnapNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const handleSubmit = async () => {
        setFormError('');
        if (!snapCity.trim() || !snapDate) {
            setFormError('City and date are required');
            return;
        }
        if (!snapFollowers && !snapEngagement) {
            setFormError('Enter at least a follower count or an engagement rate');
            return;
        }
        setSaving(true);
        try {
            await onSubmit({
                city: snapCity.trim(),
                platform: snapPlatform,
                recorded_date: snapDate,
                followers: snapFollowers ? parseInt(snapFollowers, 10) : null,
                engagement_rate_pct: snapEngagement ? parseFloat(snapEngagement) : null,
                notes: snapNotes.trim() || null,
            });
            setSnapFollowers('');
            setSnapEngagement('');
            setSnapNotes('');
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ border: '1px dashed #ccc', borderRadius: '6px', padding: '15px' }}>
            <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                Add a data point (from Meta Business Suite / LinkedIn analytics / Buffer)
            </p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <select value={snapCity} onChange={(e) => setSnapCity(e.target.value)} style={{ ...inputStyle, flex: '1 1 160px' }} disabled={citiesLoading}>
                    <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                    {cities.map((c) => (<option key={c.city} value={c.city}>{c.city}</option>))}
                </select>
                <select value={snapPlatform} onChange={(e) => setSnapPlatform(e.target.value)} style={{ ...inputStyle, flex: '0 0 130px' }}>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="linkedin">LinkedIn</option>
                </select>
                <input type="date" value={snapDate} onChange={(e) => setSnapDate(e.target.value)} style={{ ...inputStyle, flex: '0 0 150px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input
                    type="number" min="0" value={snapFollowers} onChange={(e) => setSnapFollowers(e.target.value)}
                    placeholder="Follower count" style={{ ...inputStyle, flex: '1 1 140px' }}
                />
                <input
                    type="number" min="0" max="100" step="0.1" value={snapEngagement} onChange={(e) => setSnapEngagement(e.target.value)}
                    placeholder="Engagement rate %" style={{ ...inputStyle, flex: '1 1 140px' }}
                />
                <input
                    type="text" value={snapNotes} onChange={(e) => setSnapNotes(e.target.value)}
                    placeholder="Notes (optional)" style={{ ...inputStyle, flex: '2 1 200px' }}
                />
            </div>
            {formError && <div style={{ color: 'red', fontSize: '12px', marginBottom: '8px' }}>{formError}</div>}
            <button
                type="button" onClick={handleSubmit} disabled={saving}
                style={{ padding: '6px 14px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
            >
                {saving ? 'Saving...' : 'Add data point'}
            </button>
        </div>
    );
}

function AdminSocials({ adminToken, tabName }) {
    const [aiAvailable, setAiAvailable] = useState(null); // null = still checking

    useEffect(() => {
        fetch('/api/admin/socials/ai-status', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { available: false }))
            .then((data) => setAiAvailable(!!data.available))
            .catch(() => setAiAvailable(false));
    }, [adminToken]);

    // Whether Buffer is configured (and pages exist) - once buffer_configured
    // is true, approving a draft ALWAYS auto-queues it to Buffer (see
    // update_social_post_draft in app.py). There's no separate admin
    // toggle for that - see social_scheduler.py.
    const [automationStatus, setAutomationStatus] = useState(null);

    useEffect(() => {
        fetch('/api/admin/socials/automation-status', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { buffer_configured: false, pages_configured: false }))
            .then(setAutomationStatus)
            .catch(() => setAutomationStatus({ buffer_configured: false, pages_configured: false }));
    }, [adminToken]);

    // AI prompt harness - the admin-editable pieces of what gets sent to the
    // LLM (see social_post_ai.py). brand_voice/writing_rules are shared by
    // every generation; objectives are picked per-batch in Review & Post's
    // manual "Draft posts" tool.
    const [brandVoiceDraft, setBrandVoiceDraft] = useState('');
    const [writingRulesDraft, setWritingRulesDraft] = useState('');
    const [savingPromptSettings, setSavingPromptSettings] = useState(false);
    const [promptSettingsLoaded, setPromptSettingsLoaded] = useState(false);

    // Page-type sub-prompts (one for every 'tech' page, one for every
    // 'social' page, regardless of age_group - see SocialPageTypePrompts'
    // docstring) - layered on top of brand_voice/writing_rules above, kept
    // as a separate save action/API since they're a different shape
    // (per-page-type, no history/revert) rather than crowding the
    // brand-voice save into doing two unrelated things at once.
    const [techNoteDraft, setTechNoteDraft] = useState('');
    const [socialNoteDraft, setSocialNoteDraft] = useState('');
    const [savingPageTypePrompts, setSavingPageTypePrompts] = useState(false);
    const [pageTypePromptsLoaded, setPageTypePromptsLoaded] = useState(false);

    useEffect(() => {
        fetch('/api/admin/socials/page-type-prompts', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data) return;
                setTechNoteDraft(data.tech_note || '');
                setSocialNoteDraft(data.social_note || '');
            })
            .catch(() => {})
            .finally(() => setPageTypePromptsLoaded(true));
    }, [adminToken]);

    const handleSavePageTypePrompts = async () => {
        setSavingPageTypePrompts(true);
        try {
            const response = await fetch('/api/admin/socials/page-type-prompts', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify({ tech_note: techNoteDraft, social_note: socialNoteDraft })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to save');
            const saved = await response.json();
            setTechNoteDraft(saved.tech_note);
            setSocialNoteDraft(saved.social_note);
        } catch (err) {
            alert(err.message);
        } finally {
            setSavingPageTypePrompts(false);
        }
    };

    const [objectives, setObjectives] = useState([]);
    const [objectivesLoading, setObjectivesLoading] = useState(true);
    const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
    const [newObjLabel, setNewObjLabel] = useState('');
    const [newObjDescription, setNewObjDescription] = useState('');
    const [newObjStrategy, setNewObjStrategy] = useState('');
    const [addingObjective, setAddingObjective] = useState(false);

    useEffect(() => {
        fetch('/api/admin/socials/prompt-settings', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data) return;
                setBrandVoiceDraft(data.brand_voice || '');
                setWritingRulesDraft(data.writing_rules || '');
            })
            .catch(() => {})
            .finally(() => setPromptSettingsLoaded(true));
    }, [adminToken]);

    const loadObjectives = () => {
        setObjectivesLoading(true);
        fetch('/api/admin/socials/objectives', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { objectives: [] }))
            .then((data) => setObjectives(data.objectives || []))
            .catch(() => {})
            .finally(() => setObjectivesLoading(false));
    };

    useEffect(() => {
        loadObjectives();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSavePromptSettings = async () => {
        if (!brandVoiceDraft.trim() || !writingRulesDraft.trim()) {
            alert('Brand voice and writing rules cannot be empty');
            return;
        }
        setSavingPromptSettings(true);
        try {
            const response = await fetch('/api/admin/socials/prompt-settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify({ brand_voice: brandVoiceDraft, writing_rules: writingRulesDraft })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to save');
            const saved = await response.json();
            setBrandVoiceDraft(saved.brand_voice);
            setWritingRulesDraft(saved.writing_rules);
            loadPromptHistory();
        } catch (err) {
            alert(err.message);
        } finally {
            setSavingPromptSettings(false);
        }
    };

    // Every save (including reset/restore below) snapshots the row's
    // PREVIOUS values into history server-side first (see
    // SocialPromptSettingsRepository.update) - nothing here is a dead end,
    // whatever's live right now is always one "Restore" click away from
    // coming back even after a reset.
    const [promptHistory, setPromptHistory] = useState([]);
    const [promptHistoryLoading, setPromptHistoryLoading] = useState(true);
    const [showPromptHistory, setShowPromptHistory] = useState(false);
    const [resettingPromptSettings, setResettingPromptSettings] = useState(false);
    const [restoringHistoryId, setRestoringHistoryId] = useState(null);

    const loadPromptHistory = () => {
        setPromptHistoryLoading(true);
        fetch('/api/admin/socials/prompt-settings/history', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { history: [] }))
            .then((data) => setPromptHistory(data.history || []))
            .catch(() => {})
            .finally(() => setPromptHistoryLoading(false));
    };

    useEffect(() => {
        loadPromptHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminToken]);

    const handleResetPromptSettings = async () => {
        if (!window.confirm("Reset brand voice and writing rules to OkGather's built-in defaults? The current wording is saved to history first, so it's not lost.")) return;
        setResettingPromptSettings(true);
        try {
            const response = await fetch('/api/admin/socials/prompt-settings/reset', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to reset');
            const saved = await response.json();
            setBrandVoiceDraft(saved.brand_voice);
            setWritingRulesDraft(saved.writing_rules);
            loadPromptHistory();
        } catch (err) {
            alert(err.message);
        } finally {
            setResettingPromptSettings(false);
        }
    };

    const handleRestorePromptVersion = async (historyId) => {
        if (!window.confirm('Make this past version live again? The current wording is saved to history first, so it\'s not lost.')) return;
        setRestoringHistoryId(historyId);
        try {
            const response = await fetch(`/api/admin/socials/prompt-settings/history/${historyId}/restore`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to restore');
            const saved = await response.json();
            setBrandVoiceDraft(saved.brand_voice);
            setWritingRulesDraft(saved.writing_rules);
            loadPromptHistory();
        } catch (err) {
            alert(err.message);
        } finally {
            setRestoringHistoryId(null);
        }
    };

    const handleObjectiveChange = (updated) => {
        setObjectives((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    };

    const handleDeleteObjective = async (id) => {
        if (!window.confirm('Delete this objective? Past drafts generated with it keep their label either way.')) return;
        try {
            const response = await fetch(`/api/admin/socials/objectives/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) throw new Error('Failed to delete objective');
            setObjectives((prev) => prev.filter((o) => o.id !== id));
            if (String(selectedObjectiveId) === String(id)) setSelectedObjectiveId('');
        } catch (err) {
            alert(err.message);
        }
    };

    const handleAddObjective = async () => {
        if (!newObjLabel.trim() || !newObjStrategy.trim()) {
            alert('Label and strategy instructions are both required');
            return;
        }
        setAddingObjective(true);
        try {
            const response = await fetch('/api/admin/socials/objectives', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify({
                    label: newObjLabel.trim(),
                    description: newObjDescription.trim(),
                    strategy_instructions: newObjStrategy.trim(),
                    sort_order: objectives.length,
                })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to add objective');
            const created = await response.json();
            setObjectives((prev) => [...prev, created]);
            setNewObjLabel('');
            setNewObjDescription('');
            setNewObjStrategy('');
        } catch (err) {
            alert(err.message);
        } finally {
            setAddingObjective(false);
        }
    };

    // OkGather's active operating cities (from locations_lookup), each with
    // an optional audience/theme note - see SocialCityProfile's docstring.
    // Backs every city picker below (discovery, manual template, adding a
    // page) so a city can't drift out of sync via a typo, plus its own
    // "Cities we operate in" section for editing the note itself.
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(true);

    const loadCities = () => {
        setCitiesLoading(true);
        fetch('/api/admin/socials/cities', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { cities: [] }))
            .then((data) => setCities(data.cities || []))
            .catch(() => {})
            .finally(() => setCitiesLoading(false));
    };

    useEffect(() => {
        loadCities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminToken]);

    const handleCityProfileChange = (updated) => {
        setCities((prev) => prev.map((c) => (c.city === updated.city ? updated : c)));
    };

    // Facebook/LinkedIn pages (one per city per platform) + their per-
    // objective posting cadence - see SocialPage's docstring and
    // jobs/generate_scheduled_social_posts.py, which reads these to decide
    // what's due for automatic draft generation.
    const [pages, setPages] = useState([]);
    const [pagesLoading, setPagesLoading] = useState(true);
    const [newPageCity, setNewPageCity] = useState('');
    const [newPagePlatform, setNewPagePlatform] = useState('facebook');
    const [newPageProvider, setNewPageProvider] = useState('meta_direct');
    const [newPageBufferId, setNewPageBufferId] = useState('');
    const [newPageMetaId, setNewPageMetaId] = useState('');
    const [newPageDisplayName, setNewPageDisplayName] = useState('');
    // Who this page is for - see SocialPage's page_type/age_group docstring.
    // A city can have more than one page on the same platform now (e.g. a
    // Social page and a separate Tech page), each getting different AI
    // content (jobs/generate_scheduled_social_posts.py).
    const [newPageType, setNewPageType] = useState('social');
    const [newPageAgeGroup, setNewPageAgeGroup] = useState('20s_30s');
    const [addingPage, setAddingPage] = useState(false);
    // Set right after successfully adding a page that needs the Meta-side
    // setup step (provider meta_direct) - drives the "next steps" banner
    // below the Add a page form, so this isn't easy to miss the way a
    // hover tooltip is right when it actually matters.
    const [justAddedPage, setJustAddedPage] = useState(null);

    // linkedin/instagram lock the provider (see SocialPage's docstring) -
    // keep newPageProvider in sync whenever the platform changes so the
    // form can't be submitted with an invalid combination.
    const handleNewPagePlatformChange = (platform) => {
        setNewPagePlatform(platform);
        if (platform === 'linkedin') setNewPageProvider('buffer');
        else if (platform === 'instagram') setNewPageProvider('meta_direct');
    };

    useEffect(() => {
        setPagesLoading(true);
        fetch('/api/admin/socials/pages', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { pages: [] }))
            .then((data) => setPages(data.pages || []))
            .catch(() => {})
            .finally(() => setPagesLoading(false));
    }, [adminToken]);

    const handlePageChange = (updated) => {
        setPages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    };

    // pages[].has_cadence is a snapshot computed by the GET /pages list
    // call (see app.py's list_social_pages) - saving a cadence inside a
    // PageCard doesn't refetch that list, so without this the Status tab's
    // per-city checklist kept showing "Cadence" as unmet until a full page
    // reload. Cadence save always means "now has one", so just flip it.
    const handlePageHasCadenceChange = (pageId) => {
        setPages((prev) => prev.map((p) => (p.id === pageId ? { ...p, has_cadence: true } : p)));
    };

    // Every live (page, objective) cadence, each with page/objective nested -
    // backs the "next draft expected" line on Review & Post and the
    // "upcoming drafts" list on Pages & Cadence. Reloaded (rather than
    // patched in place) after anything that can change what's due: a
    // cadence save (PageCard's own PUT, via onCadenceChange) or a page
    // delete (cascades its cadences) - both happen inside state this
    // component doesn't otherwise see updates to.
    const [allCadences, setAllCadences] = useState([]);
    const [allCadencesLoading, setAllCadencesLoading] = useState(true);

    const loadAllCadences = () => {
        setAllCadencesLoading(true);
        fetch('/api/admin/socials/cadences', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { cadences: [] }))
            .then((data) => setAllCadences(data.cadences || []))
            .catch(() => {})
            .finally(() => setAllCadencesLoading(false));
    };

    useEffect(() => {
        loadAllCadences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminToken]);

    const handleDeletePage = async (id) => {
        if (!window.confirm('Delete this page? Its posting cadence goes with it.')) return;
        try {
            const response = await fetch(`/api/admin/socials/pages/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) throw new Error('Failed to delete page');
            setPages((prev) => prev.filter((p) => p.id !== id));
            loadAllCadences();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleAddPage = async () => {
        if (!newPageCity.trim() || !newPageDisplayName.trim()) {
            alert('City and display name are required');
            return;
        }
        if (newPageProvider === 'buffer' && !newPageBufferId.trim()) {
            alert('Buffer channel ID is required for a page posting via Buffer');
            return;
        }
        if (newPageProvider === 'meta_direct' && !newPageMetaId.trim()) {
            alert(newPagePlatform === 'instagram' ? 'Instagram Business Account ID is required' : 'Meta Page ID is required for a page posting via direct Meta');
            return;
        }
        setAddingPage(true);
        try {
            const response = await fetch('/api/admin/socials/pages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                body: JSON.stringify({
                    city: newPageCity.trim(),
                    platform: newPagePlatform,
                    provider: newPageProvider,
                    buffer_profile_id: newPageBufferId.trim(),
                    meta_page_id: newPageMetaId.trim(),
                    display_name: newPageDisplayName.trim(),
                    page_type: newPageType,
                    age_group: newPageType === 'social' ? newPageAgeGroup : null,
                })
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to add page');
            const created = await response.json();
            setPages((prev) => [...prev, created]);
            setNewPageCity('');
            setNewPageBufferId('');
            setNewPageMetaId('');
            setNewPageDisplayName('');
            setNewPageType('social');
            setNewPageAgeGroup('20s_30s');
            setJustAddedPage(created.provider === 'meta_direct' ? created : null);
        } catch (err) {
            alert(err.message);
        } finally {
            setAddingPage(false);
        }
    };

    // Timeline tab: real "actual" clicks (already-collected PageView data,
    // see PageViewRepository.get_weekly_counts_by_source's docstring) plus
    // manually-entered follower/engagement data points (see
    // SocialGrowthSnapshot's docstring for why those can't be automatic).
    const [growthClicks, setGrowthClicks] = useState([]);
    const [growthPosts, setGrowthPosts] = useState([]);
    const [growthClicksLoading, setGrowthClicksLoading] = useState(true);

    useEffect(() => {
        setGrowthClicksLoading(true);
        fetch('/api/admin/socials/growth-analytics', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { weekly_clicks: [], weekly_posts: [] }))
            .then((data) => {
                setGrowthClicks(data.weekly_clicks || []);
                setGrowthPosts(data.weekly_posts || []);
            })
            .catch(() => {})
            .finally(() => setGrowthClicksLoading(false));
    }, [adminToken]);

    const [growthSnapshots, setGrowthSnapshots] = useState([]);
    const [growthSnapshotsLoading, setGrowthSnapshotsLoading] = useState(true);

    const loadGrowthSnapshots = () => {
        setGrowthSnapshotsLoading(true);
        fetch('/api/admin/socials/growth-snapshots', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { snapshots: [] }))
            .then((data) => setGrowthSnapshots(data.snapshots || []))
            .catch(() => {})
            .finally(() => setGrowthSnapshotsLoading(false));
    };

    useEffect(() => {
        loadGrowthSnapshots();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminToken]);

    const handleAddGrowthSnapshot = async (snapshot) => {
        const response = await fetch('/api/admin/socials/growth-snapshots', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
            body: JSON.stringify(snapshot),
        });
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to save data point');
        const created = await response.json();
        setGrowthSnapshots((prev) => [...prev, created]);
    };

    const handleDeleteGrowthSnapshot = async (id) => {
        if (!window.confirm('Delete this data point?')) return;
        try {
            const response = await fetch(`/api/admin/socials/growth-snapshots/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) throw new Error('Failed to delete data point');
            setGrowthSnapshots((prev) => prev.filter((s) => s.id !== id));
        } catch (err) {
            alert(err.message);
        }
    };

    // AI review of the Timeline tab's growth data (see social_growth_ai.py)
    // - same "run on demand, keep the latest, show/hide" pattern as
    // BlogAdmin.js's Content Coverage Review. Only ever sent already-
    // anonymous, aggregate data (weekly counts, per-page targets) - see
    // that module's docstring.
    const [growthReview, setGrowthReview] = useState(null);
    const [growthReviewing, setGrowthReviewing] = useState(false);
    const [growthReviewError, setGrowthReviewError] = useState('');
    const [showGrowthReview, setShowGrowthReview] = useState(false);

    useEffect(() => {
        fetch('/api/admin/socials/growth-review', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then(setGrowthReview)
            .catch(() => setGrowthReview(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminToken]);

    const handleRunGrowthReview = async () => {
        setGrowthReviewing(true);
        setGrowthReviewError('');
        try {
            const response = await fetch('/api/admin/socials/growth-review', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to run review');
            setGrowthReview(await response.json());
            setShowGrowthReview(true);
        } catch (err) {
            setGrowthReviewError(err.message);
        } finally {
            setGrowthReviewing(false);
        }
    };

    const [city, setCity] = useState('');
    const [daysAhead, setDaysAhead] = useState(7);
    const [events, setEvents] = useState(null);
    const [discovering, setDiscovering] = useState(false);
    const [error, setError] = useState('');
    // Which page this batch of drafts is for - required (see
    // generate_social_post_drafts) since a city can now have more than one
    // page on the same platform (SocialPage's page_type/age_group), so
    // there's no longer a safe default to fall back to.
    const [selectedPageId, setSelectedPageId] = useState('');
    // Reset whenever the city changes - a page selected for one city is
    // never valid for another.
    useEffect(() => { setSelectedPageId(''); }, [city]);

    const [manualEventsText, setManualEventsText] = useState('');
    const [manualError, setManualError] = useState('');

    const [selectedAngles, setSelectedAngles] = useState(() => new Set(DEFAULT_ANGLES));
    const [customAngle, setCustomAngle] = useState('');
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState('');

    const [publishing, setPublishing] = useState(false);
    const [publishError, setPublishError] = useState('');
    // currentPage represents "this week's page for this city" - set either by
    // checkCurrentPage (a read) or by a successful publish (a write), so the
    // same block below always reflects reality rather than just "what I last published".
    const [currentPage, setCurrentPage] = useState(null);
    const [checkingPage, setCheckingPage] = useState(false);

    const [livePages, setLivePages] = useState([]);
    const [livePagesLoading, setLivePagesLoading] = useState(true);

    // Most recent completed "Find Events" run per city (manual or the
    // auto-discovery kicked off by enabling a city - see app.py's
    // _run_city_enable_job) - events are never auto-published, so this is
    // what drives "N events found, awaiting review" nudges on the Cities/
    // Status tabs. Refetched after toggling a city's generation on, since
    // that's what kicks off a new auto-discovery job.
    const [discoveryJobsByCity, setDiscoveryJobsByCity] = useState({});
    const loadDiscoveryJobs = () => {
        fetch('/api/admin/socials/discover-events/latest-by-city', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { jobs_by_city: {} }))
            .then((data) => setDiscoveryJobsByCity(data.jobs_by_city || {}))
            .catch(() => {});
    };
    useEffect(() => {
        loadDiscoveryJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminToken]);

    // Persisted in the URL (not just React state) so refreshing the page, or
    // sharing/bookmarking a link, keeps the admin on the sub-tab they were
    // on instead of always bouncing back to the "workflow" default.
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('socialsTab') || 'workflow';
    const setActiveTab = (tab) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('socialsTab', tab);
            return next;
        }, { replace: true });
    };
    const [showAllCities, setShowAllCities] = useState(false);
    // City the "How a city goes live" pipeline explainer (Status tab) walks
    // through - defaults to the first city once cities load, so the section
    // is never blank while still letting the admin switch to whichever city
    // they're actually onboarding.
    const [pipelineCity, setPipelineCity] = useState('');
    useEffect(() => {
        if (!pipelineCity && cities.length > 0) setPipelineCity(cities[0].city);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cities]);
    // Manual find-events/generate-drafts tools on the Review & Post tab are
    // the on-demand alternative to the automatic Pages & Cadence pipeline -
    // collapsed by default so the primary review/approve queue above isn't
    // buried under tools most admins won't need day to day.
    const [showManualTools, setShowManualTools] = useState(false);

    // "Go to Pages & Cadence" button on a Cities-tab city card - pre-fills
    // the city so the admin doesn't have to re-pick it from the dropdown
    // on the tab they land on.
    const goToPagesTab = (city) => {
        setNewPageCity(city);
        setActiveTab('pages');
    };

    const goToCitiesTab = () => setActiveTab('cities');

    // "Go to Review & Post" jump from Status/Pages & Cadence - filters the
    // review queue to this city, and optionally expands the manual tools
    // with the city pre-picked (for "generate one now instead of waiting").
    const goToReviewTab = (cityName, { expandManualTools = false, pendingEvents = null } = {}) => {
        setCityFilter(cityName);
        if (expandManualTools) {
            setShowManualTools(true);
            setCity(cityName);
            checkCurrentPage(cityName);
            // Load an already-discovered batch (e.g. auto-discovery on city
            // enable, see CityProfileCard/CityChecklistRow) straight into
            // the "Find events" list instead of making the admin re-run
            // discovery for events that were already found.
            if (pendingEvents) setEvents(pendingEvents);
        }
        setActiveTab('workflow');
    };

    const [drafts, setDrafts] = useState([]);
    const [draftsLoading, setDraftsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [objectiveFilter, setObjectiveFilter] = useState('');

    const loadDrafts = async (status) => {
        setDraftsLoading(true);
        try {
            const qs = status ? `?status=${encodeURIComponent(status)}` : '';
            const response = await fetch(`/api/admin/socials/post-drafts${qs}`, {
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) throw new Error('Failed to load drafts');
            const data = await response.json();
            setDrafts(data.drafts || []);
        } catch (err) {
            console.error(err);
        } finally {
            setDraftsLoading(false);
        }
    };

    // "Generate Drafts" button - fires the exact same due-cadence check
    // and generation Heroku Scheduler runs automatically (see app.py's
    // trigger_generate_due_drafts/_run_generate_due_drafts_job), for when
    // an admin doesn't want to wait for the next scheduled run. Fire-and-
    // forget on the backend (it starts a background job and responds
    // immediately, no result to report beyond "started"), so this polls
    // the drafts list itself afterward rather than telling the admin to
    // come back and refresh by hand - see startGenerateDraftsPoll.
    const [generatingDrafts, setGeneratingDrafts] = useState(false);
    // null (idle) | 'polling' | 'found' | 'timeout'
    const [generateDraftsPollState, setGenerateDraftsPollState] = useState(null);
    const [generateDraftsFoundCount, setGenerateDraftsFoundCount] = useState(0);
    const generateDraftsPollRef = useRef(null);
    const generateDraftsPollsLeftRef = useRef(0);
    const [generateDraftsPageId, setGenerateDraftsPageId] = useState('');

    const stopGenerateDraftsPoll = () => {
        if (generateDraftsPollRef.current) {
            clearInterval(generateDraftsPollRef.current);
            generateDraftsPollRef.current = null;
        }
    };
    useEffect(() => stopGenerateDraftsPoll, []);

    // Checks every 15s for up to 3 minutes for a new draft to show up
    // (comparing ids against the snapshot taken right before the button
    // was clicked) - the generation job's own timing (event discovery +
    // Claude call) means there's a real, if short, wait, so this is
    // "auto-refreshing" rather than instant, but it means the admin never
    // has to guess whether to reload the page themselves.
    const startGenerateDraftsPoll = (idsBefore) => {
        stopGenerateDraftsPoll();
        setGenerateDraftsPollState('polling');
        generateDraftsPollsLeftRef.current = 12;
        generateDraftsPollRef.current = setInterval(async () => {
            generateDraftsPollsLeftRef.current -= 1;
            try {
                const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
                const response = await fetch(`/api/admin/socials/post-drafts${qs}`, {
                    headers: { 'Authorization': `Bearer ${adminToken}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    const freshDrafts = data.drafts || [];
                    setDrafts(freshDrafts);
                    const newCount = freshDrafts.filter((d) => !idsBefore.has(d.id)).length;
                    if (newCount > 0) {
                        setGenerateDraftsFoundCount(newCount);
                        setGenerateDraftsPollState('found');
                        stopGenerateDraftsPoll();
                        return;
                    }
                }
            } catch (err) {
                console.error(err);
            }
            if (generateDraftsPollsLeftRef.current <= 0) {
                setGenerateDraftsPollState('timeout');
                stopGenerateDraftsPoll();
            }
        }, 15000);
    };

    const handleGenerateDueDrafts = async () => {
        setGeneratingDrafts(true);
        setGenerateDraftsPollState(null);
        try {
            const idsBefore = new Set(drafts.map((d) => d.id));
            const response = await fetch('/api/admin/socials/generate-due-drafts', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ page_id: generateDraftsPageId ? Number(generateDraftsPageId) : null }),
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Failed to start draft generation');
            startGenerateDraftsPoll(idsBefore);
        } catch (err) {
            alert(err.message);
        } finally {
            setGeneratingDrafts(false);
        }
    };

    const loadLivePages = async () => {
        setLivePagesLoading(true);
        try {
            const response = await fetch('/api/admin/socials/event-pages', {
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) throw new Error('Failed to load pages');
            const data = await response.json();
            setLivePages(data.pages || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLivePagesLoading(false);
        }
    };

    useEffect(() => {
        loadDrafts(statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => {
        loadLivePages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Stats tab's own draft fetch, independent of the Review & Post tab's
    // `drafts`/statusFilter (that one refetches server-side filtered by
    // status, which would silently drop posted items whenever the admin
    // last filtered to something else there). "Posted" here means has a
    // logged publication or a Buffer queue entry, not draft.status==='posted'
    // specifically - a Buffer-auto-queued post can still show status
    // 'approved' until the admin manually marks it posted. Fetched once,
    // lazily, the first time this tab is opened.
    const [statsDrafts, setStatsDrafts] = useState([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsLoaded, setStatsLoaded] = useState(false);
    useEffect(() => {
        if (activeTab !== 'stats' || statsLoaded) return;
        setStatsLoading(true);
        fetch('/api/admin/socials/post-drafts', { headers: { 'Authorization': `Bearer ${adminToken}` } })
            .then((r) => (r.ok ? r.json() : { drafts: [] }))
            .then((data) => setStatsDrafts(data.drafts || []))
            .catch(() => {})
            .finally(() => { setStatsLoading(false); setStatsLoaded(true); });
    }, [activeTab, statsLoaded, adminToken]);

    // Discovery runs in a background thread on the server (see app.py's
    // SocialDiscoveryJob) since Claude's web_search-backed discovery call
    // can take well over Heroku's 30-second request timeout - starting it
    // returns a job id immediately, and this polls until it's done/failed
    // instead of waiting on one long request that Heroku would kill.
    // Caps polling at ~3 minutes (90 x 2s) - covers even a slow multi-search
    // discovery run, while not polling forever if the job gets stuck (e.g.
    // the dyno running its background thread got recycled mid-run).
    const MAX_DISCOVERY_POLLS = 90;

    const pollDiscoveryJob = async (jobId, attempt = 0) => {
        try {
            const response = await fetch(`/api/admin/socials/discover-events/${jobId}`, {
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) throw new Error('Failed to check discovery status');
            const job = await response.json();

            if (job.status === 'done') {
                setEvents(job.events || []);
                setDiscovering(false);
                checkCurrentPage(city);
            } else if (job.status === 'failed') {
                setError(job.error_message || 'Failed to discover events');
                setDiscovering(false);
            } else if (attempt >= MAX_DISCOVERY_POLLS) {
                setError('Still searching after 3 minutes - it may have stalled. Try again.');
                setDiscovering(false);
            } else {
                setTimeout(() => pollDiscoveryJob(jobId, attempt + 1), 2000);
            }
        } catch (err) {
            setError(err.message);
            setDiscovering(false);
        }
    };

    const handleDiscover = async () => {
        if (!city.trim()) {
            setError('Enter a city to search');
            return;
        }

        setDiscovering(true);
        setError('');
        setEvents(null);
        setPublishError('');

        try {
            const response = await fetch('/api/admin/socials/discover-events', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({ city: city.trim(), days_ahead: Number(daysAhead) })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to start event discovery');
            }

            const job = await response.json();
            pollDiscoveryJob(job.id);
        } catch (err) {
            setError(err.message);
            setDiscovering(false);
        }
    };

    const handleUseManualEvents = () => {
        const parsed = parseManualEvents(manualEventsText);
        if (parsed.length === 0) {
            setManualError('No events found - one per line, e.g. "Silent Disco Picnic | Sat 6 Sept, 2pm | Free outdoor silent disco in the park | https://..."');
            return;
        }
        setManualError('');
        setEvents(parsed);
        if (city.trim()) checkCurrentPage(city);
    };

    const toggleAngle = (angle) => {
        setSelectedAngles((prev) => {
            const next = new Set(prev);
            if (next.has(angle)) next.delete(angle); else next.add(angle);
            return next;
        });
    };

    // Read-only check of whichever page this week's "Publish" would update
    // (or create) for a city - so the UI can show what's already there
    // before publishing again, rather than publishing blind. Safe to call
    // freely (e.g. on blur) since it costs no AI call, just a DB lookup.
    const checkCurrentPage = async (cityName) => {
        if (!cityName.trim()) return;
        setCheckingPage(true);
        try {
            const response = await fetch(`/api/admin/socials/event-pages/current?city=${encodeURIComponent(cityName.trim())}`, {
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) return;
            const data = await response.json();
            setCurrentPage(data.page);
        } catch (err) {
            // Non-essential lookup - fail silently, publish still works either way.
        } finally {
            setCheckingPage(false);
        }
    };

    const handlePublishPage = async () => {
        if (!events || events.length === 0) {
            setPublishError('Get events into the system first, above');
            return;
        }

        setPublishing(true);
        setPublishError('');
        try {
            const response = await fetch('/api/admin/socials/event-pages', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({ city: city.trim(), events })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to publish events page');
            }
            const data = await response.json();
            setCurrentPage(data);
            loadLivePages();
        } catch (err) {
            setPublishError(err.message);
        } finally {
            setPublishing(false);
        }
    };

    const handleGenerate = async () => {
        if (!events || events.length === 0) {
            setGenError("Get events into the system first, above");
            return;
        }
        if (!selectedPageId) {
            setGenError('Pick which page these drafts are for');
            return;
        }
        const angles = Array.from(selectedAngles).map((a) => a.replace('{city}', city.trim()));
        if (customAngle.trim()) angles.push(customAngle.trim());
        if (angles.length === 0) {
            setGenError('Pick at least one content angle');
            return;
        }

        setGenerating(true);
        setGenError('');
        try {
            const response = await fetch('/api/admin/socials/generate-post-drafts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                    city: city.trim(), events, angles,
                    objective_id: selectedObjectiveId || undefined,
                    page_id: selectedPageId,
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate post drafts');
            }
            const data = await response.json();
            setDrafts((prev) => [...(data.drafts || []), ...prev]);
        } catch (err) {
            setGenError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const handleDraftChange = (updated) => {
        setDrafts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    };

    const handleDraftDelete = async (id) => {
        if (!window.confirm('Delete this draft?')) return;
        try {
            const response = await fetch(`/api/admin/socials/post-drafts/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (!response.ok) throw new Error('Failed to delete draft');
            setDrafts((prev) => prev.filter((d) => d.id !== id));
        } catch (err) {
            alert(err.message);
        }
    };

    // One row per city - the newest page per city_slug, since loadLivePages
    // returns every page (including past weeks) newest-first.
    const livePagesByCity = Object.values(
        livePages.reduce((acc, p) => {
            if (!acc[p.city_slug]) acc[p.city_slug] = p;
            return acc;
        }, {})
    );

    const enabledCities = cities.filter((c) => c.generation_enabled);
    const disabledCities = cities.filter((c) => !c.generation_enabled);
    const citiesExpanded = showAllCities || enabledCities.length === 0;

    const TABS = [
        { key: 'status', label: 'Status' },
        { key: 'workflow', label: 'Review & Post' },
        { key: 'stats', label: 'Stats' },
        { key: 'cities', label: 'Cities' },
        { key: 'pages', label: 'Pages & Cadence' },
        { key: 'timeline', label: 'Timeline' },
        { key: 'ai-settings', label: 'AI Settings' },
        { key: 'manual', label: 'Manual Fallback' },
        { key: 'advanced', label: 'Advanced' },
    ];

    // Pure summary of state already loaded elsewhere in this component - no
    // extra fetches. draftsByStatus is computed here rather than stored, so
    // this tab can never drift from what the other tabs actually show.
    const draftsByStatus = drafts.reduce((acc, d) => {
        acc[d.status] = (acc[d.status] || 0) + 1;
        return acc;
    }, {});
    // One draft can end up posted to more than one platform (each a
    // separate SocialPostPublication row on the draft) - count actual
    // published platforms rather than just the draft's own platform field.
    const postedByPlatform = drafts.reduce((acc, d) => {
        (d.publications || []).forEach((pub) => {
            acc[pub.platform] = (acc[pub.platform] || 0) + 1;
        });
        return acc;
    }, {});
    const totalPosted = Object.values(postedByPlatform).reduce((sum, n) => sum + n, 0);

    // Every city with any social activity at all (enabled, has a posting
    // page, or has a published events page) - not just enabled ones, so a
    // half-set-up city (e.g. a page added before the city was enabled)
    // still shows up on the Status tab's checklist instead of vanishing.
    //
    // Same 5 stages CityChecklistRow checks, computed here too so the list
    // can be sorted by them: a city with SOME but not all stages done (mid
    // setup - the one most likely to need attention right now) sorts
    // first, ahead of both a city that hasn't been started and one that's
    // already fully live (nothing to do there).
    const cityCompletionStage = (city) => {
        const enabled = enabledCities.some((c) => c.city === city);
        const hasLivePage = livePagesByCity.some((p) => p.city === city);
        const cityPages = pages.filter((p) => p.city === city && p.is_active);
        const hasPage = cityPages.length > 0;
        const hasCadence = cityPages.some((p) => p.has_cadence);
        const hasDrafts = drafts.some((d) => d.city === city);
        const doneCount = [enabled, hasLivePage, hasPage, hasCadence, hasDrafts].filter(Boolean).length;
        return { isPartial: doneCount > 0 && doneCount < 5 };
    };
    const activityCities = Array.from(new Set([
        ...enabledCities.map((c) => c.city),
        ...pages.map((p) => p.city),
        ...livePagesByCity.map((p) => p.city),
    ])).sort((a, b) => {
        const partialA = cityCompletionStage(a).isPartial;
        const partialB = cityCompletionStage(b).isPartial;
        if (partialA !== partialB) return partialA ? -1 : 1;
        return a.localeCompare(b);
    });

    // Drives the "How a city goes live" pipeline explainer's done-state and
    // jump buttons for whichever city is picked there - same underlying
    // signals as CityChecklistRow, just re-derived per step so each
    // PipelineStep can show its own "done" pill and "go do this"/"on to the
    // next step" button instead of one combined next-step hint.
    const pipelineCityProfile = cities.find((c) => c.city === pipelineCity);
    const pipelineActivePages = pages.filter((p) => p.city === pipelineCity && p.is_active);
    const pipelineCityDrafts = drafts.filter((d) => d.city === pipelineCity);
    const pipelineStepDone = [
        !!pipelineCityProfile?.generation_enabled,
        !!pipelineCityProfile?.generation_enabled && pipelineActivePages.length > 0,
        !!pipelineCityProfile?.generation_enabled && pipelineActivePages.length > 0 && pipelineActivePages.some((p) => p.has_cadence),
        !!pipelineCityProfile?.generation_enabled && pipelineActivePages.length > 0 && pipelineActivePages.some((p) => p.has_cadence)
            && pipelineCityDrafts.some((d) => d.status === 'approved' || d.status === 'posted'),
        automationStatus?.buffer_configured === true || automationStatus?.meta_direct_configured === true,
    ];
    const pipelineStepDestination = [
        { label: 'Cities', onClick: goToCitiesTab },
        { label: 'Pages & Cadence', onClick: () => goToPagesTab(pipelineCity) },
        { label: 'Pages & Cadence', onClick: () => goToPagesTab(pipelineCity) },
        { label: 'Review & Post', onClick: () => goToReviewTab(pipelineCity, { expandManualTools: true }) },
        { label: 'Advanced', onClick: () => setActiveTab('advanced') },
    ];
    // Each step's button either sends the admin to do that step now, or -
    // once it's done and the next one isn't - jumps straight to the next
    // step's tab. Both done (or no next step) means nothing left to jump to.
    const pipelineStepAction = (i) => {
        if (!pipelineStepDone[i]) {
            return { label: `Go to ${pipelineStepDestination[i].label}`, onClick: pipelineStepDestination[i].onClick };
        }
        if (i + 1 < pipelineStepDone.length && !pipelineStepDone[i + 1]) {
            return { label: `Next: ${pipelineStepDestination[i + 1].label}`, onClick: pipelineStepDestination[i + 1].onClick };
        }
        return null;
    };

    // allCadences grouped by page - due-ness (and so the occurrence math
    // below) is tracked per PAGE, not per (page, objective) cadence, since
    // a single batch of CADENCE_BATCH_SIZE posts can span several
    // objectives at once (see jobs/generate_scheduled_social_posts.py).
    const cadencesByPageId = allCadences.reduce((acc, c) => {
        (acc[c.page.id] = acc[c.page.id] || []).push(c);
        return acc;
    }, {});
    const pageCadenceGroups = Object.values(cadencesByPageId);

    // Soonest upcoming batch across all pages (or just cityFilter's, when
    // the Review & Post queue is filtered to one city) - drives that tab's
    // "next draft expected" line. Each page only contributes its own first
    // occurrence since that's the one that's actually "next".
    const nextDraftCandidates = pageCadenceGroups
        .filter((cadences) => !cityFilter || cadences[0].page.city === cityFilter)
        .map((cadences) => ({ time: pageBatchOccurrencesWithin(cadences, 7)[0], page: cadences[0].page }))
        .filter((entry) => entry.time !== undefined)
        .sort((a, b) => a.time - b.time);
    const nextDraft = nextDraftCandidates[0];

    // Every page's batch occurrence expected in the next 7 days, across
    // all cities - backs Pages & Cadence's upcoming-drafts list, grouped
    // by calendar day there.
    const upcomingOccurrences = pageCadenceGroups
        .flatMap((cadences) => pageBatchOccurrencesWithin(cadences, 7).map((time) => ({
            time,
            city: cadences[0].page.city,
            platform: cadences[0].page.platform,
        })))
        .sort((a, b) => a.time - b.time);

    return (
        <div className="social-admin-wrap" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <style>{`
                .social-admin-wrap { padding: 20px; box-sizing: border-box; }
                .social-admin-card { padding: 20px; box-sizing: border-box; }
                .draft-row-field { display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: #666; }
                .draft-row-field select, .draft-row-field input { min-width: 0; }
                @media (max-width: 640px) {
                    .social-admin-wrap { padding: 10px; }
                    .social-admin-card { padding: 12px; margin-bottom: 16px !important; }
                    .social-admin-tab-btn { padding: 8px 10px !important; font-size: 12px !important; }
                    .draft-row-card { padding: 10px !important; }
                    .draft-row-controls { flex-direction: column; align-items: stretch !important; gap: 10px !important; }
                    .draft-row-field { width: 100%; }
                    .draft-row-field select, .draft-row-field input { width: 100%; box-sizing: border-box; font-size: 16px !important; }
                    .draft-row-actions button { flex: 1 1 auto; }
                }
            `}</style>
            <h3>{tabName}</h3>
            <p style={{ color: '#666', fontSize: '13px', marginTop: '-10px', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                Mostly local-events content for engagement/followers, occasional manual posts about OkGather itself.
                <InfoTooltip text="Facebook and LinkedIn are the schedulable platforms (see Pages & Cadence) - Instagram drafts still get generated but need posting by hand, since Buffer can't post to Instagram without an image and this project has no image pipeline yet." />
            </p>

            <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #eee', flexWrap: 'wrap' }}>
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        className="social-admin-tab-btn"
                        onClick={() => setActiveTab(t.key)}
                        style={{
                            padding: '10px 16px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
                            border: 'none', borderBottom: activeTab === t.key ? '2px solid #6f42c1' : '2px solid transparent',
                            marginBottom: '-2px', backgroundColor: 'transparent',
                            color: activeTab === t.key ? '#6f42c1' : '#666',
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === 'status' && (
            <>
                <SectionCard title="How a city goes live">
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>
                        Numbered steps are things only you can do. The ⚡ note under each one is what that
                        step sets off on its own - nothing to do there, just what to expect next. Pick a city
                        below to see exactly which steps it's completed, with a button to do (or jump to) whichever's next.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>Walk through for:</label>
                        <select
                            value={pipelineCity}
                            onChange={(e) => setPipelineCity(e.target.value)}
                            disabled={citiesLoading}
                            style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                        >
                            <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                            {cities.map((c) => (
                                <option key={c.city} value={c.city}>{c.city}</option>
                            ))}
                        </select>
                    </div>
                    <PipelineStep
                        n={1} kind="Manual" triggers="events get found automatically, usually within a minute or two - then wait for your review (Review & Post) before the page actually publishes."
                        done={pipelineStepDone[0]} action={pipelineCity ? pipelineStepAction(0) : null}
                    >
                        Enable the city (Cities tab) - one click.
                    </PipelineStep>
                    <PipelineStep
                        n={2} kind="Manual"
                        done={pipelineStepDone[1]} action={pipelineCity ? pipelineStepAction(1) : null}
                    >
                        Add a Facebook and/or LinkedIn posting page for the city (Pages & Cadence tab). This ties to a real
                        business page you manage, so it can't be created automatically: first create (or already have)
                        the actual Facebook Page or LinkedIn Company Page and connect it as a channel in Buffer, then
                        copy that channel's ID from Buffer and paste it into the "Buffer channel ID" field here - the
                        Pages & Cadence tab doesn't create the Facebook/LinkedIn page itself, only links to it.
                    </PipelineStep>
                    <PipelineStep
                        n={3}
                        kind="Manual"
                        triggers="post drafts generate on that cadence via a daily job (or generate them on demand any time via Review & Post's manual tools)."
                        done={pipelineStepDone[2]} action={pipelineCity ? pipelineStepAction(2) : null}
                    >
                        Set a posting cadence (posts/week) for that page (Pages & Cadence tab).
                    </PipelineStep>
                    <PipelineStep
                        n={4}
                        kind="Manual"
                        triggers="the city's events page updates, and the post queues to Buffer/direct Meta too if that page's provider is connected (step 5)."
                        done={pipelineStepDone[3]} action={pipelineCity ? pipelineStepAction(3) : null}
                    >
                        Review, edit, and approve (or archive) each draft (Review & Post tab) - human review always happens,
                        whether the draft came from the cadence job above or the manual tools.
                    </PipelineStep>
                    <PipelineStep
                        n={5}
                        kind="Optional"
                        triggers="every future approval auto-posts (direct Meta, Buffer, or both, per each page's provider) - no separate switch."
                        done={pipelineStepDone[4]} action={pipelineCity ? pipelineStepAction(4) : null}
                    >
                        Connect direct Meta and/or Buffer (Heroku config, see Advanced tab) so an approved draft posts
                        itself instead of you posting it by hand.
                    </PipelineStep>
                </SectionCard>

                <SectionCard title="Cities" onNavigate={() => setActiveTab('cities')} navigateLabel="Cities">
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>
                        Checklist per city, in the order above. "Posting page" and "Cadence" are the Facebook/LinkedIn
                        side (Pages & Cadence tab); "Events page" is OkGather's own public page for that city.
                    </p>
                    {(citiesLoading || pagesLoading || livePagesLoading || draftsLoading) ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                    ) : activityCities.length === 0 ? (
                        <>
                            <p style={{ color: '#666', fontSize: '14px', margin: '8px 0 0 0' }}>No cities set up yet.</p>
                            <NextStepHint action={{ label: 'Go to Cities', onClick: goToCitiesTab }}>turn on automatic generation for a city in the Cities tab.</NextStepHint>
                        </>
                    ) : (
                        activityCities.map((city) => {
                            const cityDrafts = drafts.filter((d) => d.city === city);
                            return (
                                <CityChecklistRow
                                    key={city}
                                    city={city}
                                    enabled={enabledCities.some((c) => c.city === city)}
                                    hasLivePage={livePagesByCity.some((p) => p.city === city)}
                                    cityPages={pages.filter((p) => p.city === city)}
                                    draftsCount={cityDrafts.length}
                                    postedCount={cityDrafts.reduce((sum, d) => sum + (d.publications || []).length, 0)}
                                    onGoToCities={goToCitiesTab}
                                    onGoToPages={goToPagesTab}
                                    onGoToReview={goToReviewTab}
                                    pendingDiscoveryJob={discoveryJobsByCity[city]}
                                />
                            );
                        })
                    )}
                    {disabledCities.length > 0 && (
                        <p style={{ fontSize: '12px', color: '#999', margin: '8px 0 0 0' }}>
                            Not yet enabled: {disabledCities.map((c) => c.city).join(', ')}
                        </p>
                    )}
                </SectionCard>

                <SectionCard title="Objectives" onNavigate={() => setActiveTab('ai-settings')} navigateLabel="AI Settings">
                    {objectivesLoading ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                    ) : (
                        <>
                            <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
                                <strong>{objectives.filter((o) => o.is_active).length}</strong> active of <strong>{objectives.length}</strong> total:{' '}
                                {objectives.length === 0 ? 'none yet' : objectives.map((o) => `${o.label}${o.is_active ? '' : ' (inactive)'}`).join(', ')}
                            </p>
                            {objectives.filter((o) => o.is_active).length === 0 && (
                                <NextStepHint>add or activate an objective in AI Settings (optional - drafts work fine without one too).</NextStepHint>
                            )}
                        </>
                    )}
                </SectionCard>

                <SectionCard title="Published event pages" onNavigate={() => setActiveTab('workflow')} navigateLabel="Review & Post">
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0' }}>
                        OkGather's own public "/local-events/&lt;city&gt;" pages visitors see - not to be
                        confused with "Posting pages" above, which are the Facebook/LinkedIn destinations.
                    </p>
                    {livePagesLoading ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                    ) : livePagesByCity.length === 0 ? (
                        <>
                            <p style={{ color: '#666', fontSize: '14px' }}>None published yet.</p>
                            <NextStepHint
                                action={{ label: 'Go to Review & Post', onClick: () => { setShowManualTools(true); setActiveTab('workflow'); } }}
                            >
                                run Find Events, then Publish Events Page, in Review & Post's manual tools.
                            </NextStepHint>
                        </>
                    ) : (
                        <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
                            <strong>{livePagesByCity.length}</strong> {livePagesByCity.length === 1 ? 'city has' : 'cities have'} a published events page this week:{' '}
                            {livePagesByCity.map((p) => p.city).join(', ')}
                        </p>
                    )}
                </SectionCard>

                <SectionCard title="Drafts" onNavigate={() => setActiveTab('workflow')} navigateLabel="Review & Post">
                    {draftsLoading ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                    ) : drafts.length === 0 ? (
                        <>
                            <p style={{ color: '#666', fontSize: '14px' }}>None generated yet.</p>
                            <NextStepHint
                                action={{ label: 'Go to Review & Post', onClick: () => { setShowManualTools(true); setActiveTab('workflow'); } }}
                            >
                                generate post drafts in Review & Post's manual tools (needs events found first).
                            </NextStepHint>
                        </>
                    ) : (
                        <>
                            <p style={{ fontSize: '13px', color: '#333', margin: '0 0 4px 0' }}>
                                <strong>{drafts.length}</strong> generated - {draftsByStatus.draft || 0} awaiting review,{' '}
                                {draftsByStatus.approved || 0} approved, {draftsByStatus.posted || 0} marked posted, {draftsByStatus.archived || 0} archived.
                            </p>
                            <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
                                <strong>{totalPosted}</strong> {totalPosted === 1 ? 'post' : 'posts'} actually live
                                {totalPosted > 0 && (
                                    <> ({Object.entries(postedByPlatform).map(([platform, count]) => `${count} ${PLATFORM_LABELS[platform] || platform}`).join(', ')})</>
                                )}.
                            </p>
                            {draftsByStatus.draft > 0 && (
                                <NextStepHint action={{ label: 'Go to Review & Post', onClick: () => setActiveTab('workflow') }}>
                                    {draftsByStatus.draft} draft{draftsByStatus.draft === 1 ? '' : 's'} still awaiting review - review and approve in Review & Post.
                                </NextStepHint>
                            )}
                        </>
                    )}
                </SectionCard>

                <SectionCard title="Setup" onNavigate={() => setActiveTab('advanced')} navigateLabel="Advanced">
                    <StatusRow
                        label="Anthropic API key"
                        ok={aiAvailable === true}
                        pending={aiAvailable === null}
                        okText="Connected" notOkText="Not connected - Find Events / Generate Post Drafts are disabled"
                    />
                    <StatusRow
                        label="Direct Meta credentials"
                        ok={automationStatus?.meta_direct_configured === true}
                        pending={automationStatus === null}
                        okText="Connected - approving a draft on a direct-Meta page (the default for Facebook/Instagram) auto-posts it"
                        notOkText="Not connected - approving a draft on a direct-Meta page needs posting by hand (see Advanced tab)"
                    />
                    <StatusRow
                        label="Buffer credentials"
                        ok={automationStatus?.buffer_configured === true}
                        pending={automationStatus === null}
                        okText="Connected - approving a draft on a Buffer page (LinkedIn always, Facebook if switched) auto-posts it"
                        notOkText="Not connected - approving a draft on a Buffer page needs posting by hand (see Advanced tab)"
                    />
                    {aiAvailable === false && (
                        <NextStepHint>set <code>ANTHROPIC_API_KEY</code> in Heroku config vars.</NextStepHint>
                    )}
                    {aiAvailable !== false && automationStatus?.meta_direct_configured === false && automationStatus?.buffer_configured === false && (
                        <NextStepHint>set <code>META_POC_PAGE_ACCESS_TOKEN</code> and/or <code>BUFFER_ACCESS_TOKEN</code> in Heroku config vars, if you want approved drafts to auto-post (optional).</NextStepHint>
                    )}
                </SectionCard>
            </>
            )}

            {activeTab === 'workflow' && (
            <>
            {aiAvailable === false && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '15px',
                    padding: '12px 16px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffe69c'
                }}>
                    <span style={{ fontSize: '16px', lineHeight: '1.4' }}>🟡</span>
                    <div>
                        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '14px', color: '#856404' }}>
                            AI features aren't connected in this environment
                        </p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#856404' }}>
                            <code>ANTHROPIC_API_KEY</code> isn't set, so "Find Events" and "Generate Post Drafts"
                            below are disabled. Use the Manual Fallback tab instead - copy a template prompt into
                            ChatGPT, Claude.ai, or any AI chat tool by hand, then paste what it gives you back in.
                            Everything else (publishing the events page, reviewing/posting captions, live-page
                            tracking) works the same either way.
                        </p>
                    </div>
                </div>
            )}
            {aiAvailable === true && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px',
                    padding: '12px 16px', backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #c3e6cb'
                }}>
                    <span style={{ fontSize: '16px', lineHeight: '1.4' }}>🟢</span>
                    <p style={{ margin: 0, fontWeight: 'bold', fontSize: '14px', color: '#155724', display: 'flex', alignItems: 'center' }}>
                        Live: automatic generation (Pages & Cadence) and manual tools (below, optional)
                        <InfoTooltip text={'An LLM finds real local events, publishes a page with a real link to each one, and drafts post captions from the same list - either automatically per each city\'s configured cadence (the default, needs no admin action) or on demand via the manual tools below. Either way you review/edit the caption and post it by hand, then either point that page\'s bio at the events page or paste the draft\'s suggested first comment for a more specific link.'} />
                    </p>
                </div>
            )}

            <SectionCard title="Review & post drafts">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '15px', flexWrap: 'wrap' }}>
                    {STATUS_FILTER_OPTIONS.map((opt) => {
                        const active = statusFilter === opt.value;
                        const colors = opt.value ? STATUS_COLORS[opt.value] : { bg: '#e2d9f3', text: '#4b2e83' };
                        return (
                            <button
                                key={opt.value || 'all'}
                                type="button"
                                onClick={() => setStatusFilter(opt.value)}
                                style={{
                                    padding: '8px 18px', fontSize: '13px', fontWeight: 'bold', border: 'none',
                                    borderRadius: '18px', cursor: 'pointer',
                                    backgroundColor: active ? colors.text : colors.bg,
                                    color: active ? 'white' : colors.text,
                                    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.25)' : 'none',
                                }}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                {!draftsLoading && (
                    (draftsByStatus.draft || 0) > 0 ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px',
                            padding: '12px 16px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffe69c',
                        }}>
                            <span style={{ fontSize: '18px', lineHeight: '1.2' }}>🔔</span>
                            <p style={{ margin: 0, fontWeight: 'bold', fontSize: '14px', color: '#856404' }}>
                                {draftsByStatus.draft} draft{draftsByStatus.draft === 1 ? '' : 's'} awaiting your review
                            </p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px',
                            padding: '12px 16px', backgroundColor: '#d4edda', borderRadius: '8px', border: '1px solid #c3e6cb',
                        }}>
                            <span style={{ fontSize: '18px', lineHeight: '1.2' }}>✅</span>
                            <p style={{ margin: 0, fontWeight: 'bold', fontSize: '14px', color: '#155724' }}>
                                All caught up - nothing awaiting review right now
                            </p>
                        </div>
                    )
                )}
                <div style={{
                    fontSize: '12px', color: '#41464b', backgroundColor: '#e2e3e5', borderRadius: '4px',
                    padding: '8px 12px', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
                }}>
                    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
                        {allCadencesLoading ? (
                            'Checking automatic posting cadence...'
                        ) : nextDraft ? (
                            <>
                                <strong>Next automatic batch expected:</strong>&nbsp;
                                {nextDraft.page.city} - {CADENCE_BATCH_SIZE} posts ({PLATFORM_LABELS[nextDraft.page.platform] || nextDraft.page.platform}),&nbsp;
                                {formatRelativeEta(nextDraft.time)}
                                <InfoTooltip text={`Estimated from when that page last generated a batch - a batch of ${CADENCE_BATCH_SIZE} posts (spanning whichever objectives are due, per that page's ratio) is generated every ${CADENCE_BATCH_INTERVAL_DAYS} days. The daily generation job still needs to actually run and event discovery still needs to succeed, so treat this as roughly when, not a guarantee.`} />
                            </>
                        ) : (
                            <>No automatic posting cadence configured{cityFilter ? ` for ${cityFilter}` : ''} - set one in Pages &amp; Cadence, or generate a draft manually below.</>
                        )}
                    </span>
                    <select
                        value={generateDraftsPageId}
                        onChange={(e) => setGenerateDraftsPageId(e.target.value)}
                        disabled={generatingDrafts || pagesLoading}
                        style={{ fontSize: '12px', padding: '4px 6px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        <option value="" disabled>Select a page...</option>
                        {pages
                            .slice()
                            .sort((a, b) => a.city.localeCompare(b.city) || a.display_name.localeCompare(b.display_name))
                            .map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.city} - {p.display_name} ({PLATFORM_LABELS[p.platform] || p.platform})
                                </option>
                            ))}
                    </select>
                    <button
                        type="button"
                        onClick={handleGenerateDueDrafts}
                        disabled={generatingDrafts || aiAvailable !== true || !generateDraftsPageId}
                        style={{
                            padding: '6px 14px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap',
                            border: 'none', borderRadius: '4px', cursor: (generatingDrafts || aiAvailable !== true || !generateDraftsPageId) ? 'default' : 'pointer',
                            backgroundColor: '#6f42c1', color: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                            opacity: (generatingDrafts || aiAvailable !== true || !generateDraftsPageId) ? 0.5 : 1,
                        }}
                    >
                        {generatingDrafts ? 'Starting...' : 'Generate Drafts'}
                    </button>
                    <InfoTooltip text={'Generates a fresh batch of drafts for the page you pick above right now - unlike the automatic scheduled job, this doesn\'t wait for that page\'s posting cadence to actually be due, since picking a page and clicking this is already a deliberate request for content now. New posts still only go live at the normal 2/day pace once approved (target_date/time still packs onto the next open day) - this only affects how fast drafts pile up for review, not how fast they post. Different from "Generate Post Drafts" in the manual tools below, which drafts from a specific city\'s already-discovered events instead of finding new ones.'} />
                </div>
                {generateDraftsPollState && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '8px',
                        margin: '0 0 14px 0', fontSize: '14px', fontWeight: 'bold',
                        backgroundColor: generateDraftsPollState === 'found' ? '#d4edda' : generateDraftsPollState === 'timeout' ? '#fff3cd' : '#cfe2ff',
                        color: generateDraftsPollState === 'found' ? '#155724' : generateDraftsPollState === 'timeout' ? '#856404' : '#084298',
                    }}>
                        <span style={{ fontSize: '18px' }}>
                            {generateDraftsPollState === 'found' ? '✅' : generateDraftsPollState === 'timeout' ? '⏱️' : '⏳'}
                        </span>
                        <span>
                            {generateDraftsPollState === 'polling' && (
                                "Checking for new drafts every 15 seconds, no need to refresh - this page updates itself as soon as they're ready (up to a few minutes)."
                            )}
                            {generateDraftsPollState === 'found' && (
                                `${generateDraftsFoundCount} new draft${generateDraftsFoundCount === 1 ? '' : 's'} generated - see the list below.`
                            )}
                            {generateDraftsPollState === 'timeout' && (
                                "Nothing appeared after 3 minutes. This page was confirmed properly configured before starting, so this usually means no events could be found for its city right now, or the draft-writing step failed - try \"Find Events\" in the manual tools below to see what's actually available for this city, or check back and try again shortly."
                            )}
                        </span>
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', color: '#666' }}>Also filter by:</span>
                    <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={{ ...inputStyle, padding: '4px 6px', fontSize: '12px' }}>
                        <option value="">All cities</option>
                        {Array.from(new Set(drafts.map((d) => d.city))).sort().map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    <select value={objectiveFilter} onChange={(e) => setObjectiveFilter(e.target.value)} style={{ ...inputStyle, padding: '4px 6px', fontSize: '12px' }}>
                        <option value="">All objectives</option>
                        {Array.from(new Set(drafts.map((d) => parseAngle(d.angle).objective).filter(Boolean))).sort().map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                </div>

                {draftsLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading drafts...</p>
                ) : drafts.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>
                        No drafts yet - these generate automatically on each city's posting cadence (Pages &amp; Cadence),
                        or use the manual tools below to generate some on demand, or just copy a caption straight
                        from the Manual Fallback tab (nothing needs to land here first).
                    </p>
                ) : (() => {
                    const filteredDrafts = drafts
                        .filter((d) => !cityFilter || d.city === cityFilter)
                        .filter((d) => !objectiveFilter || parseAngle(d.angle).objective === objectiveFilter);
                    // Only 'draft' still needs active review - everything else
                    // (approved/posted/archived) is done, so it collapses into
                    // ResolvedDraftsTable below instead of taking up a full
                    // card's worth of space per post.
                    const openDrafts = filteredDrafts.filter((d) => d.status === 'draft');
                    const resolvedDrafts = filteredDrafts.filter((d) => d.status !== 'draft');
                    return (
                        <>
                            <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', margin: '0 0 10px 0' }}>
                                Drafts awaiting review ({openDrafts.length})
                            </p>
                            {openDrafts.length === 0 ? (
                                <p style={{ color: '#666', fontSize: '14px' }}>Nothing awaiting review right now.</p>
                            ) : (
                                openDrafts.map((draft) => {
                                    // batch siblings looked up from the full (unfiltered-by-status)
                                    // drafts array, since a status filter's server-side refetch
                                    // can otherwise hide the very sibling that makes this one
                                    // locked - see handleDraftChange/create_many.
                                    const batchSiblings = draft.batch_id ? drafts.filter((d) => d.batch_id === draft.batch_id) : [];
                                    const batchChosen = batchSiblings.find((d) => d.id !== draft.id && (d.status === 'approved' || d.status === 'posted'));
                                    return (
                                        <DraftRow
                                            key={draft.id}
                                            draft={draft}
                                            adminToken={adminToken}
                                            onChange={handleDraftChange}
                                            onDelete={handleDraftDelete}
                                            automationStatus={automationStatus}
                                            onGoToManualTools={(cityName) => { setShowManualTools(true); setCity(cityName); checkCurrentPage(cityName); }}
                                            batchSize={batchSiblings.length > 1 ? batchSiblings.length : undefined}
                                            batchChosen={batchChosen || null}
                                            pages={pages}
                                        />
                                    );
                                })
                            )}
                            {resolvedDrafts.length > 0 && (
                                <div style={{ marginTop: '28px', paddingTop: '18px', borderTop: '3px solid #e2d9f3' }}>
                                    <ResolvedDraftsTable
                                        drafts={resolvedDrafts}
                                        allDraftsForBatches={drafts}
                                        adminToken={adminToken}
                                        onChange={handleDraftChange}
                                        onDelete={handleDraftDelete}
                                        automationStatus={automationStatus}
                                        onGoToManualTools={(cityName) => { setShowManualTools(true); setCity(cityName); checkCurrentPage(cityName); }}
                                        pages={pages}
                                    />
                                </div>
                            )}
                        </>
                    );
                })()}
                {(draftsByStatus.approved > 0) && automationStatus?.buffer_configured === false && (
                    <NextStepHint action={{ label: 'Go to Advanced', onClick: () => setActiveTab('advanced') }}>
                        {draftsByStatus.approved} approved draft{draftsByStatus.approved === 1 ? '' : 's'} still need posting by hand - connect
                        Buffer (optional) so future approvals post themselves.
                    </NextStepHint>
                )}
            </SectionCard>

            <SectionCard title="Published event pages">
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                    One row per city - the current week's public page, whichever is newest.
                    <InfoTooltip text="This is what /local-events/<city> (each page's stable bio link, see Pages & Cadence) actually resolves to right now." />
                </p>
                {livePagesLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                ) : livePagesByCity.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>No pages published yet - events get found automatically once a city's enabled, but still need your review and an explicit publish (Cities tab, or "Find Events" in the manual tools below).</p>
                ) : (
                    livePagesByCity.map((p) => (
                        <div key={p.city_slug} style={{ borderTop: '1px solid #eee', padding: '10px 0', fontSize: '13px' }}>
                            <p style={{ margin: '0 0 4px 0' }}>
                                <strong>{p.city}</strong> — {(p.events || []).length} events, week of {p.week_start}
                            </p>
                            <p style={{ margin: 0 }}>
                                <a href={`${window.location.origin}/local-events/${p.slug}`} target="_blank" rel="noopener noreferrer">
                                    /local-events/{p.slug}
                                </a>
                                {' · '}
                                <a href={`${window.location.origin}/local-events/${p.city_slug}`} target="_blank" rel="noopener noreferrer">
                                    /local-events/{p.city_slug} (stable/bio link)
                                </a>
                            </p>
                        </div>
                    ))
                )}
            </SectionCard>

            <div style={{ textAlign: 'center', marginBottom: showManualTools ? '15px' : '10px' }}>
                <button
                    type="button"
                    onClick={() => setShowManualTools((v) => !v)}
                    style={{
                        padding: '4px 10px', backgroundColor: 'transparent', border: 'none',
                        cursor: 'pointer', fontSize: '12px', color: '#999', textDecoration: 'underline',
                    }}
                >
                    {showManualTools ? '▾ Hide manual tools' : '▸ Manual tools (optional, advanced)'}
                </button>
            </div>

            {showManualTools && (
            <>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 15px 0', textAlign: 'center' }}>
                Find events and generate drafts on demand, instead of waiting for each city's automatic
                posting cadence - most admins won't need this day to day.
            </p>
            <SectionCard title="Find events">
                <select
                    value={city}
                    onChange={(e) => { setCity(e.target.value); checkCurrentPage(e.target.value); }}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '15px' }}
                    disabled={discovering || citiesLoading}
                >
                    <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                    {cities.map((c) => (
                        <option key={c.city} value={c.city}>{c.city}</option>
                    ))}
                </select>

                <div style={{ border: '1px solid #eee', borderRadius: '6px', padding: '15px', marginBottom: '15px', opacity: aiAvailable === false ? 0.6 : 1 }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', margin: '0 0 8px 0' }}>
                        AI-powered (needs a connected Anthropic API key)
                    </p>
                    {aiAvailable === false && (
                        <p style={{ fontSize: '13px', color: '#856404', marginBottom: '12px' }}>
                            Not configured in this environment yet - use the manual option above for now.
                        </p>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                        <input
                            type="number"
                            min="1"
                            max="30"
                            value={daysAhead}
                            onChange={(e) => setDaysAhead(e.target.value)}
                            style={{ ...inputStyle, width: '90px' }}
                            disabled={discovering || aiAvailable === false}
                            title="Days ahead to search"
                        />
                        <button
                            type="button"
                            onClick={handleDiscover}
                            disabled={discovering || aiAvailable === false}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: (discovering || aiAvailable === false) ? '#ccc' : '#6f42c1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: (discovering || aiAvailable === false) ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}
                        >
                            {discovering ? 'Searching...' : 'Find Events'}
                        </button>
                    </div>

                    {error && (
                        <div style={{
                            color: 'red', padding: '10px', marginBottom: '15px',
                            border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffe6e6', fontSize: '13px'
                        }}>
                            {error}
                        </div>
                    )}
                </div>

                {events && (
                    events.length === 0 ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>No events found - try a different city or a wider date range.</p>
                    ) : (
                        <div>
                            {events.map((event, i) => (
                                <div key={i} style={{ borderTop: '1px solid #eee', padding: '10px 0' }}>
                                    <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px' }}>
                                        {event.name}{event.date ? ` — ${event.date}` : ''}
                                    </p>
                                    <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#333' }}>
                                        {event.description}
                                    </p>
                                    {event.source_url && (
                                        <a href={event.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
                                            Source
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                )}

                {city.trim() && (
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '10px' }}>
                        <p style={{ fontSize: '13px', color: '#333', fontWeight: 'bold', marginBottom: '8px' }}>
                            This week's public events page for {city.trim()}
                        </p>

                        {checkingPage ? (
                            <p style={{ fontSize: '13px', color: '#666' }}>Checking...</p>
                        ) : currentPage ? (
                            <div style={{
                                backgroundColor: '#f8f9fa', border: '1px solid #eee', borderRadius: '4px',
                                padding: '10px 12px', marginBottom: '12px', fontSize: '13px'
                            }}>
                                <p style={{ margin: '0 0 6px 0' }}>
                                    Already published, with <strong>{(currentPage.events || []).length} events</strong> on it.
                                    {events && events.length > 0 ? ' Publishing below adds any new ones found above to this same page.' : ''}
                                </p>
                                <p style={{ margin: '0 0 2px 0' }}>
                                    <strong>This week's link</strong> (use for a draft's "suggested first comment"):{' '}
                                    <a href={currentPage.url} target="_blank" rel="noopener noreferrer">{currentPage.url}</a>
                                </p>
                                <p style={{ margin: 0 }}>
                                    <strong>Stable link</strong> (this is what each page's bio should point at - always resolves to whichever week is current):{' '}
                                    <a href={currentPage.latest_url} target="_blank" rel="noopener noreferrer">{currentPage.latest_url}</a>
                                </p>
                            </div>
                        ) : (
                            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                                No page published yet this week for {city.trim()}.
                                {events && events.length > 0 ? '' : ' Get events above, then publish one below.'}
                            </p>
                        )}

                        {events && events.length > 0 && (
                            <>
                                <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                                    Publishing turns the list above into (or adds it to) that page - real links to
                                    each event, safe to share. One page per city per week, so every post you send
                                    out this week can point at the same link instead of each getting its own.
                                </p>
                                <button
                                    type="button"
                                    onClick={handlePublishPage}
                                    disabled={publishing}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: publishing ? '#ccc' : 'white',
                                        color: '#6f42c1',
                                        border: '1px solid #6f42c1',
                                        borderRadius: '4px',
                                        cursor: publishing ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {publishing ? 'Publishing...' : (currentPage ? "Add These to This Week's Page" : "Publish This Week's Events Page")}
                                </button>

                                {publishError && (
                                    <div style={{ color: 'red', fontSize: '13px', marginTop: '10px' }}>{publishError}</div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </SectionCard>

            <SectionCard title="Draft posts from those events (AI-powered)">
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                    Pick which angles to write, then generate - each becomes one draft caption below.
                    <InfoTooltip text={'Whenever a draft mentions OkGather itself, the same fact-check discipline as the blog (OKGATHER_FACTS.md) is applied automatically. Needs a connected Anthropic API key, same as "Find Events" above - if you used the manual template workflow instead, you already have a caption from that and don\'t need this step.'} />
                </p>
                {selectedAngles.size + (customAngle.trim() ? 1 : 0) > 1 && (
                    <p style={{ fontSize: '12px', color: '#4b2e83', backgroundColor: '#e2d9f3', borderRadius: '4px', padding: '8px 12px', margin: '0 0 15px 0', display: 'flex', alignItems: 'center' }}>
                        These are alternate variations, not a full schedule - approving one below locks the rest until you revoke it.
                        <InfoTooltip text="Every angle checked here is generated together from the same events, so they'll likely overlap. Below, they're labeled as one batch to pick from - once one is approved, its siblings can't be approved too until that approval is revoked (a 'Revoke approval' button appears on the approved one)." />
                    </p>
                )}

                <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                    Page (required)
                </label>
                <select
                    value={selectedPageId}
                    onChange={(e) => setSelectedPageId(e.target.value)}
                    disabled={aiAvailable === false}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '15px' }}
                >
                    <option value="">
                        {pages.filter((p) => p.city === city.trim() && p.is_active).length === 0
                            ? 'No pages for this city yet - add one in Pages & Cadence'
                            : 'Select a page'}
                    </option>
                    {pages.filter((p) => p.city === city.trim() && p.is_active).map((p) => (
                        <option key={p.id} value={p.id}>
                            {PLATFORM_LABELS[p.platform]} - {p.page_type === 'tech' ? 'Tech' : `Social (${p.age_group === '30s_40s' ? '30s&40s' : '20s&30s'})`} - {p.display_name}
                        </option>
                    ))}
                </select>

                <div style={{ marginBottom: '10px', opacity: aiAvailable === false ? 0.6 : 1 }}>
                    {DEFAULT_ANGLES.map((angle) => (
                        <label key={angle} style={{ display: 'block', fontSize: '13px', marginBottom: '6px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selectedAngles.has(angle)}
                                onChange={() => toggleAngle(angle)}
                                disabled={aiAvailable === false}
                                style={{ marginRight: '8px' }}
                            />
                            {angle.replace('{city}', city.trim() || '[city]')}
                        </label>
                    ))}
                </div>
                <input
                    type="text"
                    value={customAngle}
                    onChange={(e) => setCustomAngle(e.target.value)}
                    placeholder="Custom angle (optional), e.g. 'Best rooftop bars this summer'"
                    disabled={aiAvailable === false}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '15px' }}
                />

                <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                    Objective (optional)
                </label>
                <select
                    value={selectedObjectiveId}
                    onChange={(e) => setSelectedObjectiveId(e.target.value)}
                    disabled={aiAvailable === false}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '15px' }}
                >
                    <option value="">General (no specific objective)</option>
                    {objectives.filter((o) => o.is_active).map((o) => (
                        <option key={o.id} value={o.id}>{o.label}{o.description ? ` - ${o.description}` : ''}</option>
                    ))}
                </select>

                <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating || aiAvailable === false || !selectedPageId}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: (generating || aiAvailable === false || !selectedPageId) ? '#ccc' : '#6f42c1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (generating || aiAvailable === false || !selectedPageId) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        marginBottom: '10px'
                    }}
                >
                    {generating ? 'Drafting...' : 'Generate Post Drafts'}
                </button>
                {aiAvailable === false && (
                    <p style={{ fontSize: '13px', color: '#856404', margin: 0 }}>
                        Not configured in this environment yet.
                    </p>
                )}

                {genError && (
                    <div style={{
                        color: 'red', padding: '10px', marginBottom: '15px',
                        border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffe6e6', fontSize: '13px'
                    }}>
                        {genError}
                    </div>
                )}
            </SectionCard>
            </>
            )}
            </>
            )}

            {activeTab === 'stats' && (
            <SectionCard title="Post performance">
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px 0', display: 'flex', alignItems: 'center' }}>
                    Every post that's actually gone out, Buffer-queued or logged by hand.
                    <InfoTooltip text={'Metrics come from Buffer\'s experimental, personal-use-only post-metrics API - Buffer\'s own docs say not to rely on it for reporting, and it can come back empty even for a real post, so it\'s fetched per-post on click rather than for the whole list at once. There\'s no live-post URL in Buffer\'s API either, so a Buffer-queued post (as opposed to one logged by hand with its real link via "Add Publication") links to that channel\'s Buffer dashboard instead of the post itself.'} />
                </p>
                {statsLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                ) : (() => {
                    const postedItems = statsDrafts.filter(
                        (d) => (d.publications || []).length > 0 || (d.scheduler_queue || []).some((q) => q.status === 'queued')
                    );
                    if (postedItems.length === 0) {
                        return <p style={{ color: '#666', fontSize: '14px' }}>Nothing posted yet - approved drafts show up here once Buffer queues them, or once you log a publication by hand.</p>;
                    }
                    return postedItems.map((d) => (
                        <div key={d.id} style={{ borderTop: '1px solid #eee', padding: '10px 0' }}>
                            <p style={{ margin: '0 0 4px 0', fontSize: '13px' }}>
                                <strong>{d.city}</strong> · {PLATFORM_LABELS[d.platform] || d.platform}
                                {' · '}{new Date(d.created_at).toLocaleDateString()}
                            </p>
                            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                                {d.caption.length > 140 ? `${d.caption.slice(0, 140)}...` : d.caption}
                            </p>
                            {(d.publications || []).map((pub) => (
                                <div key={pub.id} style={{ marginBottom: '6px' }}>
                                    <a href={pub.post_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                        View post ↗
                                    </a>
                                </div>
                            ))}
                            {(d.scheduler_queue || []).filter((q) => q.status === 'queued').map((q) => {
                                const page = pages.find((p) => p.city === d.city && p.platform === q.platform);
                                const channelUrl = page ? `https://publish.buffer.com/channels/${page.buffer_profile_id}` : null;
                                return (
                                    <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                        {channelUrl ? (
                                            <a href={channelUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                View in Buffer ↗
                                            </a>
                                        ) : (
                                            <span style={{ fontSize: '12px', color: '#999' }}>Queued via Buffer</span>
                                        )}
                                        {q.external_id && <PostMetricsButton postId={q.external_id} adminToken={adminToken} />}
                                    </div>
                                );
                            })}
                        </div>
                    ));
                })()}
            </SectionCard>
            )}

            {activeTab === 'cities' && (
            <SectionCard title="Cities we operate in">
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px 0', display: 'flex', alignItems: 'center' }}>
                    Every city picker in this tab is populated from here, so a typo can't create a mismatched city.
                    <InfoTooltip text="Pulled from OkGather's active operating cities (the same locations used elsewhere in the app). Add a city context note to nudge event discovery and caption drafting for that city specifically - optional, layered on top of the general brand voice above. This is general city flavor, not audience targeting - whether a page is Social (with an age group) or Tech is chosen per page in the Pages & Cadence tab, a step later in setup." />
                </p>
                {citiesLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                ) : cities.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>
                        No active cities found - add one under Admin &gt; Locations first.
                    </p>
                ) : (
                    <>
                        {enabledCities.length === 0 ? (
                            <p style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>
                                No cities have generation turned on yet - enable one below to get started.
                            </p>
                        ) : (
                            enabledCities.map((c) => (
                                <CityProfileCard
                                    key={c.city}
                                    profile={c}
                                    pages={pages}
                                    adminToken={adminToken}
                                    onChange={handleCityProfileChange}
                                    onGoToPages={goToPagesTab}
                                    onGoToReview={goToReviewTab}
                                    livePage={livePagesByCity.find((p) => p.city === c.city)}
                                    pendingDiscoveryJob={discoveryJobsByCity[c.city]}
                                    onDiscoveryJobStarted={loadDiscoveryJobs}
                                />
                            ))
                        )}

                        {disabledCities.length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                                {!citiesExpanded && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllCities(true)}
                                        style={{
                                            width: '100%', textAlign: 'left', padding: '8px 12px', backgroundColor: '#f8f9fa',
                                            border: '1px solid #eee', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#666'
                                        }}
                                    >
                                        ▸ +{disabledCities.length} more {disabledCities.length === 1 ? 'city' : 'cities'} not yet enabled
                                    </button>
                                )}
                                {citiesExpanded && (
                                    <>
                                        {enabledCities.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setShowAllCities(false)}
                                                style={{
                                                    width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: '8px', backgroundColor: '#f8f9fa',
                                                    border: '1px solid #eee', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#666'
                                                }}
                                            >
                                                ▾ Hide cities not yet enabled
                                            </button>
                                        )}
                                        {disabledCities.map((c) => (
                                            <CityProfileCard
                                    key={c.city}
                                    profile={c}
                                    pages={pages}
                                    adminToken={adminToken}
                                    onChange={handleCityProfileChange}
                                    onGoToPages={goToPagesTab}
                                    onGoToReview={goToReviewTab}
                                    livePage={livePagesByCity.find((p) => p.city === c.city)}
                                    pendingDiscoveryJob={discoveryJobsByCity[c.city]}
                                    onDiscoveryJobStarted={loadDiscoveryJobs}
                                />
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </SectionCard>
            )}

            {activeTab === 'ai-settings' && (
            <SectionCard title="AI prompt settings">
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 15px 0', display: 'flex', alignItems: 'center' }}>
                    This is the AI's harness - edit it to change output without a code change.
                    <InfoTooltip text={'Controls how AI-generated drafts are written, whether from automatic cadence generation or the manual "Draft posts" tool in Review & Post - brand voice and writing rules apply to every generation, objectives are picked per-batch there. Fact-checking against OKGATHER_FACTS.md and the "never invent an event" rule always apply on top of whatever\'s here and aren\'t editable - that\'s a safety rail, not a style choice.'} />
                </p>

                <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    Brand voice and audience
                    <InfoTooltip text="Who this account is speaking to and how it sounds, e.g. audience age range, tone (punchy/warm vs. formal), and what generally gets favored or avoided when picking events to feature." />
                </label>
                <textarea
                    value={brandVoiceDraft}
                    onChange={(e) => setBrandVoiceDraft(e.target.value)}
                    rows={4}
                    disabled={!promptSettingsLoaded}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '12px', fontFamily: 'inherit' }}
                />

                <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    Writing rules
                    <InfoTooltip text="Concrete formatting/style rules every caption must follow, e.g. no em dashes, how to handle hashtags, when to invite readers to tag a friend, and how a LinkedIn post's register should differ from Instagram/Facebook." />
                </label>
                <textarea
                    value={writingRulesDraft}
                    onChange={(e) => setWritingRulesDraft(e.target.value)}
                    rows={6}
                    disabled={!promptSettingsLoaded}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    <button
                        type="button"
                        onClick={handleSavePromptSettings}
                        disabled={savingPromptSettings || !promptSettingsLoaded}
                        style={{ padding: '8px 16px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                    >
                        {savingPromptSettings ? 'Saving...' : 'Save Brand Voice & Writing Rules'}
                    </button>
                    <button
                        type="button"
                        onClick={handleResetPromptSettings}
                        disabled={resettingPromptSettings || !promptSettingsLoaded}
                        title="Reverts both fields to OkGather's built-in defaults - the current wording is saved to history first"
                        style={{ padding: '8px 16px', backgroundColor: 'white', color: '#6f42c1', border: '1px solid #6f42c1', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                    >
                        {resettingPromptSettings ? 'Resetting...' : 'Reset to default'}
                    </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <button
                        type="button"
                        onClick={() => setShowPromptHistory((v) => !v)}
                        style={{
                            width: '100%', textAlign: 'left', padding: '8px 12px', backgroundColor: '#f8f9fa',
                            border: '1px solid #eee', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#666',
                        }}
                    >
                        {showPromptHistory ? '▾' : '▸'} Version history{promptHistory.length > 0 ? ` (${promptHistory.length})` : ''}
                    </button>
                    {showPromptHistory && (
                        <div style={{ border: '1px solid #eee', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '10px 12px' }}>
                            {promptHistoryLoading ? (
                                <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>Loading...</p>
                            ) : promptHistory.length === 0 ? (
                                <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>
                                    No past versions yet - one gets saved here automatically the next time you save, reset, or restore.
                                </p>
                            ) : (
                                promptHistory.map((h) => (
                                    <div key={h.id} style={{ borderTop: '1px solid #f2f2f2', padding: '8px 0', display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: '1 1 300px' }}>
                                            <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                                                {new Date(h.created_at).toLocaleString()}
                                            </p>
                                            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666', whiteSpace: 'pre-wrap' }}>
                                                <strong>Brand voice:</strong> {h.brand_voice}
                                            </p>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#666', whiteSpace: 'pre-wrap' }}>
                                                <strong>Writing rules:</strong> {h.writing_rules}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRestorePromptVersion(h.id)}
                                            disabled={restoringHistoryId === h.id}
                                            style={{ padding: '5px 10px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #6f42c1', borderRadius: '4px', backgroundColor: 'white', color: '#6f42c1', cursor: 'pointer', flexShrink: 0 }}
                                        >
                                            {restoringHistoryId === h.id ? 'Restoring...' : 'Restore this version'}
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginBottom: '20px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '4px', display: 'flex', alignItems: 'center' }}>
                        Page-type sub-prompts
                        <InfoTooltip text={'Layered on top of the brand voice/writing rules above, based on each page\'s type (Pages & Cadence) - every "tech" page gets the Tech note, every "social" page gets the Social note, regardless of that page\'s age group. Leave either blank to add no extra layer for that type.'} />
                    </p>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px 0' }}>
                        One note per page type, shared by every page of that type - not set per individual page.
                    </p>

                    <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                        Tech pages
                    </label>
                    <textarea
                        value={techNoteDraft}
                        onChange={(e) => setTechNoteDraft(e.target.value)}
                        rows={3}
                        placeholder="(no extra note - falls back to the brand voice above)"
                        disabled={!pageTypePromptsLoaded}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '12px', fontFamily: 'inherit' }}
                    />

                    <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                        Social pages
                    </label>
                    <textarea
                        value={socialNoteDraft}
                        onChange={(e) => setSocialNoteDraft(e.target.value)}
                        rows={3}
                        placeholder="(no extra note - falls back to the brand voice above)"
                        disabled={!pageTypePromptsLoaded}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }}
                    />
                    <button
                        type="button"
                        onClick={handleSavePageTypePrompts}
                        disabled={savingPageTypePrompts || !pageTypePromptsLoaded}
                        style={{ padding: '8px 16px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                    >
                        {savingPageTypePrompts ? 'Saving...' : 'Save Page-Type Sub-Prompts'}
                    </button>
                </div>

                <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                        Objectives
                        <InfoTooltip text="Different posts can serve different goals - pick one per batch in Review & Post's manual 'Draft posts' tool. General organic B2C guidance: most volume should be Engagement/Follower Growth (value content, no hard ask); use Conversion sparingly, roughly 1 in every 5-7 posts, so the account reads as a genuinely useful resource rather than an ads feed." />
                    </p>

                    {objectivesLoading ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                    ) : (
                        objectives.map((o) => (
                            <ObjectiveCard key={o.id} objective={o} adminToken={adminToken} onChange={handleObjectiveChange} onDelete={handleDeleteObjective} />
                        ))
                    )}

                    <div style={{ border: '1px dashed #ccc', borderRadius: '6px', padding: '15px', marginTop: '10px' }}>
                        <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>Add an objective</p>
                        <input
                            type="text"
                            value={newObjLabel}
                            onChange={(e) => setNewObjLabel(e.target.value)}
                            placeholder="Label, e.g. 'Event Spotlight'"
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}
                        />
                        <input
                            type="text"
                            value={newObjDescription}
                            onChange={(e) => setNewObjDescription(e.target.value)}
                            placeholder="Short description (shown in the picker)"
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}
                        />
                        <textarea
                            value={newObjStrategy}
                            onChange={(e) => setNewObjStrategy(e.target.value)}
                            placeholder="Strategy instructions - what should the AI do differently for this objective?"
                            rows={3}
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px', fontFamily: 'inherit' }}
                        />
                        <button
                            type="button"
                            onClick={handleAddObjective}
                            disabled={addingObjective}
                            style={{ padding: '6px 14px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                        >
                            {addingObjective ? 'Adding...' : 'Add Objective'}
                        </button>
                    </div>
                </div>
            </SectionCard>
            )}

            {activeTab === 'pages' && (
            <>
            <SectionCard title="Upcoming drafts (next 7 days)">
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px 0', display: 'flex', alignItems: 'center' }}>
                    Estimated from each page's last generated batch - a batch of {CADENCE_BATCH_SIZE} posts every {CADENCE_BATCH_INTERVAL_DAYS} days.
                    <InfoTooltip text={`Purely an estimate from last_generated_at, the same math the daily generation job itself uses to decide what's due. A batch of ${CADENCE_BATCH_SIZE} posts (spanning whichever objectives are due, per that page's ratio) is generated every ${CADENCE_BATCH_INTERVAL_DAYS} days per page. The job still needs to actually run and event discovery still needs to succeed for a batch to really land in Review & Post, so treat this as roughly when, not a guarantee.`} />
                </p>
                {allCadencesLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                ) : upcomingOccurrences.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>
                        No drafts expected in the next 7 days - set a posting cadence below, or generate one now via
                        Review &amp; Post's manual tools.
                    </p>
                ) : (
                    Array.from({ length: 7 }).map((_, i) => {
                        const dayStart = new Date();
                        dayStart.setHours(0, 0, 0, 0);
                        dayStart.setDate(dayStart.getDate() + i);
                        const dayEnd = new Date(dayStart);
                        dayEnd.setDate(dayEnd.getDate() + 1);
                        const dayLabel = i === 0
                            ? 'Today'
                            : i === 1
                                ? 'Tomorrow'
                                : dayStart.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
                        const dayOccurrences = upcomingOccurrences.filter((o) => o.time >= dayStart.getTime() && o.time < dayEnd.getTime());
                        return (
                            <div key={i} style={{ borderTop: i === 0 ? 'none' : '1px solid #f2f2f2', padding: '8px 0' }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>{dayLabel}</div>
                                {dayOccurrences.length === 0 ? (
                                    <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>Nothing expected</p>
                                ) : (
                                    dayOccurrences.map((o, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '2px 0', flexWrap: 'wrap' }}>
                                            <span style={{ color: '#666', minWidth: '60px' }}>
                                                {new Date(o.time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                            </span>
                                            <strong>{o.city}</strong>
                                            <span>{CADENCE_BATCH_SIZE} posts (mixed objectives)</span>
                                            <span style={{
                                                padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold',
                                                backgroundColor: '#d0e6ff', color: '#004085',
                                            }}>
                                                {PLATFORM_LABELS[o.platform] || o.platform}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        );
                    })
                )}
            </SectionCard>
            <SectionCard title="Pages & posting cadence">
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px 0', display: 'flex', alignItems: 'center' }}>
                    One page per city, each with its own posting cadence - lands as drafts in Review & Post, never auto-posted.
                    <InfoTooltip text={'One Facebook or LinkedIn page per city, each with its own posting cadence per objective (posts per week). jobs/generate_scheduled_social_posts.py, fired regularly by Heroku Scheduler, checks these and automatically generates new drafts for whichever page/objective combinations are due - they land in the Review & Post tab\'s review list with status "draft", same as anything generated manually there. Nothing posts on its own; every auto-generated draft still needs to be approved first.'} />
                </p>
                <p style={{ fontSize: '12px', color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '4px', padding: '8px 12px', margin: '0 0 12px 0', display: 'flex', alignItems: 'center' }}>
                    Bio link is the always-on baseline; each draft's suggested first comment (Review & Post) converts better but is manual per post.
                    <InfoTooltip text={'Captions point readers to "the link in our bio" since there\'s no reliable way to auto-post a link in a comment. That\'s generic per page though, not specific to a given post\'s exact events - manually pasting each draft\'s "suggested first comment" (shown per-draft in Review & Post) links directly to that post\'s own events and will likely convert/click through better than the bio link alone, at the cost of being a manual step per post. Worth doing both.'} />
                </p>

                {pagesLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading pages...</p>
                ) : pages.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>
                        No pages set up yet - add your first city/platform pair below.
                    </p>
                ) : (
                    pages.map((p) => (
                        <PageCard
                            key={p.id}
                            page={p}
                            objectives={objectives}
                            adminToken={adminToken}
                            onChange={handlePageChange}
                            onDelete={handleDeletePage}
                            hasDraftsForCity={drafts.some((d) => d.city === p.city)}
                            onGoToReview={goToReviewTab}
                            onHasCadenceChange={handlePageHasCadenceChange}
                            onCadenceChange={loadAllCadences}
                            hasLivePage={livePagesByCity.some((lp) => lp.city === p.city)}
                        />
                    ))
                )}

                <div style={{ border: '1px dashed #ccc', borderRadius: '6px', padding: '15px', marginTop: '10px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>Add a page</p>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <select
                            value={newPageCity}
                            onChange={(e) => setNewPageCity(e.target.value)}
                            style={{ ...inputStyle, flex: '1 1 160px' }}
                            disabled={citiesLoading}
                        >
                            <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                            {cities.map((c) => (
                                <option key={c.city} value={c.city}>{c.city}</option>
                            ))}
                        </select>
                        <select value={newPagePlatform} onChange={(e) => handleNewPagePlatformChange(e.target.value)} style={{ ...inputStyle, flex: '0 0 140px' }}>
                            <option value="facebook">Facebook</option>
                            <option value="instagram">Instagram</option>
                            <option value="linkedin">LinkedIn</option>
                        </select>
                        {newPagePlatform === 'facebook' ? (
                            <ProviderToggle value={newPageProvider} onChange={setNewPageProvider} />
                        ) : (
                            <span style={{
                                display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '12px', color: '#666',
                                border: '1px solid #eee', borderRadius: '4px', backgroundColor: '#f8f9fa', flex: '0 0 160px',
                            }}>
                                Posting via: {newPagePlatform === 'linkedin' ? 'Buffer' : 'Direct Meta'} (only option)
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <PageTypeToggle value={newPageType} onChange={setNewPageType} />
                        {newPageType === 'social' && (
                            <AgeGroupToggle value={newPageAgeGroup} onChange={setNewPageAgeGroup} />
                        )}
                        <InfoTooltip text={PAGE_TYPE_HELP_TEXT} />
                    </div>
                    <input
                        type="text"
                        value={newPageDisplayName}
                        onChange={(e) => setNewPageDisplayName(e.target.value)}
                        placeholder="Display name, e.g. 'OkGather London'"
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}
                    />
                    <ProviderIdFields
                        platform={newPagePlatform}
                        provider={newPageProvider}
                        bufferValue={newPageBufferId}
                        onBufferChange={setNewPageBufferId}
                        metaValue={newPageMetaId}
                        onMetaChange={setNewPageMetaId}
                        adminToken={adminToken}
                        onSelectChannel={(c) => { if (!newPageDisplayName.trim() && c.name) setNewPageDisplayName(c.name); }}
                    />
                    <button
                        type="button"
                        onClick={handleAddPage}
                        disabled={addingPage}
                        style={{ padding: '6px 14px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                    >
                        {addingPage ? 'Saving...' : 'Save'}
                    </button>
                </div>
                {justAddedPage && (
                    <div style={{
                        marginTop: '10px', padding: '12px 14px', borderRadius: '6px',
                        border: '2px solid #ffc107', backgroundColor: '#fff8e1',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                            <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#856404' }}>
                                "{justAddedPage.display_name}" added - one more step before it can post
                            </p>
                            <button
                                type="button"
                                onClick={() => setJustAddedPage(null)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#856404', flexShrink: 0 }}
                            >
                                Dismiss ✕
                            </button>
                        </div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#333' }}>
                            This page posts via direct Meta, which needs the {justAddedPage.platform === 'instagram' ? 'Instagram account' : 'Page'} connected
                            to OkGather's System User before it can actually post, the ID alone isn't enough:
                        </p>
                        <ol style={{ margin: '0 0 4px 0', paddingLeft: '20px', fontSize: '13px', color: '#333', lineHeight: 1.6 }}>
                            {justAddedPage.platform !== 'instagram' && (
                                <li>
                                    <strong>Only if you created this Page while just logged into a personal Facebook account</strong> (not
                                    through the Business Portfolio) - it won't show up in the next step yet. First, in{' '}
                                    <ExternalLink href={META_BUSINESS_SETTINGS_URL}>Business Settings</ExternalLink>, go to Accounts &gt;
                                    Pages &gt; Add &gt; Add a Page, and select it there. This just links the existing page in (you keep
                                    your personal admin access, nothing is recreated or moved). Skip this if the page already shows up
                                    in the next step.
                                </li>
                            )}
                            <li>
                                Open <ExternalLink href={META_SYSTEM_USER_URL}>Meta System Users settings</ExternalLink> and assign this{' '}
                                {justAddedPage.platform === 'instagram' ? 'Instagram account' : 'Page'} to "DirectPosting" as an asset
                                with the <strong>"Content"</strong> permission.
                            </li>
                            <li>
                                Double-check the ID you entered ({justAddedPage.meta_page_id || 'none set'}) is correct
                                {justAddedPage.platform === 'instagram' ? (
                                    <> - use the asset_id from that account's URL when viewed inside Business Manager's asset list
                                    (e.g. business.facebook.com/.../?...&amp;asset_id=1234567890 - the digits after asset_id=).</>
                                ) : (
                                    <> - on the Page itself (facebook.com), check About &gt; Page transparency (or Settings &gt; Page
                                    transparency), which shows "Page ID: ...". Not the number in a facebook.com/profile.php?id= URL,
                                    which can be different (a mismatch here shows up as Facebook demanding the long-deprecated
                                    "publish_actions" permission when you actually try to post, since the target no longer
                                    resolves as a real Page). Alternatively, the asset_id in the page's own URL when viewed inside{' '}
                                    <ExternalLink href={META_BUSINESS_SETTINGS_URL}>Business Manager</ExternalLink>'s asset list
                                    is also the Page ID.</>
                                )}
                            </li>
                        </ol>
                        <p style={{ margin: 0, fontSize: '12px', color: '#856404' }}>
                            This is quick, the App and System User token already exist and work - no Heroku config changes
                            needed for this or any future page.
                        </p>
                    </div>
                )}
            </SectionCard>
            </>
            )}

            {activeTab === 'timeline' && (
            <>
            <SectionCard title="AI growth review">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                        {growthReview
                            ? `Last reviewed: ${new Date(growthReview.created_at).toLocaleString()}`
                            : 'Not reviewed yet.'}
                    </p>
                    <button
                        type="button"
                        onClick={handleRunGrowthReview}
                        disabled={growthReviewing || aiAvailable !== true}
                        title={aiAvailable !== true ? 'Needs a connected Anthropic API key' : undefined}
                        style={{
                            padding: '6px 14px', fontSize: '13px', fontWeight: 'bold', border: 'none', borderRadius: '4px',
                            backgroundColor: (growthReviewing || aiAvailable !== true) ? '#ccc' : '#6f42c1', color: 'white',
                            cursor: (growthReviewing || aiAvailable !== true) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {growthReviewing ? 'Analyzing...' : 'Analyze with Claude'}
                    </button>
                </div>
                <p style={{ fontSize: '11px', color: '#999', margin: '0 0 10px 0' }}>
                    Only sends already-anonymous, aggregate data (weekly counts, per-page cadence targets) - never
                    individual user records.
                </p>
                {growthReviewing && <AnalyzingIndicator color="#6f42c1" />}
                {growthReviewError && (
                    <div style={{ color: 'red', fontSize: '13px', padding: '8px 10px', border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffe6e6', marginBottom: '10px' }}>
                        {growthReviewError}
                    </div>
                )}
                {growthReview && (
                    <>
                        <button
                            type="button"
                            onClick={() => setShowGrowthReview((v) => !v)}
                            style={{ padding: '4px 12px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}
                        >
                            {showGrowthReview ? 'Hide review' : 'Show review'}
                        </button>
                        {showGrowthReview && (
                            <div style={{ marginTop: '12px' }}>
                                <p style={{ fontSize: '13px', color: '#333', margin: '0 0 10px 0' }}>{growthReview.review_data.overall_summary}</p>
                                {growthReview.review_data.findings?.length > 0 && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Findings</p>
                                        {growthReview.review_data.findings.map((f, i) => (
                                            <div key={i} style={{ borderTop: '1px solid #eee', padding: '6px 0' }}>
                                                <p style={{ margin: '0 0 2px 0', fontSize: '13px' }}><strong>[{f.severity}]</strong> {f.label}</p>
                                                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{f.detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {growthReview.review_data.recommendations?.length > 0 && (
                                    <div>
                                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Recommendations</p>
                                        {growthReview.review_data.recommendations.map((r, i) => (
                                            <div key={i} style={{ borderTop: '1px solid #eee', padding: '6px 0' }}>
                                                <p style={{ margin: '0 0 2px 0', fontSize: '13px' }}><strong>[{r.priority}]</strong> {r.action}</p>
                                                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{r.rationale}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </SectionCard>

            <SectionCard title="What to expect (benchmarks)">
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px 0', display: 'flex', alignItems: 'center' }}>
                    Rough, industry-typical ranges for a brand-new small/local-interest page posting consistently -
                    a sanity check, not a target or a guarantee.
                    <InfoTooltip text="Drawn from general small-business/organic-social benchmarks, not OkGather-specific data (there isn't any yet). A hyper-local city page starts from a smaller addressable audience than the accounts these ranges are typically drawn from, so treat them as a rough ballpark. Instagram's range assumes some Reels mixed in - this pipeline currently only produces a static branded image, so expect the lower end without that." />
                </p>
                {Object.entries(GROWTH_BENCHMARKS).map(([platform, rows]) => (
                    <div key={platform} style={{ marginBottom: '14px' }}>
                        <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', margin: '0 0 4px 0' }}>{PLATFORM_LABELS[platform]}</p>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                                <thead>
                                    <tr>
                                        {['Milestone', 'Followers', 'Engagement rate', 'Clicks to OkGather'].map((h) => (
                                            <th key={h} style={{ textAlign: 'left', padding: '4px 10px 4px 0', color: '#666', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.milestone}>
                                            <td style={{ padding: '4px 10px 4px 0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{row.milestone}</td>
                                            <td style={{ padding: '4px 10px 4px 0' }}>{row.followers}</td>
                                            <td style={{ padding: '4px 10px 4px 0' }}>{row.engagement}</td>
                                            <td style={{ padding: '4px 10px 4px 0' }}>{row.clicks}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </SectionCard>

            <SectionCard title="Posting frequency: target vs actual">
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px 0', display: 'flex', alignItems: 'center' }}>
                    Per page, how many posts your configured cadence calls for vs. how many actually went out.
                    <InfoTooltip text={'Target is the sum of that page\'s active per-objective cadences (posts_per_week, from Pages & Cadence) - what should be going out if generation keeps up and every draft gets approved promptly. Actual, last 4 weeks is real: SocialSchedulerQueueEntry rows Buffer accepted (status=\'queued\') once automation is on, plus manually-marked-\'posted\' drafts for the manual-workflow fallback - not drafts sitting unreviewed. A gap usually means drafts are piling up unreviewed in Review & Post, not that generation itself is behind (check the "Next automatic draft expected" line there).'} />
                </p>
                {allCadencesLoading || growthClicksLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                ) : (() => {
                    const targetsByPage = {};
                    allCadences.forEach((c) => {
                        const key = `${c.page.city}|${c.page.platform}`;
                        targetsByPage[key] = (targetsByPage[key] || 0) + (c.posts_per_week || 0);
                    });
                    const fourWeeksAgo = Date.now() - 28 * 24 * 60 * 60 * 1000;
                    const actualByPage = {};
                    growthPosts.forEach((row) => {
                        if (new Date(row.week_start).getTime() < fourWeeksAgo) return;
                        const key = `${row.city}|${row.platform}`;
                        actualByPage[key] = (actualByPage[key] || 0) + row.count;
                    });
                    const pageKeys = Array.from(new Set([...Object.keys(targetsByPage), ...Object.keys(actualByPage)])).sort();

                    return pageKeys.length === 0 ? (
                        <p style={{ color: '#666', fontSize: '14px' }}>No pages with a cadence configured yet - set one in Pages &amp; Cadence.</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                                <thead>
                                    <tr>
                                        {['City', 'Platform', 'Target', 'Actual, last 4 weeks'].map((h) => (
                                            <th key={h} style={{ textAlign: 'left', padding: '4px 10px 4px 0', color: '#666', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageKeys.map((key) => {
                                        const [pageCity, pagePlatform] = key.split('|');
                                        const targetPerWeek = targetsByPage[key] || 0;
                                        const actualTotal = actualByPage[key] || 0;
                                        const actualPerWeek = actualTotal / 4;
                                        return (
                                            <tr key={key}>
                                                <td style={{ padding: '4px 10px 4px 0' }}>{pageCity}</td>
                                                <td style={{ padding: '4px 10px 4px 0' }}>{PLATFORM_LABELS[pagePlatform] || pagePlatform}</td>
                                                <td style={{ padding: '4px 10px 4px 0', whiteSpace: 'nowrap' }}>
                                                    {targetPerWeek}/week ({(targetPerWeek / 7).toFixed(1)}/day)
                                                </td>
                                                <td style={{ padding: '4px 10px 4px 0', whiteSpace: 'nowrap' }}>
                                                    {actualTotal} in 4 weeks (~{actualPerWeek.toFixed(1)}/week, {(actualPerWeek / 7).toFixed(1)}/day)
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}
            </SectionCard>

            <SectionCard title="Actual: clicks to OkGather">
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px 0', display: 'flex', alignItems: 'center' }}>
                    Weekly visits to OkGather already tagged as coming from a social page's bio link or first
                    comment - real, automatically-collected data, not an estimate.
                    <InfoTooltip text="Sourced from page_views.traffic_source, matched against this app's own utm_source tagging for social pages (facebook/linkedin directly, Instagram - and anything else - bucketed as 'social' since Instagram bio/comment links use the same utm_source today). Only counts visits where the visitor had consented to tracking, so this is a floor on real clicks, not a complete count. Not broken down by city - the utm tagging doesn't currently distinguish which city's page a click came from, only the platform." />
                </p>
                {growthClicksLoading ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
                ) : growthClicks.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px' }}>No tracked clicks yet.</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                            <thead>
                                <tr>
                                    {['Week of', 'Facebook', 'Instagram (as "social")', 'LinkedIn'].map((h) => (
                                        <th key={h} style={{ textAlign: 'left', padding: '4px 10px 4px 0', color: '#666', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {growthClicks.slice().reverse().map((row) => (
                                    <tr key={row.week_start}>
                                        <td style={{ padding: '4px 10px 4px 0', whiteSpace: 'nowrap' }}>{row.week_start}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{row.facebook || 0}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{row.social || 0}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{row.linkedin || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>

            <SectionCard title="Actual: followers & engagement">
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px 0', display: 'flex', alignItems: 'center' }}>
                    Entered by hand from what you see in Meta Business Suite, LinkedIn analytics, or Buffer.
                    <InfoTooltip text="Neither Buffer's API nor this app has an automatic source for follower counts or historical engagement - Buffer's GraphQL API has no follower-count field at all, and its per-post metrics query is point-in-time/experimental, not a trend. Logging a data point every week or two here is enough to see a rough trend line against the benchmarks above." />
                </p>
                <GrowthSnapshotForm cities={cities} citiesLoading={citiesLoading} onSubmit={handleAddGrowthSnapshot} />
                {growthSnapshotsLoading ? (
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>Loading...</p>
                ) : growthSnapshots.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>No data points logged yet.</p>
                ) : (
                    <div style={{ overflowX: 'auto', marginTop: '10px' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
                            <thead>
                                <tr>
                                    {['Date', 'City', 'Platform', 'Followers', 'Engagement', 'Notes', ''].map((h) => (
                                        <th key={h} style={{ textAlign: 'left', padding: '4px 10px 4px 0', color: '#666', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {growthSnapshots.slice().reverse().map((s) => (
                                    <tr key={s.id}>
                                        <td style={{ padding: '4px 10px 4px 0', whiteSpace: 'nowrap' }}>{s.recorded_date}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{s.city}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{PLATFORM_LABELS[s.platform] || s.platform}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{s.followers ?? '-'}</td>
                                        <td style={{ padding: '4px 10px 4px 0' }}>{s.engagement_rate_pct != null ? `${s.engagement_rate_pct}%` : '-'}</td>
                                        <td style={{ padding: '4px 10px 4px 0', color: '#666' }}>{s.notes || ''}</td>
                                        <td style={{ padding: '4px 0' }}>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteGrowthSnapshot(s.id)}
                                                style={{ padding: '2px 8px', fontSize: '11px', border: '1px solid #dc3545', borderRadius: '4px', backgroundColor: 'white', color: '#dc3545', cursor: 'pointer' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
            </>
            )}

            {activeTab === 'manual' && (
            <SectionCard title="Manual fallback - paste into ChatGPT/Claude.ai by hand">
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Only needed if you want to draft a post without Review & Post's AI-powered manual tools (e.g. no{' '}
                    <code>ANTHROPIC_API_KEY</code> connected yet, or you just feel like doing one by hand).
                </p>

                <select
                    value={city}
                    onChange={(e) => { setCity(e.target.value); checkCurrentPage(e.target.value); }}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '15px' }}
                    disabled={citiesLoading}
                >
                    <option value="">{citiesLoading ? 'Loading cities...' : 'Select a city'}</option>
                    {cities.map((c) => (
                        <option key={c.city} value={c.city}>{c.city}</option>
                    ))}
                </select>

                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Copy a template below into ChatGPT, Claude.ai, or any AI chat tool by hand (it'll already have
                    {city.trim() ? ` ${city.trim()}` : ' your city'} filled in). Paste back the caption straight
                    into Facebook/Instagram, and paste the event list it gives you into the box below.
                </p>

                <TemplateBlock
                    title="Multiple events (weekly roundup)"
                    description="5-10 events for a roundup-style post."
                    template={buildRoundupTemplate(city)}
                />
                <TemplateBlock
                    title="One event (daily spotlight)"
                    description="For a daily single-event cadence per city."
                    template={buildSpotlightTemplate(city)}
                />

                <textarea
                    value={manualEventsText}
                    onChange={(e) => setManualEventsText(e.target.value)}
                    placeholder={'Paste the event list here, one per line:\nName | Date | Description | URL'}
                    rows={4}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '8px', fontFamily: 'inherit' }}
                />
                {manualError && (
                    <div style={{ color: 'red', fontSize: '13px', marginBottom: '8px' }}>{manualError}</div>
                )}
                <button
                    type="button"
                    onClick={handleUseManualEvents}
                    style={{ padding: '8px 16px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                >
                    Use These Events
                </button>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>
                        Events loaded here show up back in Review & Post's manual tools, ready to publish an events
                        page or hand off to drafting - switch tabs any time, nothing here needs to happen in order.
                    </span>
                    <button
                        type="button"
                        onClick={() => { setShowManualTools(true); setActiveTab('workflow'); }}
                        style={{
                            padding: '4px 12px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #6f42c1',
                            borderRadius: '4px', backgroundColor: 'white', color: '#6f42c1', cursor: 'pointer', flexShrink: 0,
                        }}
                    >
                        Go to Review & Post &rarr;
                    </button>
                </p>
            </SectionCard>
            )}

            {activeTab === 'advanced' && (
            <SectionCard
                title="Approve → auto-update page → auto-post"
                status={(automationStatus?.buffer_configured || automationStatus?.meta_direct_configured) ? 'Active' : 'Needs a provider configured'}
                statusColor={(automationStatus?.buffer_configured || automationStatus?.meta_direct_configured) ? { bg: '#d4edda', text: '#155724' } : { bg: '#fff3cd', text: '#856404' }}
            >
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Draft generation is already automatic (see Pages & Cadence) - this is the next step, skipping
                    the manual posting itself too.
                </p>
                <p style={{
                    fontSize: '13px', color: '#333', margin: '0 0 12px 0', lineHeight: 1.6,
                    backgroundColor: '#f8f9fa', border: '1px solid #eee', borderRadius: '4px', padding: '10px 12px',
                }}>
                    There's no separate on/off switch here. Reviewing and approving a draft is always a manual, human
                    step in Review & Post - that never changes. What this section explains is what happens the moment
                    you click approve: it <strong>always</strong> updates the city's events page, and it{' '}
                    <strong>always</strong> posts too, as long as a provider is connected for that page's platform (see
                    below). Each page's own "Posting via" setting in Pages & Cadence decides whether that goes through
                    Buffer or straight to Meta - if neither is connected for a platform, you post the approved draft by
                    hand instead - nothing more to configure either way.
                    <InfoTooltip text="Approving a draft is the only manual gate in this pipeline. Once a page has a working provider, every approval from then on posts automatically. There used to be a separate admin toggle for this; it was removed because there's never a reason to approve a draft and then NOT want it posted." />
                </p>
                <div style={{
                    padding: '10px 12px', borderRadius: '4px', fontSize: '13px', marginBottom: '12px',
                    backgroundColor: (automationStatus?.buffer_configured || automationStatus?.meta_direct_configured) ? '#d4edda' : '#f8f9fa',
                    border: '1px solid #eee'
                }}>
                    <p style={{ margin: '0 0 4px 0' }}>
                        Direct Meta (Facebook + Instagram): <strong>{automationStatus?.meta_direct_configured ? 'Connected' : 'Not connected'}</strong>{' '}
                        (<code>META_POC_PAGE_ACCESS_TOKEN</code>, set in Heroku config)
                    </p>
                    <p style={{ margin: '0 0 4px 0' }}>
                        Buffer (LinkedIn, or Facebook pages set to use it): <strong>{automationStatus?.buffer_configured ? 'Connected' : 'Not connected'}</strong>{' '}
                        (<code>BUFFER_ACCESS_TOKEN</code>, set in Heroku config)
                    </p>
                    <p style={{ margin: 0 }}>
                        Pages configured: <strong>{automationStatus?.pages_configured ? 'Yes' : 'No'}</strong>{' '}
                        (see Pages & Cadence, including each page's "Posting via" setting)
                    </p>
                </div>
                <p style={{ fontSize: '13px', color: '#333', margin: 0, display: 'flex', alignItems: 'center' }}>
                    Direct Meta posting (my_app/meta_scheduler.py) is the default for Facebook and Instagram; Buffer
                    (my_app/social_scheduler.py) is still used for LinkedIn, which direct Meta posting can't reach.
                    <InfoTooltip text={'Facebook pages can be switched between Buffer and direct Meta posting per page (see the "Posting via" control on each page in Pages & Cadence) - both a page\'s Buffer channel ID and its Meta Page ID can stay saved at the same time, so switching a page back to Buffer, even temporarily if direct posting has an issue, is a single click with nothing to re-enter. Instagram only ever posts via direct Meta (Buffer never supported Instagram posting at all); LinkedIn only ever posts via Buffer (direct Meta has no LinkedIn API).'} />
                </p>
            </SectionCard>
            )}
        </div>
    );
}

export default AdminSocials;
